import type * as THREE from "three";

import type { Hud } from "../hud/hud";
import type {
  ErasView,
  JournalShare,
  StagedDelivery,
  TracedEra,
  TracedOrigin,
  TracedRecord,
} from "../hud/panels";
import { boardWork } from "../hud/panels/market";
import {
  formatWorldDate,
  type Journal,
  type JournalChannel,
  type JournalEntry,
} from "../journal/journal";
import { type People, remember, type RememberedPerson } from "../journal/people";
import { WalkedGround } from "../map/ground";
import { type MapRoad, type SurveyedMap, surveyMap } from "../map/survey";
import { overlapsSolid } from "../player/collision";
import { BODY_HALF_WIDTH, BODY_HEIGHT, EYE_HEIGHT } from "../player/controller";
import type { TimeOfDaySource } from "../render/daylight";
import {
  propKindForFeature,
  propVantage,
  type PropKind,
  PropRegistry,
  type SceneProp,
} from "../render/props";
import {
  AFFLICTED_EMPTY_HANDED,
  AFFLICTED_FAINT,
  AFFLICTED_OFF_ITS_FEET,
  AFFLICTED_SENSELESS,
  AFFLICTED_UNFIT,
  type AftermathFeature,
  type CarriedItem,
  type ChronicleView,
  type EventId,
  FEATURE_MARKET_BOARD,
  type FigureId,
  ITEM_WEAPON,
  type JobPost,
  LOOSE_COOLDOWN_TICKS,
  MAX_CARRIED,
  MAX_COMPANIONS,
  MAX_CHRONICLE_ENTRIES,
  NOBODY_RESTING,
  type PresentFigure,
  STRIKE_COOLDOWN_TICKS,
  type RefugeeGroup,
  type RestReading,
  type RouteLeg,
  type RuinView,
  type SettlementId,
  type SettlementView,
  type SpokenLine,
  type SpotlightBeat,
  type TerrainSampler,
  type TilePos,
  type TownLot,
  type TradePulse,
  type WitnessSource,
  withinBeckonReach,
  withinBowRange,
  withinStrikeReach,
  type WorldDateView,
} from "../world/types";
import { REFUSED_COMPANY_FULL, REFUSED_TOO_FAR_TO_TAKE } from "./company";
import {
  handsHeld,
  looseRefusal,
  loosePrompt,
  type LooseState,
  type Quiver,
  // **One sentence, one owner.** The swing and the shot refuse a child in the
  // same words because it is the same life-stage row refusing them, and two
  // copies of one string is how a rename leaves half a client saying something
  // else.
  REFUSED_A_CHILD_CANNOT_FIGHT,
  REFUSED_NOTHING_TO_WEAR,
  REFUSED_NOTHING_WORN,
} from "./arms";
import { AmbientCrowd, GATHERED_LOITER_TILES, LOITER_RADIUS_TILES, surfaceZ } from "./crowd";
import { Director, DIRECTOR_DEFAULTS, type DirectorConfig, type StagedBeat } from "./director";
import {
  INTERACT_RANGE_TILES,
  type Interactable,
  type InteractSubject,
  LOOSE_CONE_COS,
  LOOSE_RANGE_TILES,
  promptFor,
  selectInteractable,
  STRIKE_CONE_COS,
  STRIKE_RANGE_TILES,
  verbFor,
} from "./interact";
import {
  ACCUSATION_TILES,
  arrestBeat,
  type BodyNear,
  bodyBeforeHeap,
  crimeTag,
  mouseAnswer,
  type MouseAnswer,
  nearestWithin,
  needsMyTile,
  reprimandClosed,
  reprimandLine,
  type RowInHand,
  tileATakeWouldCite,
  tilesApart,
} from "./law";
import type { OriginChoice } from "./origins";
import { erasPages, readSaga, type SagaBook } from "./saga";
import {
  BodyFacing,
  bodyPieces,
  goodsPiece,
  type ScenePiece,
  toilPieces,
  WalkSmoothing,
  workWord,
} from "./scenes";
import { BattleStaging } from "./staging";

/*
 * Witnessing, assembled: the roster becomes bodies, the log's aftermath becomes
 * props, the interact verb becomes a spoken line, and what the player was told
 * becomes a journal entry that points back at the record.
 *
 * This is the one place in the client where the pieces meet, and it exists so
 * that `main.ts` stays what it has always been — wiring, and the shape of a
 * frame.
 *
 * # What this file is allowed to send
 *
 * Phase 3 decision 10 with the one clause Phase 5 amended
 * (`docs/plans/player-as-figure.md` §1): the player's **body** never crosses the
 * seam — no position, no transform, nothing the sim could hash — and their
 * **deeds** do. So this file holds a {@link PlayerDeeds} handle and queues three
 * commands with it: a name claimed, word carried to a town, a book left on the
 * ground.
 *
 * Every one of them keeps the properties a dig has and they are worth restating,
 * because they are what stops this being a gameplay decision in JavaScript
 * (devlog #2): the command is *queued* and applied inside a tick, it is written
 * into the replay, and **what it does is the simulation's answer, not this
 * file's**. A tell that teaches a town nothing produces nothing; a book that
 * resolves to no records is not left; a second claim is ignored. Nothing below
 * re-applies those rules or predicts them — the honest test is to look at the
 * world afterwards, which is what the reads are for.
 *
 * # Where the positions come from
 *
 * **One owner per body** (Phase 7, item 6, ruled 2026-08-28). Decision 8 said
 * the simulation hands over identity and knowledge and never a position, and
 * this file laid every body out itself; wave 1F amended that for one settlement
 * — a *promoted* town's roster stands on real ground and walks real paths
 * sim-side — and for a wave the two arrangements ran side by side, which is what
 * made a figure teleport in front of a standing player.
 *
 * So: **a named figure is drawn from {@link WitnessSource.presentFigures} or is
 * not drawn at all.** Nothing below anchors one, and the ambient loop that used
 * to is kept as the future crowd layer with nobody in it (`witness/crowd.ts`).
 * What this file still places is everything the simulation holds no position for
 * and never will — a column of refugees, a heap of char in a sacked town, a
 * signpost, a book fanned off the tile it was left on — and every one of those
 * is presentation over a fact the log states.
 *
 * # What moves, and when
 *
 * The cadence is plan decision 11's: **slow bulk is pulled on a day boundary and
 * never per frame.** So this file watches two things on the fixed step — the
 * world's date and which settlement the player is nearest — and re-reads on a
 * change and on nothing else:
 *
 * - **the date turns** → the aftermath and the roads are re-read, because the
 *   log has had a day to leave a grave or put somebody on the road, and an open
 *   market panel is refilled, because a price is derived on every call and moves
 *   without a record being written (`world/wasm.ts`);
 * - **the promoted town changes** → the place is re-read, because "the visited
 *   settlement" is where the roster, the buildings and the board come from. That
 *   is what `here()` is since item 6 (`world/types.ts`): the town the simulation
 *   is holding up, and `null` when it is holding none.
 *
 * Phase 4 hangs four more things on that same boundary and no new cadence: the
 * **routes** (the lines the roads are drawn along and the bearings the signposts
 * point down), the **pulses** (the trade the sim runs over those roads), the
 * **beats** the simulation says are worth meeting here, and — off the back of
 * the beats — the one moment in this file that is a *sequence* rather than a
 * read: news landing today turns the town out (`witness/staging.ts`). Everything
 * about that sequence except its timing is the simulation's: the beat exists or
 * it does not, the line is `speakOf`'s, and the smoke stands on the bearing to a
 * settlement a second record says was struck.
 *
 * *When* it happens is `witness/director.ts`'s, and the seam is worth naming
 * because it is the phase's central split: the feed is read here on the day
 * boundary and handed to the director, which chooses at most one beat, holds it
 * until the player is actually playing, and spaces one delivery from the next.
 * This file then does the only two things the director cannot: it puts the crowd
 * somewhere and it asks the simulation for the words.
 *
 * # What the player remembers
 *
 * Two stores, both client-side, both written from here and only from here: the
 * **journal**, which is records (`journal/journal.ts`), and the **people**, which
 * is who the player has stood in front of and spoken to (`journal/people.ts`).
 * The second is Phase 4 decision 6's honest client half — the client cannot read
 * the future, so an anchor is emphasis and memory rather than a script. A
 * meeting is written down where the verb fires, below in {@link
 * Witnessing.interact}, because that is the one place the client knows both who
 * was addressed and where the player was standing.
 *
 * The roster is *not* re-read on a day boundary, and that is a decision rather
 * than an omission: what it answers is what this town's people are **called**,
 * and a name does not change. Who is standing in the street is a different read
 * on a different cadence — the local tier's, once a scene tick — so somebody who
 * dies during a session stops being drawn on the next frame, which is the
 * simulation's answer arriving rather than the client's changing its mind.
 *
 * # Per-frame allocation
 *
 * The prop list and the interactable list are rebuilt every frame rather than
 * kept in sync incrementally. A settlement is a couple of dozen entities and two
 * arrays of that size cost less than the terrain work in the same frame, where
 * an incremental scheme would cost a class of bug — a body that moved and a
 * prompt that did not.
 *
 * # Where the history is
 *
 * `docs/decisions/witness.md` — the wave-by-wave record of what each number was
 * before, the playtest defects that put the guards here, and the measurements
 * behind the clock. This file carries the contract; that page carries how it got
 * one.
 */

/**
 * How much room around a thing the player has to walk up to and read, in tiles.
 *
 * **Nothing loiters on the market board.** The board stands a few paces off the
 * middle of town (`world/wasm.ts`'s `MARKET_BOARD_OFFSET`) and the interact verb
 * addresses the *nearest* thing in the cone (`witness/interact.ts`), so anything
 * standing beside it takes every press meant for it. That is not a rule to bend:
 * the prompt names what it would address and a player can turn away, but a board
 * that is unreadable half the time is a channel that is unreadable half the
 * time, and a headless session found exactly that ("aimed at Sigsol, the
 * Far-Marched") before a person did.
 *
 * The number is not a taste: it is how far a body can be from its anchor plus
 * how far the verb reaches, which is the exact condition under which an anchored
 * body can ever be nearer to the board than the player standing at it. Derived
 * from those two rather than picked, so it moves when either does.
 *
 * **It outlived the loop it was written for.** The bodies it kept off the board
 * were the ambient loop's, and since item 6 that loop draws no figures — where a
 * named body stands is the simulation's answer and the client cannot push one
 * anywhere ({@link Witnessing.collectCrowd}). What still passes through here is
 * the wreckage of a ruined town, which the client does lay out
 * ({@link Witnessing.layOutRuin}), and the arithmetic is unchanged because the
 * question is: *how near can a thing the client places stand to a thing the
 * player has to read.*
 */
const PROP_CLEARANCE_TILES = LOITER_RADIUS_TILES + INTERACT_RANGE_TILES;

/**
 * How far in a blocked screenshot vantage is pulled per attempt, in tiles, and
 * how many attempts it gets.
 *
 * Half a body width per step, because that is the resolution at which the
 * footprint's tile coverage can change at all — a smaller step re-asks the same
 * tiles and a larger one can step clean over a doorway. Six of them is
 * {@link BODY_HALF_WIDTH} × 6 = 1.8 tiles of pull, which clears a wall (one
 * tile) and a wall with a kerb in front of it, and is where the useful cases
 * stop: a camera that has come in nearly two metres and is still in masonry is
 * inside a building it should be photographing from the doorway of.
 *
 * PROVISIONAL, like every framing number in this area, and dev-only — nothing a
 * player sees reads them ({@link Witnessing.vantageDistance}).
 */
const VANTAGE_PULL_STEP = BODY_HALF_WIDTH;
const VANTAGE_PULL_ATTEMPTS = 6;

/**
 * How close a screenshot vantage may ever be pulled, in tiles.
 *
 * Just outside the player's own footprint plus a prop's half-width: a camera
 * nearer than this is standing in the thing it is looking at, and the near plane
 * clips it into a wall of colour. It is the floor rather than the target — the
 * search stops when a body fits, and stops looking here whether or not one has.
 */
const VANTAGE_MIN_DISTANCE = 0.9;

/**
 * The angle between successive anchors.
 *
 * The golden angle, which is the standard way to scatter n points around a
 * centre without them lining up for any n — the alternative, `2π / count`,
 * puts a crowd of six on the vertices of a hexagon.
 */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/**
 * How much of its road a refugee group has behind it per day since it left.
 *
 * PROVISIONAL, a *presentation* number rather than a travel speed, and the one
 * number in this file tied to something outside it: `world/wasm.ts` counts a
 * group as still on the road for `REFUGEE_WINDOW_DAYS` — a fortnight — and this
 * is what it does with that fortnight. An eighth of the road a day is **eight
 * days of walking and then a camp outside the gate for the rest of the window**,
 * which is the shape the phase needs: a war that ignites while the player is
 * standing in town puts people on the road they can watch, and then leaves them
 * at the gate long enough to be walked up to and asked.
 *
 * **If that window moves, this moves with it**, and the seam is worth saying
 * twice because the two numbers live in different files and answer to different
 * layers: the window is what the simulation is asked for, this is what the
 * client draws with the answer.
 *
 * # Denominated in days, on purpose (Phase 5 decision 9)
 *
 * **The rate is a fraction of a road per day and not per wall second.** A
 * column's speed is a fact about the world — the simulation counts it as on the
 * road for a fortnight and this is what a fortnight of walking looks like — and
 * re-cutting it into wall seconds so that columns still arrive inside a session
 * would be the client speeding the world up to suit the length of a sitting.
 * The consequence is taken deliberately and named in the plan: **walkers are
 * slow scenery.** A session sees the roads moving rather than emptying and
 * filling, which is what a road looks like from a town.
 *
 * Nothing that depends on a column *arriving* depends on this: the staged
 * reaction joins a beat to a column that is on the road at all
 * ({@link Witnessing.columnCarrying}), not to one that has got here.
 */
export const ROAD_FRACTION_PER_DAY = 0.125;

/**
 * How far short of its destination a group stops, in tiles.
 *
 * **A *distance* and not a fraction of the road.** What is being kept is a
 * sentence about tiles — "a camp outside the gate", because there is no arrival
 * mechanic and a column standing on the market board would be a bug that looked
 * like a decision — and a fraction keeps it only on roads of one length. The
 * same percentage that is three tiles short of a thirty-tile town is seventy-two
 * short of a six-hundred-tile one, which is past the fog.
 *
 * Twelve tiles is what this client means by **where the town starts**. It was
 * the outer edge of the ring the ambient loop anchored a settlement's people on,
 * which was the only measure the client had of how big a town is; item 6 stopped
 * that loop drawing anybody, and the number is kept as the *distance* it always
 * was rather than re-derived — a column coming to rest somewhere else would be a
 * change to a shipped picture with no ruling behind it. The real answer is the
 * town plan's own reach, which the simulation holds and this client can now ask
 * for ({@link WitnessSource.lots}); taking it there is a wave of its own.
 *
 * So a column comes to rest where the town starts, on a road of any length, and
 * the player walks out to it.
 */
export const REFUGEE_CAMP_TILES = 12;

/**
 * How much further out each successive camp on the same road stands, in tiles,
 * and how many places there are in the rotation.
 *
 * **Two groups fleeing two different battles down the same road would otherwise
 * be drawn standing inside each other**, both talkable, with the nearest winning
 * every press — which is one column that says two things and a screenshot of a
 * bug. Two out of the same town inside a fortnight is what a war taking a second
 * settlement produces, so it is not a rare case.
 *
 * The rotation is on the group's own id, which is the battle record it is
 * fleeing (`world/wasm.ts`), so it is stable for the life of the world and
 * different for neighbouring groups — the same trick `facingOf` uses to keep a
 * row of gravestones from being parallel, and cosmetic in exactly the same way.
 */
const CAMP_SPACING_TILES = 2.5;
const CAMP_PLACES = 3;

/**
 * Where a refugee group stops, by its id. See {@link REFUGEE_CAMP_TILES} and
 * {@link CAMP_SPACING_TILES}.
 *
 * Exported because the self-checks measure a camp against it: the claim they
 * hold the client to is "a column stops outside the town rather than in it", and
 * the distance it stops at is this and not the bare constant.
 */
export function refugeeCampTiles(group: number): number {
  return REFUGEE_CAMP_TILES + (((group % CAMP_PLACES) + CAMP_PLACES) % CAMP_PLACES) * CAMP_SPACING_TILES;
}

/**
 * How far out of its origin something on a road is drawn on the day it sets out,
 * in tiles.
 *
 * **A walker drawn at a walk fraction of zero is standing on the settlement's
 * own centre**, on the morning the record that put them on the road was written.
 * That is not only a picture: the verb addresses the nearest thing in the cone
 * (`witness/interact.ts`), so a body on the town centre takes every press meant
 * for the books lying there.
 *
 * So a walker is drawn between a road mouth and a road mouth: out past the
 * signpost on the day they leave, short of the gate at the end
 * ({@link Witnessing.walkFraction}). Beyond the furthest camp
 * ({@link refugeeCampTiles} tops out at seventeen), because the town it left is
 * behind them rather than around them.
 *
 * Nothing about it is a fact the simulation holds: where on the line the client
 * draws the body is presentation and always was (decision 8).
 */
const SETS_OUT_TILES = 21;

/**
 * The most of a road the mouth above may eat, as a fraction of it.
 *
 * **Roads are not all six hundred tiles long.** A live world's are, and
 * {@link SETS_OUT_TILES} is three per cent of one; the fixture's are thirty, and
 * the same distance would be two thirds of the way to the next town — which
 * would pin every column at the far end of its road and stop the walk being a
 * walk at all. A tenth is the cap: on a real road the *tiles* decide,
 * and on a short one the two road mouths simply meet nearer the middle.
 */
const MOUTH_FRACTION_MAX = 0.1;

/**
 * How far out of town a signpost stands, and how far to the side of the road, in
 * tiles.
 *
 * Past the camp and off the road rather than on it — a post in the middle of a
 * path is a post everything walking the path stands inside. It is the last thing
 * on the way out and the first on the way in, which is where a fingerpost
 * belongs and where a player looking for the road will find it (Phase 4
 * decision 4).
 *
 * **Since wave 1.5 the distance is a fallback and the side is not.** The road is
 * terrain now and the simulation knows exactly where it leaves the streets
 * ({@link WorldSource.roadMouth}), so a post stands at that mouth, this far to
 * the side of it — which is a kerb rather than a guess. Fifteen tiles along the
 * bearing is the fallback, and it is what a source with no plan behind it (the
 * fixture) gets: deep inside a real plan's reach, which is why no town with a
 * plan uses it.
 */
const SIGNPOST_ROAD_TILES = 15;
const SIGNPOST_SIDE_TILES = 2.2;

/**
 * Where a town's name board stands, as an offset from the settlement's centre,
 * in tiles.
 *
 * The market board stands at the centre's other quarter (`world/wasm.ts`,
 * `MARKET_BOARD_OFFSET` — {x: 3, y: 5}), so this mirrors it across the middle:
 * the two boards frame the centre instead of crowding one corner of it, and a
 * body loitering at one is never standing inside the other.
 */
const TOWN_BOARD_OFFSET = { x: -4, y: -3 };

/**
 * Where a town keeps the Book of Eras when nobody has left a book in it, as an
 * offset from the settlement's centre, in tiles. PROVISIONAL.
 *
 * **The town's copy stands on the town's shelf where there is one** — beside
 * whatever somebody left there, in the same fan ({@link Witnessing.erasStand}),
 * which is the O6 exit criterion read literally: the same text, on a shelf.
 * Where nobody has ever written a book, this is the spot instead, because a book
 * about the world that only existed in the towns somebody happened to write in
 * would be a book most sessions never meet.
 *
 * **It is out beyond the market board's crowd, and that is the whole of the
 * arithmetic.** The first placement of this book — five tiles out on the board's
 * own side — put it within arm's length of the ring a staged morning gathered
 * the crowd on ({@link GATHER_RADIUS_TILES}), so the press meant for the book
 * went to whoever had turned out for the news. Found in the screenshot pass,
 * which is what it is for.
 *
 * So: the opposite quarter from the board (`world/wasm.ts`'s
 * `MARKET_BOARD_OFFSET` — {x: 3, y: 5}), ten tiles from the centre. That is
 * fifteen tiles from the board, which leaves the gathering ring some eight tiles
 * clear of the book — more than the verb can reach across — and near enough the
 * middle that it is a thing in the town rather than a thing outside it.
 */
const ERAS_SHELF_OFFSET = { x: -9, y: -4 };

/**
 * The id of the Book of Eras' prop, its interactable and its vantage.
 *
 * One per session rather than one per settlement, because only the visited
 * town's copy is ever drawn — the same rule the shelf itself is read under
 * ({@link Witnessing.books}) — and a stable id is what lets a screenshot driver
 * stand in front of it by name (`dev/handle.ts`).
 */
const ERAS_PROP_ID = "eras";

/**
 * What the player would call it, standing in front of it.
 *
 * The plan's own name for the thing (decision O6) and plain enough to survive
 * the standing rule that a word a player has to look up is a prompt that failed
 * ({@link labelForFeature}). "A book" would be a lie by omission: the point of
 * this one is that it is not somebody's.
 */
const ERAS_LABEL = "the book of eras";

/**
 * How far from the market board the crowd stands when it converges on it.
 *
 * The clearance plus the shuffle a standing body still does
 * ({@link GATHERED_LOITER_TILES}), which makes the invariant exact rather than
 * approximate: **no crowd body is ever nearer the board than
 * {@link PROP_CLEARANCE_TILES}, gathering or not**. The whole staged reaction
 * would be self-defeating if the press meant for the board went to whoever was
 * standing nearest it (`witness/interact.ts` addresses the nearest thing in the
 * cone) — a board that is unreadable during the one morning it matters most is
 * worse than no reaction at all.
 *
 * **Nobody is gathered at this scale**, and the constant is kept rather than
 * deleted for the reason the loop it sizes is: since item 6 the crowd holds only
 * the unnamed population, which is empty until Phase 8's cities
 * (`witness/crowd.ts`), and the day it is not, the ring it stands on has to be
 * this far out for the same reason it always did.
 */
const GATHER_RADIUS_TILES = PROP_CLEARANCE_TILES + GATHERED_LOITER_TILES;

/*
 * `ACCUSATION_TILES` used to be spelled here and lives in `witness/law.ts` since
 * chunk Fx-2, with the rest of what the watch makes of a press. It has two
 * readers now — the accusation, and the moment this client starts telling the
 * simulation where the player is standing — and they are the same moment seen
 * from two sides, so the number moved to where both of them can see one copy.
 */

/**
 * How long between one accusation and the next, in seconds: 12.
 *
 * **PROVISIONAL**, and it is a *repeat* rate rather than a delay: the first line
 * lands on the step the guard arrives on, and this is what stops him saying it
 * again every frame while he stands there. Longer than a spoken line holds at
 * full strength (`hud/panels.ts`), so the player finishes reading before it can
 * come round again.
 */
const ACCUSATION_SECONDS = 12;

/**
 * How far ahead of today the client asks for spotlight beats, in days.
 *
 * It is a *number of days* rather than two dates because the client owns no
 * calendar arithmetic (`world/types.ts`, {@link WorldDateView}).
 *
 * **It is a horizon and not a session length** (Phase 5 decision 9): a session
 * is one or two in-game days, and this is how far ahead the projection is
 * allowed to tell the client about material that has not surfaced yet. The
 * director refuses everything before its date anyway ({@link Director.due}), so
 * the only thing a shorter window would buy is a shorter list — and material
 * that *stands*, a grave, a price, a rumour the town already holds, comes back
 * however long ago it landed ({@link WitnessSource.beats}). Nothing is lost
 * either way, and the wider horizon keeps the live source's cache doing one
 * shape of work.
 */
const BEAT_WINDOW_DAYS = 15;

/**
 * How long a day lasts, in seconds of stepped time, until the client has watched
 * one go by end to end. PROVISIONAL.
 *
 * **The client does not know how long a day is, and this is not it.** The
 * simulation owns the calendar and says only that it turned (`world/types.ts`,
 * {@link WitnessSource.date}); the phase's cross-cutting rule is that the
 * calendar is spelled once, sim-side, and a client constant equal to the real
 * day would be that number transcribed in different clothes. So this is a
 * deliberately *round placeholder* rather than an estimate of anything: between
 * the forty seconds the old builds measured and the twenty minutes the owner's
 * pacing asks for, and used for at most one day of one session either way.
 *
 * What replaces it is a measurement — the seconds of stepped time between two
 * date changes, taken from the fixed step, which makes it a count of ticks
 * rather than a wall clock and therefore stable across a slow frame or a
 * backgrounded tab (`host/timestep.ts`).
 *
 * Being wrong about it costs smoothness and nothing else. The walk re-anchors to
 * the date at every boundary, and the sub-day term is clamped
 * ({@link DAY_FRACTION_CEILING}) so an over-guess parks a column at the far end
 * of today rather than walking it into tomorrow.
 */
export const DAY_SECONDS_GUESS = 200;

/**
 * The band a measured day has to fall in to be believed, in seconds.
 *
 * A guard rather than a tuning, and deliberately loose at both ends, because
 * **the client is never told which world it is in**: the same build has to
 * accept the forty-second day an old wasm measures and the twenty-minute day
 * the retuned calendar produces (plan decision 7). Four seconds still catches
 * the failure the floor was written for — the fixture never turns a day at all,
 * and a source that turned two in a frame would teach the client that a day is
 * a millisecond and send every column down its road at once. The ceiling is two
 * long days: past that the client has almost certainly missed a boundary rather
 * than watched a slow one, and keeping the previous measurement is the better
 * guess.
 *
 * **{@link DAY_SECONDS_MAX} is load-bearing outside this file.** The fast-
 * forward's spin guard is derived from it — a client that will believe in a day
 * this long must be willing to *cross* one this long — and `dev/selfcheck.ts`
 * asserts the two against each other so neither can drift alone
 * (`host/fastforward.ts`, `MAX_TICKS_PER_DAY`).
 */
export const DAY_SECONDS_MIN = 4;
export const DAY_SECONDS_MAX = 2_400;

/**
 * The most of a day the client will admit to having watched, as a fraction.
 *
 * A day that runs longer than the last one measured — the first day of a
 * session, a wasm whose calendar moved — would otherwise push the sub-day clock
 * past one and wrap it: a column would walk into tomorrow and snap back, and
 * the sky would show dawn while the world was still in the small hours. Holding
 * just short of the boundary is the honest degraded answer, and it costs
 * nothing when the measurement is right, which it is from the second day on.
 */
const DAY_FRACTION_CEILING = 0.999;

/**
 * How close a body has to be for its appearance or disappearance to reach the
 * debug log, in tiles: half again the interact range.
 *
 * **PROVISIONAL**, and it is the wave-1.6 note's own bound ("within interact-ish
 * range"). The question the log is answering is *"the person I was talking to
 * stopped existing"*, so the radius is the one the talking verb uses
 * ({@link INTERACT_RANGE_TILES}) with room for the body to have taken a step
 * away first. Wider and a busy street writes a line per body per swap; narrower
 * and a partner who backed off half a tile is off the list.
 */
const NEAR_BODY_TILES = INTERACT_RANGE_TILES * 1.5;

/**
 * How many heaps of wreckage a town in ruin is drawn with, and how far out they
 * stand. PROVISIONAL.
 *
 * Phase 5 decision 11, and the whole of the client's structural half of it: real
 * damage in world data is Phase 7's, per the owner's recorded art direction, so
 * what a ruin is here is **props the witness layer already owns**, standing in
 * the middle of the town rather than out on the road where the burned farmsteads
 * already are. Five is enough that a player crossing a settlement meets one
 * without looking and few enough that the town is still a town.
 *
 * The ring was the ambient crowd's anchor ring pulled in a little, so that the
 * wreckage stood *among* the people. That ring went with item 6 — where a body
 * stands is the simulation's answer now — and these two are kept unchanged,
 * because where five heaps of char stand in a sacked town is a shipped picture
 * and re-deriving it against the town plan is a different job
 * (`docs/decisions/witness.md`).
 *
 * **This is what is left of the client's ruin.** The other half — half a town's
 * roster kept in the street, quietened — was the loop's, and the simulation's
 * roster is the honest ruin: a sack takes a defender's roster down and
 * `FigureDied` names them, so who is out in the street after one is a fact the
 * log holds rather than a fraction this file applies.
 */
const RUIN_PROP_COUNT = 5;
const RUIN_RING_MIN = 4;
const RUIN_RING_MAX = 10;

/**
 * How far apart two books left on the same tile are set down, in tiles.
 *
 * **The simulation puts a book on a tile, and a shelf is a tile.** The curated
 * world's shelf is one book — chronicle #17, one road out in Askmoor — but two
 * can land on the same tile, left by a ruler who won twice, and drawn at the
 * position the simulation reports they are one prop with another inside it: one
 * silhouette, one press, and the second book invisible and unreadable. Where a
 * book *is* is sim truth and where it is *drawn* is this file's, which is the
 * same split every body on this street is under (decision 8), so books sharing
 * a tile are fanned around it.
 *
 * Under a tile, so the fan stays within the square the simulation named and its
 * neighbours' edges: a book drawn on the next tile along would be the client
 * moving an object the world knows the position of. It is also sized against
 * the verb — two books on opposite sides of a fan this wide are
 * `2 × spacing` apart, which with the vantage's own stand-off puts the far one
 * outside {@link INTERACT_RANGE_TILES} of the near one's vantage, so standing in
 * front of one addresses that one.
 */
const SHELF_SPACING_TILES = 0.7;

/**
 * The three commands the player's deeds land through.
 *
 * A handle rather than the whole of `WorldSource`, and the narrowness is the
 * point: this file is the witnessing layer, and what it is entitled to do to the
 * world is exactly these three things (Phase 5 decisions 1, 4 and 5). A
 * `WorldSource` satisfies it structurally, so `main.ts` passes the world and
 * nothing is wrapped.
 *
 * Optional on {@link WitnessOptions}, because the self-checks build a
 * {@link Witnessing} over a fixture with no simulation behind it: a session with
 * no deeds handle draws every surface and queues nothing, which is also what a
 * `?bootstrap=0` world would want if it had any towns to claim in.
 */
export interface PlayerDeeds {
  claim(name: string, settlement: SettlementId): void;
  tell(settlement: SettlementId, event: EventId): void;
  placeChronicle(entries: readonly EventId[], pos: TilePos): void;
}

/**
 * The four commands the player's *hands* land through, and the one read that
 * says where to look afterwards.
 *
 * {@link PlayerDeeds}' sibling for Phase 7 (plan, 1F) and a separate handle for
 * a reason worth stating: a deed is a thing that writes **history** — a name, a
 * telling, a book — and these four write the world a player is standing in and
 * nothing else. Gathering, crafting, picking up and putting down are *seen,
 * never logged* (plan, O11), so a handle that mixed them with the three above
 * would blur the one distinction this phase is built on.
 *
 * `takeDugTiles` is not a command and is here anyway, because it is the answer
 * to the question the four raise: **where is there something to look at.** The
 * simulation's own note says a renderer learns where to look from `Event::Dug`
 * (`sim_core::Snapshot::items_on` — *"there is deliberately no 'every pile in
 * the world' iterator"*), and this is that event stream, drained per frame like
 * the mesh updates.
 *
 * Optional on {@link WitnessOptions} for {@link PlayerDeeds}' reason: a session
 * with no simulation behind it draws every surface and queues nothing.
 */
export interface PlayerHands {
  gather(pos: TilePos): void;
  craft(pos: TilePos, recipe: number | null): void;
  take(pos: TilePos): void;
  drop(pos: TilePos): void;

  /*
   * And the two the same hands fight with (wave 2D-a).
   *
   * They are on this handle rather than on a second one because they are the
   * same bargain: an intention queued as a command, applied inside a tick,
   * written into the replay, and **refused in silence by a world that has the
   * last word**. What differs is that a strike names a person — see
   * {@link WorldSource.strike}, which carries the argument for the one exception
   * this vocabulary makes.
   */

  stand(pos: TilePos): void;
  strike(figure: FigureId): void;

  /*
   * And wave 2D-b's three, on the same handle and the same bargain. Two of them
   * name **nothing at all**, which is the tile rule at its end point: which
   * piece of armour goes on is the simulation's pick, so a press is a press and
   * never a selection (`sim_core::hands`).
   */

  loose(figure: FigureId): void;
  wear(): void;
  doff(): void;

  /*
   * And wave 2F's two, which are the same bargain a third time: an intention
   * queued, applied inside a tick, written into the replay, and refused in
   * silence by a world that has the last word. They cite a **person** for the
   * strike's reason and under its one exception — see {@link WorldSource.beckon}.
   */

  beckon(figure: FigureId): void;
  dismiss(figure: FigureId): void;

  /** The tiles the last ticks changed, drained. See the interface's note. */
  takeDugTiles(): readonly TilePos[];
}

/**
 * The life a session was born into, as the client remembers it.
 *
 * Handed to {@link Witnessing.bornAs} by `main.ts` when the birth screen settles,
 * and the *only* thing about the birth this class holds: who the player is comes
 * back out of the world on the next step like any other claim
 * ({@link Witnessing.watchForClaim}), because a birth is a command and a command
 * is answered by the world rather than by the caller.
 *
 * The fourth deed is deliberately **not** on {@link PlayerDeeds}: a birth is
 * queued before this class has stepped once, through a door `hud/origins.ts`
 * holds (`witness/origins.ts`'s `BirthDoor`), and adding it here would put a verb
 * on a handle whose whole job is what a player does *in* the world.
 */
export interface BornLife {
  /** The card that was taken, with its citations (`witness/origins.ts`). */
  readonly chosen: OriginChoice;

  /** The name that was typed, as typed. Normalization is the wall's. */
  readonly name: string;

  /** Whether the command reached a door. Not whether the simulation took it. */
  readonly queued: boolean;
}

/**
 * How many tiles the client keeps an eye on for things lying about.
 *
 * PROVISIONAL. Every one of them costs two small reads when something the
 * player did could have moved one — never on a clock — so this is a bound on a
 * *burst* rather than on a rate: a player who digs a corridor twenty tiles long
 * has twenty piles behind them, and the ones they have walked away from stop
 * being worth the calls.
 *
 * Sixty-four is comfortably more than a session leaves in one square of a town
 * and small enough that a re-read of the whole set is a handful of microseconds.
 * Pruned oldest-first ({@link Witnessing.watch}).
 */
const WATCHED_TILES = 64;

/**
 * What a library's shelf is called when the player walks up to it.
 *
 * The one word this file puts on a shelf, and it is a *label* rather than a
 * sentence: what a shelf **says** is the simulation's, either as the pages of a
 * book it is holding ({@link WitnessSource.chronicleLines}) or as the one line
 * below when it is holding nothing.
 */
const SHELF_LABEL = "a shelf";

/**
 * What an empty shelf says when somebody reads it.
 *
 * **The empty-shelf invitation** (Phase 7 plan, decision 14, from the
 * open-questions session of 2026-08-25): a town with a library and nothing on
 * its shelves is a town whose history nobody has written down, and saying so is
 * how a player finds out that *they could*.
 *
 * **This is client copy, and it is the one line the phase-6 ruling authorised**
 * — checked against the alternative first, which is the rule: the voice loom
 * composes sentences about *records* (`sim_core::history::speech`, `voice`), and
 * a bare shelf cites none, so there is nothing for the simulation to be asked
 * about. The register is the written one every other read surface in this world
 * uses — no contractions, nothing anybody's mouth — and it names the town out of
 * the simulation's own string rather than saying "here".
 *
 * The day `sim_core` grows a bank for it, this becomes a call and the constant
 * goes; that is the same seam the market board's cause line came through in wave
 * 4 of the voice overhaul, and it is worth leaving marked.
 */
const EMPTY_SHELF_LINE = (town: string): string =>
  `The shelves are bare. No chronicle of ${town} has been written down.`;

/**
 * Why nothing happened, in the one register a refusal is allowed
 * (`hud/panels.ts`'s `FeedbackLine`).
 *
 * **The graybox ruling**: every refused action displays a judgeable message. A
 * key that does nothing with nothing said is indistinguishable from a broken
 * key, and a player who cannot tell a refusal from a bug stops pressing.
 *
 * Four rules run through the whole table and each one is the ruling rather than
 * taste:
 *
 * - **It is client copy, and it is allowed to be.** Every other string this
 *   client puts on screen about the *world* comes out of the simulation, because
 *   composing a sentence about a record is the thing above this seam that is
 *   never permitted (devlog #2). None of these is about a record. They are about
 *   a key that did nothing, which is a fact about the client's own input and the
 *   one subject it owns outright.
 * - **The judgement behind each one is the simulation's** where the simulation
 *   has one: {@link WitnessSource.workAt} says whether there is work at a tile,
 *   the board says whether somebody has taken it, {@link MAX_CARRIED} says when
 *   hands are full. The client turns an answer into a sentence and never the
 *   other way round.
 * - **Where the client cannot predict, it says the honest generic.** A guess
 *   dressed as a diagnosis teaches a player something false about the world;
 *   {@link REFUSED_NOTHING} only tells them what they already saw.
 * - **Plain English, no jargon.** No *site*, no *job kind*, no *board*. A word a
 *   player has to look up is a prompt that failed (`witness/interact.ts` says the
 *   same thing about a label).
 */
const REFUSED_NOTHING = "nothing happened";
const REFUSED_NO_WORK = "nothing to gather here";
// Two verbs since chunk Fx-2, and the second one names the worker where the
// town has a name for them: *"Havald is already working here"* is this sentence
// with the one fact the board carries put into it
// ({@link Witnessing.checkCraftLanded}).
const REFUSED_TAKEN = "somebody is already working here";
const REFUSED_OUT_OF_REACH = "nothing within reach";
const REFUSED_HANDS_FULL = "your hands are full";
const REFUSED_HANDS_EMPTY = "your hands are empty";
const REFUSED_NO_GROUND = "nothing to dig here";

/*
 * And the five a swing can come to nothing for (wave 2D-a).
 *
 * Every one of them is a rule the *simulation* holds and this client can apply
 * first — reach, the stage table, the cooldown, and what is in the hands — which
 * is the same bargain the seven above are written under: the sim refuses in
 * silence and the client explains, and it can only do that honestly for rules it
 * has the numbers for ({@link STRIKE_REACH_TILES}, {@link STRIKE_COOLDOWN_TICKS},
 * both checked against the simulation's own at startup).
 *
 * The one that is not a sim rule is {@link REFUSED_NO_QUARREL}, and it is this
 * client's own aiming rule rather than a refusal the world would make: an
 * unarmed player pointing at a neighbour who has done nothing is a player
 * digging, not a player brawling (the mouse-0 rule — {@link Witnessing.foe}).
 */
const REFUSED_NOBODY_TO_STRIKE = "nobody in front of you";
const REFUSED_OUT_OF_SWING = "too far to strike";
const REFUSED_NO_QUARREL = "you have no quarrel with them";
const REFUSED_STILL_SWINGING = "your arm is still coming back";

/**
 * How many steps the client waits before believing a take or a drop.
 *
 * A command lands on a tick and the world is only different afterwards, so a
 * re-read in the same frame reads the world as it was. Two, like the books'
 * latch ({@link Witnessing.booksAwaited}), which is one for the tick the
 * command is submitted to and one for the frame that draws it.
 */
const HANDS_SETTLE_STEPS = 2;

/**
 * How many steps the client waits before deciding a craft order never landed.
 *
 * {@link HANDS_SETTLE_STEPS}' cousin with one more step in it, and the extra one
 * is the board's own cadence: a claim is validated in the tick it is submitted
 * to (`sim_core::jobs`' `claim`) but the *board* is re-read where the scene is
 * drawn, so the answer is one frame behind the tick that decided it.
 *
 * It is short on purpose. This is the delay between pressing a key and being
 * told nothing came of it, and a second of it would read as the world thinking
 * about it.
 */
const CRAFT_SETTLE_STEPS = 3;

/**
 * What is lying on one tile, as the client last read it.
 *
 * The tile, what is on it and whether the thing on it is a shelf — three facts
 * from two reads ({@link WitnessSource.tileItems} and
 * {@link WitnessSource.hasShelf}), kept together because they are asked
 * together and answered about the same square.
 *
 * `shelf` is asked rather than inferred from the items: the client could compare
 * an item's kind against a number, and that number would be a copy of a
 * vocabulary the simulation owns (`witness/scenes.ts` makes the same call).
 */
interface Pile {
  readonly at: TilePos;
  readonly items: readonly CarriedItem[];
  readonly shelf: boolean;

  /**
   * What a pickup would take from this tile and what it is called, or `null`
   * when nothing lying here is something a pair of hands lifts.
   *
   * **The first *portable* item in handle order, which is the simulation's own
   * rule** (`sim_core::hands`' `first_portable`) — and it is not the same thing
   * as the first item, which is what this field held before chunk B. A furnished
   * workshop has a table standing on its work tile and every standing building
   * has a door in its doorway (`sim_core::townplan`'s furnish pass), so on
   * exactly the tiles a player walks up to, the handle at the front of the list
   * is a fitting the verb skips.
   *
   * `null` is what makes the pickup verb honest about a tile it cannot act on:
   * the fitting is still drawn, and no press is offered over it
   * ({@link Witnessing.pileSubject}).
   *
   * **Not per frame**, which is decision 11's rule and the one thing about these
   * reads that is easy to get wrong: a label is a *string* and strings cross per
   * call, so asking for one while assembling a prop list would be a call across
   * the wall sixty times a second for a word that changes when somebody picks
   * something up. The portability read is resolved here with it, for the same
   * reason and at the same moment.
   */
  readonly takes: string | null;
}

/**
 * One building this town has standing, as the client keeps it.
 *
 * The simulation's row plus the two things a frame would otherwise ask for while
 * it was drawing: what the building is called, and which tile a walker stands on
 * at its bench. Both are resolved when the town is read
 * ({@link Witnessing.readLots}) rather than per frame, which is decision 11's
 * rule for the first and the footing probe's cost for the second.
 */
interface StandingLot {
  readonly lot: TownLot;
  readonly name: string;
  readonly at: TilePos;
}

/** What {@link Witnessing} needs to do its job. */
export interface WitnessOptions {
  readonly source: WitnessSource;
  readonly terrain: TerrainSampler;
  readonly scene: THREE.Scene;
  readonly hud: Hud;
  readonly journal: Journal;

  /**
   * Who the player has met (`journal/people.ts`).
   *
   * Handed in beside the journal rather than owned here, and for the same
   * reason: it is the player's memory rather than the town's, it outlives any
   * one settlement's crowd, and `main.ts` gives the debug handle a way to read
   * it without reaching into this class.
   */
  readonly people: People;

  /**
   * The director's knobs (`witness/director.ts`, Phase 5 decision 9).
   *
   * Optional, and the default is what ships: `main.ts` reads the URL once and
   * passes the answer, and every other construction of this class — the
   * self-checks, chiefly — gets the shipped pacing without having to know the
   * knobs exist.
   */
  readonly director?: DirectorConfig;

  /**
   * Where the player's deeds go ({@link PlayerDeeds}).
   *
   * Absent in every session with no simulation to write to, which is the
   * fixture and the self-checks. The flows still run — the offer is still
   * computed, the book is still compiled — and the command is simply not
   * queued, so what a check can assert is exactly what the client decided.
   */
  readonly deeds?: PlayerDeeds;

  /**
   * Where the player's hands reach ({@link PlayerHands}).
   *
   * {@link WitnessOptions.deeds}' twin and absent in the same sessions. A
   * fixture has no board to claim work off and no arena to pick anything out
   * of, so the verbs are offered nowhere and pressed nothing — which is the same
   * answer that surface gives to every other Phase 5 and Phase 7 read
   * (`world/fixture.ts`).
   */
  readonly hands?: PlayerHands;

  /**
   * How many ticks the simulation spends walking a body across one tile
   * (`sim_wasm::walk_ticks_per_step`).
   *
   * Handed in rather than imported, because it is a *simulation* number and this
   * client is not allowed a second copy of one — the same rule
   * {@link MAX_CARRIED} is under. It is what {@link WalkSmoothing} paces itself
   * by, and everything downstream of it is presentation.
   *
   * Optional on {@link WitnessOptions.director}'s terms: `main.ts` forwards what
   * the wall answers, and a construction with no simulation behind it — the
   * fixture, the self-checks — has no promoted town and therefore no walkers to
   * smooth, so it draws bodies exactly where it is told.
   */
  readonly walkTicksPerStep?: number;

  /**
   * The host's fixed step, in seconds (`host/timestep.ts`'s `TICK_SECONDS`).
   *
   * Beside the cadence above, because the two only mean anything together: one
   * says how many ticks a step of walking takes and the other how long a tick
   * is. Passed rather than imported for the reason `host/loop.ts` passes it to
   * every other stepped thing — the number belongs to the loop, and a module
   * that read it from a constant would be a module that could not be run at
   * another rate.
   */
  readonly stepSeconds?: number;

  /**
   * The settlement this session opened in, or absent.
   *
   * **The one town this class is told rather than asks for**, and it exists
   * because of a single instant: the claim is queued in the boot sequence,
   * before the world's first tick, and `here()` is the promotion slot — which is
   * filled *inside* that first tick (item 6's rider 4). So at the moment the
   * name goes into the record there is no promoted town to put it in, and the
   * honest answer is the one the boot sequence already made: the settlement it
   * chose to stand the player in (`world/wasm.ts`'s spawn, or whatever `?home=`
   * named).
   *
   * It is used for that and nothing else ({@link Witnessing.claimTheName}).
   * Everything after the first tick asks `here()` like everything always did —
   * a session that walked somewhere else before claiming is not a case, because
   * the claim happens before the first frame is drawn.
   */
  readonly home?: SettlementId | null;

  /**
   * Where this layer says what it just did, or absent.
   *
   * **Wave 1.6's instrument** ({@link WitnessWatch}). Absent in every ordinary
   * session, in the fixture and in the self-checks, and when it is absent every
   * call site below is a `?.` — which is the whole of what an unwatched session
   * pays. Spelled `watcher` on the class because `watch` is already a verb there
   * (the watched-tiles set), and two of those would be one too many.
   */
  readonly watcher?: WitnessWatch;

  /**
   * What the aiming prompt names as the way to press the interact verb, or
   * `null` where there is nothing to name.
   *
   * `"F"` unless somebody says otherwise, which is every session but a touch one
   * (`main.ts` reads the mode once and passes the answer, exactly as it does
   * with {@link WitnessOptions.director}). On a touchscreen there is no F and the
   * verb is on a button under the player's thumb, so the line drops the key and
   * says what it would do — the button beside it says the same word
   * (`witness/interact.ts`'s `promptFor`, `input/touch.ts`).
   *
   * It reaches exactly one string. Nothing else in this layer names a key,
   * because nothing else in it is a key: the panels' own hints belong to
   * `hud/panels.ts`.
   */
  readonly promptKey?: string | null;
}

/**
 * Where the witnessing layer reports what it just did.
 *
 * **The debug window's seam** (`docs/plans/phase-7-wave-1.6-notes.md`, item 7).
 * The class below is the one place in the client that knows *when* the crowd
 * changes source, *which* bodies stopped existing and *why* — and none of that
 * is visible from outside, which is why an investigation into a figure that
 * teleported while the player stood still could not be closed from a screenshot.
 *
 * Declared here rather than imported from `dev/`, on `PlayerDeeds`' terms and for
 * its reason: this file is a production module and a development one must not be
 * in its import graph. `dev/debuglog.ts`'s `DebugLog` satisfies this
 * structurally, and its tag union is wider than this one — a `sim` note and a
 * dropped-notes line come from `main.ts`, not from here.
 *
 * It **reads nothing back**. Nothing in this class ever asks the watcher a
 * question, so a session with one attached takes exactly the decisions a session
 * without one takes.
 */
export interface WitnessWatch {
  /**
   * One line, tagged by which family of event it belongs to.
   *
   * `town` is `here()` — the promotion slot — changing hands; `tier` is a
   * `readPlace()` firing; `body` is somebody near enough to talk to appearing or
   * ceasing to exist; `snap` is {@link WalkSmoothing} giving up on a glide.
   */
  note(tag: "town" | "tier" | "body" | "snap", text: string): void;
}

/**
 * Why {@link Witnessing.readPlace} fired.
 *
 * The reason tag the wave-1.6 note asks for by name, and it is the whole list of
 * callers: the constructor's first read, the promoted town changing, a town
 * going into or out of ruin, a name being claimed, and a day boundary landing on
 * a stale roster. A swap the log cannot attribute is a swap nobody can reason
 * about, which is what item 6 spent an investigation discovering.
 *
 * **`promotedFlip` is gone and could not be re-created.** It was `readLocal`
 * noticing that the simulation had promoted a different town than the one the
 * client thought it was standing in — the disagreement window rider 4 deletes.
 * `here()` *is* the promotion slot now, so a flip and an arrival are one event
 * with one name.
 */
export type PlaceReason = "boot" | "arrived" | "ruinTurned" | "claim" | "dayTurned";

/**
 * What the debug window reads off the witnessing layer at one instant.
 *
 * A value rather than a set of getters ({@link Witnessing.watched}). Everything
 * here is either already held by that class or a count of something it holds:
 * nothing is computed for the window's sake, and nothing the window asks for
 * changes what the client draws.
 */
export interface WitnessWatched {
  /** The town the client is drawing, and its name and centre distance. */
  readonly here: SettlementId | null;
  readonly hereName: string;
  readonly hereDistance: number | null;

  /**
   * The town the simulation has promoted, asked at this instant.
   *
   * **The same answer as {@link WitnessWatched.here} by construction**, and
   * carried anyway: `here` is the value this class latched on its last step and
   * this is a fresh read, so the two can differ for exactly one step and the
   * window is where a client that stopped following the slot would show up. The
   * agreement line the Info tab draws is that comparison (`dev/debuglog.ts`).
   */
  readonly promoted: SettlementId | null;
  readonly promotedName: string;

  /**
   * Everybody alive in {@link WitnessWatched.here}, whatever their age, and how
   * that splits by life stage.
   *
   * **The split is the reading item 6 left behind** (rider 2). Presence holds
   * every living resident now, children included, so the roster and the bodies
   * should agree to within the player — and *"twelve alive, nine grown"* is what
   * says whether a town with three people missing from its street has three
   * children in it or a bug.
   *
   * Wave 2A widened it from a boolean's two halves to the stage table's own
   * rows, which is the filed gap closed: *"the debug window's alive-here splits
   * by stage"*. Empty for a town with nobody in it.
   */
  readonly roster: number;
  readonly stages: readonly WatchedStage[];

  /** Bodies the simulation has standing on real ground here. */
  readonly presence: number;

  /** Props drawn last frame, and how many of them were people. */
  readonly props: number;
  readonly bodies: number;

  /** The board, the buildings and the tiles being watched for heaps. */
  readonly jobs: number;
  readonly jobsTaken: number;
  readonly lots: number;
  readonly piles: number;

  /** What the interact key would address, by prop id, or `null`. */
  readonly aimed: string | null;

  /** How far through its day the world is, or `null` when the client cannot tell. */
  readonly phase: number | null;

  /** How many bodies {@link WalkSmoothing} has snapped. Zero is the healthy answer. */
  readonly snaps: number;

  /** How many times `readPlace()` has fired, by reason. */
  readonly tierSwaps: readonly (readonly [PlaceReason, number])[];
}

/**
 * Where the player stands with the watch, at one instant
 * ({@link Witnessing.watching}, chunk Fx-2).
 *
 * **The arrest chain in the order it happens**, so a driver that finds it broken
 * can say which link went: the town wants you, a hand is near, this client is
 * telling the simulation where you are, the simulation is holding that tile, a
 * hand is on your shoulder, and the beat is on the screen. It exists because the
 * wave-2 scripted proof sent its own stands — so every link but the third was
 * being checked, and the third was the one that was broken.
 *
 * {@link WitnessWatched}'s rule applies: nothing here is computed for the
 * reading.
 */
export interface WitnessWatching {
  /** What the town holds against the player, in the simulation's word, or `""`. */
  readonly wanted: string;

  /** How far the nearest watchman is standing, in tiles, or -1 for none in earshot. */
  readonly guardAway: number;

  /** Whether this client would state the player's tile on the next crossing. */
  readonly standing: boolean;

  /** The tile the simulation is holding as the player's, or `null` if it holds none. */
  readonly stood: TilePos | null;

  /** Who has a hand on the player's shoulder, or -1. */
  readonly arresting: FigureId | number;

  /** What the mouse line says, which is where the surrender beat is drawn. */
  readonly beat: string;
}

/**
 * One body the debug window's Bodies list draws a line for (chunk D2).
 *
 * The owner went looking for one child in their home town — *"add the
 * where-is-figure-N feature on debug"* — and had nothing to look with: the
 * street holds a dozen people, they are all named on their plates, and the only
 * way to find a particular one was to walk the town reading plates.
 *
 * **Nothing here is computed for the window and nothing crosses the wall for
 * it.** The position is the local tier's own answer, already read on the scene
 * cadence ({@link Witnessing.present}); the name is the roster's, already
 * joined; the board is already held; and the stage rides the body's own row
 * ({@link PresentFigure.stage}). **Where the player is and which way they are
 * facing is deliberately not on this row** — the distance and the bearing are
 * arithmetic over the player's own pose, and they are done where they can be
 * checked without a world (`dev/debuglog.ts`'s `bodiesLines`).
 */
/**
 * How many of a town's living people are in one life stage.
 *
 * A row of {@link WitnessWatched.stages}, and every field of it is the
 * simulation's: the id is `sim_core::body::LifeStage`'s and the word is the
 * stage table's own ({@link WitnessSource.lifeStageWord}, `""` for grown). What
 * the client owns is the counting and the grammar that prints it
 * (`dev/debuglog.ts`).
 */
export interface WatchedStage {
  readonly stage: number;
  readonly word: string;
  readonly count: number;
}

export interface WatchedBody {
  /** The simulation's own display number, which is what the owner is hunting by. */
  readonly figure: FigureId;

  /** What the roster calls them, or `""` for somebody it has no name for. */
  readonly name: string;

  /**
   * Which life stage the simulation says they are in, and what it calls it.
   *
   * The id is `sim_core::body::LifeStage`'s and the word is the stage table's
   * own ({@link WitnessSource.lifeStageWord}), empty for a grown body — so a
   * row that prints it says exactly what the plate over that body says
   * (`witness/scenes.ts`'s `bodyPieces`) and the two surfaces cannot disagree
   * about who is a child.
   *
   * A boolean until wave 2A, which is rider 2's filed gap closed: *"the debug
   * window's alive-here splits by stage"*.
   */
  readonly stage: number;
  readonly stageWord: string;

  /** Where they are standing, the simulation's answer rather than the drawn one. */
  readonly at: TilePos;

  /**
   * How hurt they are, in the simulation's own word, or `""` for whole.
   *
   * `sim_core::health::Condition` through {@link WitnessSource.conditionWord}
   * ({@link WatchedBody.stageWord}'s exact shape, including the empty string
   * being an answer). **No number crosses**: the simulation keeps three
   * accumulators per tissue layer of every wound and a reader gets one of four
   * words, which is the phase's own rule about where a damage number may be
   * computed (`docs/plans/phase-7-plan.md`, cross-cutting).
   */
  readonly conditionWord: string;

  /**
   * What the client can already see them doing, in a few words.
   *
   * **Only what is cheap and already read**: an order on the board with their
   * name on it, a bench they are standing at, or *standing*. The simulation
   * publishes no "what is this figure up to" read and this is not a request for
   * one — a list that guessed would be worse than a list that says *standing*.
   */
  readonly doing: string;
}

/**
 * One place the debug window's Places list draws a line for (chunk D2).
 *
 * The owner's second ask on the same window — *"a map that shows close
 * locations like workshops and such … it's hard to find locations right now"* —
 * answered as a list rather than a map, because a list sorts by distance and a
 * map does not.
 *
 * **What counts as a place: something the town has that does not move.** Two
 * sources, both already read:
 *
 * - **The town plan's standing buildings** ({@link WitnessSource.lots}, held by
 *   {@link Witnessing.readLots} since the last place-or-day change) — every one
 *   of them, homes included, each with the simulation's own word for it and the
 *   craft its bench practises where it has one. This half is *complete*: it is
 *   the whole plan and not the part near the player.
 * - **The standing things the client has placed in it** — the town board, a
 *   grave, a book, the Book of Eras, the wreckage of a sacked town. This half is
 *   *partial by construction*: those become addressable when the client places
 *   them and a grave is only offered once its record is readable, so the list
 *   says what the client knows and never claims to be a survey.
 *
 * Bodies are not places (they have their own tab), heaps of goods are things
 * rather than places, and a refugee column is somewhere *else* by the time a
 * line about it is read.
 */
export interface WatchedPlace {
  /** What it is, in the simulation's own word where there is one: *tavern*, *a grave*. */
  readonly what: string;

  /**
   * What is done there, or `""`.
   *
   * The bench's craft for a workshop ({@link TownLot.trade} through
   * `witness/scenes.ts`'s `workWord`), and nothing for everything else — a home
   * is a home.
   */
  readonly note: string;

  /** The tile to walk to: a bench for a workshop, the thing itself otherwise. */
  readonly at: TilePos;
}

/**
 * One work order the debug window's Places list draws a line for (chunk D3).
 *
 * **The owner's third gap in the same window**: the market board panel prices
 * commodities and nothing anywhere says *what work this town has going*, so a
 * reader could see a body `felling` and had no way to find the tree. The board
 * is already held ({@link Witnessing.board}, re-read on the place-or-day
 * cadence), so this is a join over a list the client has rather than a read.
 *
 * **A job is `(site, kind)` and never a row id.** Plan decision O5 says it for
 * commands — *a command cites the work, never the board row* — and a surface
 * that lets somebody follow a piece of work is under the same rule: the board is
 * derived sim-side, an order can be taken, finished and re-posted, and a client
 * holding an index into it would be following a slot. That pair is what the row
 * carries and what the mark tracks (`dev/track.ts`).
 *
 * Not a {@link WatchedPlace}, though it draws in the same columns: a place is
 * something the town *has* and an order is something the town is *doing*, and a
 * grave that becomes a felling order when somebody takes an axe to a tree would
 * be a list that cannot say which of its rows will still be there tomorrow.
 */
export interface WatchedOrder {
  /** The simulation's own `JOB_*` id, which with the site is the order's identity. */
  readonly kind: number;

  /** What it is, in this client's gerund: `felling` (`witness/scenes.ts`'s `workWord`). */
  readonly work: string;

  /** The tile the work is done to ({@link JobPost.site}). */
  readonly at: TilePos;

  /** `taken by Bera Highfell`, or `open`. */
  readonly note: string;
}

/**
 * Which addressable things the Places list counts as places (chunk D2).
 *
 * Typed against {@link InteractSubject}'s own union rather than as a set of
 * strings, so a kind renamed one file over is a compile error here instead of a
 * line that quietly stopped appearing — the failure mode a debug list can least
 * afford, because nothing about a missing row looks wrong.
 *
 * What is left out, and why each one is not a place:
 *
 * - `npc` — a person, and the Bodies tab is the list of those.
 * - `refugees` — walking, so the tile on the line is wrong by the time it is
 *   read.
 * - `item` — a heap on the ground is a thing rather than a place, and the
 *   watched set turns over as the town works.
 * - `workshop` — the same buildings the plan already answers with, and with
 *   less on the row: the plan's half of this list carries the craft.
 */
const PLACE_SUBJECTS: ReadonlySet<InteractSubject["kind"]> = new Set([
  "market",
  "grave",
  "chronicle",
  "eras",
  "ruin",
  "shelf",
]);

/**
 * Somewhere to stand to look at one thing in the world.
 *
 * **A screenshot affordance, and development only.** Every aesthetic constant in
 * `render/props.ts` is judged by looking at it (the art-pass precedent,
 * `docs/plans/gate-a.md`), and a judgement is only worth anything if the same
 * picture can be taken again after the numbers move. So the client can be asked
 * where to stand for each prop, rather than a driver dead-reckoning a walk to it
 * and hoping. Nothing in a play session calls this; `dev/handle.ts` is the only
 * consumer and it is absent from the production bundle.
 */
export interface Vantage {
  /** The prop's id, which is also the interactable's: `feature:2`, `npc:11`. */
  readonly id: string;

  /** What the player would call it, for the driver's own listing. */
  readonly label: string;

  /** Where to put the player's feet, in world tiles. */
  readonly x: number;
  readonly y: number;
  readonly z: number;

  /** Where to point the view. Yaw 0 looks along +y; positive pitch looks up. */
  readonly yaw: number;
  readonly pitch: number;

  /**
   * How far the camera actually ended up from the prop, in tiles.
   *
   * Not `propVantage(kind).distance`: the vantage pulls itself in until a body
   * fits ({@link Witnessing.vantageFor}), so a reader that wants the *prop's*
   * position back out of a vantage has to be told how far it was actually
   * stepped rather than how far the table asked for.
   *
   * Stepping back along the look direction by this recovers the prop: the
   * client's forward is `(-sin yaw, cos yaw)`, so the prop is at
   * `(x - sin(yaw) * distance, y + cos(yaw) * distance)`.
   */
  readonly distance: number;
}

export class Witnessing implements TimeOfDaySource {
  private readonly source: WitnessSource;
  private readonly terrain: TerrainSampler;
  private readonly hud: Hud;
  private readonly journal: Journal;
  private readonly people: People;

  /** Where the player's deeds go, or `null` in a session with no world to write to. */
  private readonly deeds: PlayerDeeds | null;
  private readonly hands: PlayerHands | null;

  private readonly props: PropRegistry;
  private readonly crowd: AmbientCrowd;

  /**
   * The ground the player has actually covered (`map/ground.ts`).
   *
   * The third client-side store, beside the journal and the people, and written
   * from here for the same reason they are: this is the one place that knows
   * where the player is standing on every frame. It is what the map panel is
   * allowed to draw and the only thing that decides so — the terrain sampler
   * answers about every column in the world, so the mask *is* the honesty layer
   * (Phase 5 decision 10).
   */
  private readonly ground: WalkedGround;

  /** The town's reaction to whatever landed today, if anything has. */
  private readonly staging = new BattleStaging();

  /**
   * When the player meets what the simulation is offering (`witness/director.ts`).
   *
   * It owns the "already staged" set, the quiet gap between deliveries, the
   * encounter budget and the log of everything staged; this file owns the
   * crowd, the words, the smoke — and, since Phase 5 decision 9, the *clock the
   * offers are made on* ({@link Witnessing.standWatch}), because a fraction of
   * a day is a thing only the side that measures days can count.
   */
  private readonly director: Director;

  /** How many times a day the feed is offered. See `director.ts`'s `watchesPerDay`. */
  private readonly watchesPerDay: number;

  /**
   * Whether the session is actually being played yet.
   *
   * The host loop steps the world while the click-to-play overlay is still up
   * (`main.ts`), so without this the opening delivery would happen to an empty
   * room. It is set from the same fact that dismisses the key card — the first
   * input of the session — and it is one of the two things the director knows
   * that the simulation never can.
   */
  private playing = false;

  /**
   * The aftermath, re-read whenever the date turns.
   *
   * Derived from the log rather than stored (plan decision 6), so it does not
   * move — but it *grows*: the day somebody is buried, a grave joins the list.
   */
  private features: readonly AftermathFeature[] = [];

  /**
   * Where the props the town has built over are drawn instead, by prop id.
   *
   * **The wave-1.5 graybox ruling — *nothing stands in a street***. A grave, a
   * market board, a chronicle stand and a town board are all placed from a
   * *record's* position or from a fixed offset off the town's centre, and
   * neither knows anything about the town that grew around it — which is how a
   * prop ends up in the middle of a road or inside a building's footprint.
   *
   * The answer is the simulation's ({@link WitnessSource.openGround}) and it is
   * cached here for the reason every other bulk read in this class is cached:
   * laying a town out is expensive, the answer only moves when the town does,
   * and a frame that asked would be asking sixty times a second. Refreshed on
   * the day boundary with the lists it is about
   * ({@link Witnessing.snapProps}).
   *
   * A prop with no entry is drawn where it always was, which is what a town with
   * nothing standing and a prop already on open ground both get.
   *
   * Columns and not tiles: how high the ground is stays this client's question
   * ({@link surfaceZ}), because the read is a pure function of (seed, records)
   * and has to answer the same before the ground is streamed and after — a prop
   * that moved as the world loaded around it would move out from under a player
   * walking up to it.
   */
  private propGround = new Map<string, readonly [number, number]>();

  /** Settlements by id, for the names and positions a route or a group needs. */
  private settlements = new Map<SettlementId, SettlementView>();

  /** The groups on the roads, re-read on the same cadence and for the same reason. */
  private groups: readonly RefugeeGroup[] = [];

  /**
   * The trade legs, which are the lines the roads are drawn along and the
   * bearings the signposts point down.
   *
   * Read on the day boundary with everything else, although the road graph
   * changes about once a decade: it costs one cached call on the far side
   * (`world/wasm.ts`) and a settlement founded during a session would otherwise
   * be a town with no road to it until the player walked somewhere else.
   */
  private routes: readonly RouteLeg[] = [];

  /**
   * The same legs with both ends resolved to positions, for the paper map.
   *
   * Built on the day boundary beside the routes rather than per frame: the
   * routes only move when the log does, and the map panel is opened far less
   * often than a frame is drawn.
   *
   * **It is not what draws the roads.** A road is terrain
   * (`crates/sim-core/src/roadplan.rs`), meshed like the hillside it runs over,
   * so what this list is for is the one thing a *map* needs and the world cannot
   * draw — a line on paper between two named places.
   */
  private roadLines: readonly MapRoad[] = [];

  /**
   * The trade the simulation ran over the roads this turn, and the same cadence
   * again. Nothing is drawn for one: what reads it is the row that says a body
   * standing here is a pulse's named talker ({@link Witnessing.talkerNote}).
   */
  private pulses: readonly TradePulse[] = [];

  /** What the simulation says is worth meeting here, in its own order. */
  private beats: readonly SpotlightBeat[] = [];

  /**
   * The books standing in the visited settlement, re-read on the day boundary.
   *
   * Sim truth and the whole of it (Phase 5 decision 5): a book the player left
   * and a book a victorious ruler left ten reigns ago come back in the same
   * list and are drawn by the same prop, which is the NPC parity test on this
   * verb answered by there being no second code path.
   */
  private books: readonly ChronicleView[] = [];

  /**
   * The Book of Eras as it stands, re-read on the day boundary with the rest of
   * the bulk.
   *
   * Day-boundary rather than per frame for the reason everything above it is:
   * the ages break when a record lands, so a day is the smallest span in which
   * the book can have changed at all. It is read whether or not anybody opens
   * it, because the *town's copy is a prop* and whether one is drawn depends on
   * there being ages to read ({@link Witnessing.erasStand}) — a session that
   * only paid for the book when somebody opened it would be a session where the
   * thing to open was not standing there.
   *
   * `null` only before the first read, which is the first thing the constructor
   * does ({@link Witnessing.eras} covers it either way).
   */
  private book: SagaBook | null = null;

  /** Which page of the Book of Eras is open at a shelf. Zero when it is shut. */
  private erasPage = 0;

  /** The towns still in the wreckage of the last army through them, by id. */
  private ruins = new Map<SettlementId, RuinView>();

  /**
   * Where the wreckage stands in the visited settlement, laid out here.
   *
   * An embodiment decision like every other position in this file: the
   * simulation says a town is in ruin and says nothing whatever about where
   * anything is (`world/types.ts`, {@link RuinView}). Computed on the day
   * boundary rather than per frame so the anchors can be laid out clear of it —
   * a body loitering on the one thing in a burned town the player can read
   * would take every press meant for it ({@link PROP_CLEARANCE_TILES}).
   */
  private ruinProps: readonly { readonly x: number; readonly y: number; readonly z: number }[] = [];

  /**
   * Whether the visited settlement was in ruin at the last read, and whether
   * that answer has just changed.
   *
   * The crowd is anchored when the player *reaches* a town and at no other time,
   * which is what keeps a body walking rather than snapping back to its anchor
   * (the header's note on what moves and when). A town catching fire under the
   * player is the one other thing that has to move it: how many people are in
   * the street and how they carry themselves are both facts about the ruin
   * (decision 11), and a session that watched the sack land and saw nothing
   * change would be the client holding yesterday's town.
   */
  private ruinedHere = false;
  private ruinTurned = false;

  /**
   * Everything the player has to be able to walk up to and address, for the
   * anchoring to keep bodies off.
   *
   * The aftermath is a third of it: books and wreckage are addressed the same
   * way and take the press the same way, so they belong in the same clearance
   * ({@link Witnessing.clearRadius}). Built on the day boundary beside the
   * things it is built from.
   */
  private clearances: readonly { readonly x: number; readonly y: number }[] = [];

  /**
   * The figure the player's deeds cite, and the simulation's own introduction
   * of it.
   *
   * Polled on the fixed step rather than on the day boundary, and it is the one
   * read in this file that is: a claim lands on a tick and the moment it does is
   * a moment the client is watching for — the journal's header, the trace view's
   * listener line and the crowd's exclusion all turn on it. The read is a scalar
   * with no bulk behind it and the boundary leaves it uncached for exactly this
   * (`world/wasm.ts`).
   *
   * The introduction is pulled once per *change*, because it is a string and
   * strings cross per call (Phase 3 decision 11) — and because it is the only
   * way the client can read the player's own byname at all. There is no
   * `figureName`/`byname` on {@link WitnessSource}; `introduce` is the whole
   * surface, and it is the right one: an epithet is the simulation's judgement
   * of a figure's records, and a client that assembled "name, the byname" out of
   * two strings would be composing an identity (decision 6b).
   */
  private player: FigureId | null = null;
  private playerIntroduced = "";

  /**
   * The life this session was born into, or `null` in one nobody was born in.
   *
   * **Held rather than derived**, unlike everything else in this file, and that
   * is what it is: the birth screen happens before the session — before the loop,
   * before the controls, before this class has stepped once (`hud/origins.ts`) —
   * so the card the player read and the name they typed exist nowhere else by the
   * time anything could ask. What is kept is exactly what the trace view needs to
   * show the records the life stands on ({@link Witnessing.bornTraced}); who the
   * player *is* is still read off the world every step like any other claim.
   */
  private born: BornLife | null = null;

  /**
   * What the player has told which town this session: `settlement:event`.
   *
   * **Client-side memory over a sim-side truth, and it exists for one tick's
   * worth of latency.** The offer is a comparison of the journal against
   * `rumors(here)`, which is the real answer — but a `tell` is queued and lands
   * on the *next* tick, so between pressing the button and the world knowing, a
   * panel that only asked the simulation would keep offering the thing that was
   * just handed over. This is what stops the nag.
   *
   * It is not a claim that the telling *landed*: the simulation decides whether
   * the town learned anything and a tell that taught nothing is still a thing
   * the player did and does not need offering twice. Dying with the tab is
   * correct for the same reason the journal does — the replay is the save
   * format, and this is a fact about a UI.
   */
  private readonly told = new Set<string>();

  /** What the player holds that the visited settlement does not, oldest first. */
  private share: readonly JournalEntry[] = [];

  /**
   * Which books and ruins are already on the map, so a mark is written once.
   *
   * `map/ground.ts` refuses a mark on ground the player has never been near and
   * counts the refusal, which is the honesty guard — so a thing that has *not*
   * been marked yet is retried on every frame it is drawn, and lands on the
   * paper the moment the player has earned the ground under it.
   */
  private readonly marked = new Set<string>();

  /**
   * Whether the player is choosing pages for a book, and which records they
   * have chosen, in the order they chose them.
   *
   * The order is the citation order and click order is the only ordering a
   * player can express by pressing lines in a list, which is the whole of the
   * compile UI (`hud/panels.ts`'s `JournalBook`). Bounded by
   * {@link MAX_CHRONICLE_ENTRIES} here as well as at the wall — the simulation
   * drops a seventeenth citation whatever the client does, and knowing the
   * number is what lets the panel stop offering pages instead of letting one go
   * missing.
   */
  private compiling = false;
  private picked: EventId[] = [];

  /**
   * Ticks to wait before re-reading the shelf, or zero.
   *
   * A book is left by a *command*, so it does not exist until the tick that
   * consumes it — and the shelf is otherwise a day-boundary read, which at
   * twenty-minute days would mean a player standing over a book they left and
   * not seeing it for a quarter of an hour. Two ticks is the queue plus the step
   * that applies it, with one to spare.
   */
  private booksAwaited = 0;

  /**
   * Where the player was standing when the last frame was drawn.
   *
   * The staged reaction picks who speaks by proximity and is advanced on the
   * fixed step, which has no position argument — so it uses the frame's, which
   * is at most a thirtieth of a second and four fifths of a tile behind the
   * truth. That is smaller than the body it is measuring to.
   */
  private from = { x: 0, y: 0, z: 0 };

  /**
   * Which features have something carved on them, by feature id.
   *
   * **Whatever the sim carved carries text.** `sim-core`'s aftermath cuts an
   * inscription for a burial — a figure reference, a name over two dates (items
   * §10.4, plan decision 6) — and, since Phase 4 decision 10c, a record
   * reference for a battlefield marker and a burned farmstead too. The client
   * still authors nothing (decision 9): a feature offers the read verb exactly
   * when `inscription(...)` comes back non-empty, so a kind the sim leaves
   * uncut stays a prop and the player is never given a prompt that does
   * nothing.
   *
   * Asked **once per feature for the life of the session**, not once per day: a
   * feature id is stable (`world/wasm.ts` hands out a counter keyed on the
   * record and the kind) and what is carved on a stone derived from a record
   * that has already happened does not change. So the day boundary pays for the
   * graves it has just learned about and nothing else, which is what keeps the
   * re-read a read rather than a rebuild.
   */
  private readonly readable = new Map<number, boolean>();

  /** The world's date as of the last boundary the client noticed. */
  private today: WorldDateView;

  /**
   * The town this session is drawing, or `null` for nowhere.
   *
   * `WitnessSource.here()` as of the last step, which since item 6's rider 4 is
   * the simulation's promotion slot and nothing else (`world/types.ts`). Every
   * read in this file that is *about a settlement* — the roster, the market, the
   * board, the beats, the books, the lots — is about this one, and `null` is a
   * real answer that each of them has a branch for: a player out past every
   * town's halo is in none of them, and so is a session before its first tick.
   */
  private settlement: SettlementId | null;

  /**
   * The settlement this session opened in ({@link WitnessOptions.home}).
   *
   * Read once, by the claim, and never again.
   */
  private readonly home: SettlementId | null;

  /**
   * Whether the last roster read belonged to somewhere else.
   *
   * A roster entry carries the settlement it belongs to (`world/types.ts`), so
   * the client can tell — and when it is somebody else's, the honest thing is to
   * keep the names this town was already drawn with and ask again at the next
   * day boundary rather than to label its people out of another town's list.
   *
   * **The live source's cache is keyed on where the player is, so this should
   * never fire** (Phase 4 decision 10a). The guard is kept anyway for the reason
   * it was written: it is the *client's* own check on data it did not produce, it
   * costs one comparison on a day boundary, and the failure it prevents looks
   * like a simulation bug and is not one. `dev/selfcheck.ts` forces the stale
   * read deliberately to keep it honest.
   *
   * What it guards changed with item 6 and the guard did not: the roster used to
   * be *where people stood* and is now only *what they are called*, because a
   * body's position is the simulation's.
   */
  private rosterStale = false;

  /**
   * Seconds of stepped time since the date last turned, and how long the last
   * whole day took.
   *
   * The two halves of the sub-day clock — see {@link DAY_SECONDS_GUESS} for why
   * the client measures a day rather than being told how long one is. Three
   * things read it now: the walk down the roads, the watch cadence, and the
   * time of day the sky is drawn from ({@link Witnessing.phase}).
   */
  private sinceDay = 0;

  /** How long since the watch last said anything to the player about a crime. */
  private sinceAccusation = 0;
  private daySeconds = DAY_SECONDS_GUESS;

  /**
   * The row a hand is on the player's shoulder for, or `null` — which is every
   * step of every session nobody is being arrested in.
   *
   * The one frame of memory {@link Witnessing.watchTheRow} needs: a reprimand is
   * the row and the hand both being *gone*, so what closed has to have been
   * written down before it closed (`witness/law.ts`'s {@link RowInHand}).
   */
  private heldFor: RowInHand | null = null;

  /**
   * Whether a date change has been *seen*, and whether a whole day has been
   * watched from one to the next.
   *
   * Two flags rather than one, because they answer two different questions and
   * the first is what makes the second honest. A session opens partway through
   * a day at whatever tick the bootstrap left the world on, and `sinceDay`
   * starts at zero regardless — so the span from construction to the first
   * boundary is a *fragment* of a day and believing it as a whole one is how a
   * twenty-minute day gets measured as three minutes. The first boundary is
   * therefore only an anchor; the second is the first measurement.
   *
   * Until both are true the client genuinely does not know the time of day, and
   * says so ({@link Witnessing.phase} answers `null` and the sky shows fixed
   * morning light rather than a guess it would have to correct). **That is the
   * cost of measuring instead of asking**, which is why the simulation's own
   * `time_of_day()` is asked first: it knows the tick within the day on the
   * first frame (plan decision 8). What is left measuring is a source with no
   * clock, which is the fixture.
   */
  private dayAnchored = false;
  private dayMeasured = false;

  /**
   * Seconds since the day turned at which the next watch stands.
   *
   * The offer cadence (`witness/director.ts`'s `watchesPerDay`), kept as a next
   * time rather than a count so that resetting it — a new day, a new town —
   * cannot make several watches fall due at once and spend a morning's budget
   * in one step.
   */
  private nextWatch = 0;

  /**
   * Which watch of the day was last stood, when the simulation is answering
   * what time it is. `-1` before the first of a day or a town.
   *
   * **The cadence's better clock** (Phase 5 decision 9). The alternative,
   * {@link Witnessing.nextWatch}, divides a *measured* day — and a measurement
   * needs two boundaries to exist, so on a session's first day it is dividing
   * {@link DAY_SECONDS_GUESS} instead, which is a round placeholder and not a
   * day. A cadence that is wrong on the one day every session has is a cadence
   * that is wrong.
   *
   * Where the simulation answers, the day fraction is exact from the first
   * frame, so a watch is a *slice of the day* rather than a stretch of seconds:
   * the sixth of a day the player is standing in is the watch they are standing
   * in, and it needs no measurement, no anchor and no guess. The seconds path
   * below is what a source with no clock gets, which is the fixture.
   */
  private watchSlot = -1;

  /**
   * The revision of the walked ground the open map was last drawn at.
   *
   * A map left open while the player walks has to keep up, and the *day*
   * boundary is the wrong cadence for it — new ground arrives every couple of
   * seconds and the date turns every twenty minutes. So this is the one surface
   * in the client with a refresh trigger of its own: one integer compare per
   * step, and a redraw only when there is something new on the paper
   * (`map/ground.ts`'s `revision`).
   */
  private mapDrawn = -1;

  /** What the interact key would address right now, as of the last frame. */
  private target: Interactable | null = null;

  /**
   * The body the *strike* pick is on, or `null` when the crosshair has nobody in
   * front of it.
   *
   * {@link target}'s sibling and a second call into the same selector rather
   * than a second selector ({@link Witnessing.render}): a swing has a shorter
   * cone and a longer reach than a conversation, so it is the same algorithm
   * with two arguments changed.
   */
  private aimedFoe: Interactable | null = null;

  /**
   * Whose offer to follow has been spoken (wave 2F,
   * {@link Witnessing.stageCompany}).
   *
   * A latch and not a memory of anything the simulation holds: the *rule* is
   * asked fresh every frame, and this only stops one sentence being said twice.
   */
  private readonly offersHeard = new Set<FigureId>();

  /** And which followers have said they will come no further, this time. */
  private readonly partingsHeard = new Set<FigureId>();

  /**
   * How many fixed steps have passed since the player's last swing was sent.
   *
   * **The readiness clock, and it is a *client* clock about a *client* press**
   * — the simulation keeps its own beside the held stand and refuses a swing
   * inside it whatever this believes ({@link STRIKE_COOLDOWN_TICKS}). What this
   * is for is the prompt: a crosshair that says *a moment* is a player who
   * understands the cadence they are fighting at, where a key that silently does
   * nothing is a key they stop pressing (the graybox ruling).
   *
   * Counted in **steps** rather than read off a clock, because one fixed step is
   * one tick and the step is the only place this class is called on a schedule
   * ({@link Witnessing.step}). It starts at infinity, which is the honest state
   * of a hand that has never swung.
   *
   * It is reset on the press rather than on the answer, which makes it wrong in
   * exactly one direction: a swing the simulation refused outright — at nobody,
   * out of reach — costs a moment of prompt that the world did not charge. That
   * is the safe direction to be wrong in, and every one of those cases is a
   * refusal this client says out loud anyway.
   */
  private sinceSwing = Number.POSITIVE_INFINITY;

  /**
   * The record that says the player's own figure died, once there is one.
   *
   * Latched rather than re-read, because it is the one fact in this file that
   * cannot become false again and because the session that reads it takes the
   * frame away ({@link Witnessing.playerDeath}).
   */
  private died: EventId | null = null;

  /**
   * The death this session has already answered, so the screen opens once.
   *
   * A player who reaches the origins screen and declines every life on it is a
   * player still standing in the world with a dead figure — and without this the
   * next step would find the same record and open the same screen for ever. A
   * *new* figure's death is a different record, so the door still works for the
   * next life.
   */
  private mourned: EventId | null = null;

  /**
   * What the aiming prompt names as the way to press it, or `null` for nothing
   * ({@link WitnessOptions.promptKey}).
   */
  private readonly promptKey: string | null;

  /*
   * ---------------------- Phase 7: the local tier ----------------------
   *
   * Five pieces of state on a **third cadence**. Everything above is either
   * per-frame presentation or day-boundary bulk; the marketplace moves on a job
   * pulse and a body moves every tick, so these are re-read when the scene is
   * *drawn* and a tick has happened since the last time
   * ({@link Witnessing.localStale}).
   *
   * Cheap, and it is worth saying how cheap: a town's board is a handful of
   * rows and its named roster is about ten people, so the two reads are two
   * small `Int32Array` copies against a frame that costs twenty milliseconds.
   * What is deliberately *not* on this cadence is anything with a string in it
   * or a plan behind it — the lots and the piles are pulled when something
   * changes and never on a clock.
   */

  /** The promoted town's board, as of the last step. Empty when nothing is promoted. */
  private board: readonly JobPost[] = [];

  /** The bodies the promoted town has standing, as of the last step. */
  private bodies: readonly PresentFigure[] = [];

  /** What the player is carrying, as of the last step. */
  private held: readonly CarriedItem[] = [];

  /**
   * What the player has on, and what they could shoot with (wave 2D-b).
   *
   * Read on the same cadence and out of the same pass {@link Witnessing.held}
   * is, because they are the same question asked of two union arms — and
   * because the loose prompt is drawn at frame rate and must not cross the wall
   * to do it.
   */
  private wornItems: readonly CarriedItem[] = [];
  private quiverState: Quiver = { bow: null, arrows: 0 };

  /**
   * The piece the wear verb would put on next, or `null` for a press that would
   * do nothing.
   *
   * **The simulation's own pick, asked rather than guessed**
   * ({@link WitnessSource.wearable}). A client counting armour in the hands
   * would get it wrong the moment somebody carried a second breastplate: what
   * decides is whether the body *part* is bare, and the part table is the
   * simulation's.
   */
  private wearable: number | null = null;

  /**
   * The word for each crime id, once (chunk Fx-2).
   *
   * A string crosses the wall per call and this one is asked about the thing
   * under the crosshair, which changes at frame rate — so the *id* is read every
   * frame and the noun is fetched once, ever. Five rows in a build, and they
   * cannot change inside a session: `crime_name` is a table lookup on the far
   * side (`sim_core::history::CrimeKind::name`).
   */
  private readonly crimeWords = new Map<number, string>();

  /**
   * The crime word for whatever the aiming prompt is on, or `""`.
   *
   * Worked out once in {@link Witnessing.render} and read twice — by the prompt
   * and by the on-screen interact button — because the two say the same thing
   * about the same target and a second read would be a second crossing for one
   * answer.
   */
  private aimedCrime = "";

  /**
   * The body the *loose* pick is on, or `null` when the crosshair has nobody in
   * it.
   *
   * {@link Witnessing.aimedFoe}'s twin over the bow's own cone and range — the
   * same selector at a third call site, which is the shape `witness/interact.ts`
   * predicted and the reason that function takes both as arguments.
   */
  private aimedShot: Interactable | null = null;

  /** The buildings this town has standing, re-read when the place or the day changes. */
  private townLots: readonly StandingLot[] = [];

  /**
   * What each present figure is called, joined once a step rather than once a
   * frame.
   *
   * The roster is a cached read on the far side (`world/wasm.ts`), but a *name*
   * is a string and strings cross per call — so the join belongs on the local
   * tier's own cadence and not on the frame's (decision 11).
   *
   * Joined where the place is read ({@link Witnessing.anchorCrowd}) rather than
   * where the bodies are, which is the two cadences doing what they are for: a
   * position moves every tick and a name moves when the town does.
   *
   * Not replaced while the roster read belongs to somewhere else
   * ({@link Witnessing.rosterStale}): keeping the names this town was drawn with
   * is better than labelling its people with another town's.
   */
  private presentNames = new Map<FigureId, string>();

  /**
   * What a plate calls each life stage, by stage id
   * ({@link WitnessSource.lifeStageWord}).
   *
   * **Four entries for the life of a session.** A stage's word is a fact about a
   * row of the body plan's table and not about a person, so unlike the
   * per-figure *is this one grown* memo it replaced (wave 2A) there is nothing
   * a date turning can change and nothing to clear on a boundary. Which stage a
   * given body is in is not cached at all — it rides the presence row
   * ({@link PresentFigure.stage}), which is a column of a list the client
   * already pulls.
   */
  private readonly stageWords = new Map<number, string>();

  /**
   * What is lying on the tiles worth looking at, by tile key.
   *
   * **A watched set rather than a scan**, which is the simulation's own
   * instruction: there is no *"every pile in the world"* iterator and a scan of
   * the map would be the expensive way to ask a question nobody has
   * (`sim_core::Snapshot::items_on`). What is watched is what the client already
   * has a reason to know about — a tile a dig opened, a site somebody is
   * working, a workshop's bench — and it is re-read when one of those changes
   * rather than on a clock ({@link Witnessing.repile}).
   */
  private readonly piles = new Map<string, Pile>();

  /**
   * Steps left before the piles are re-read after a verb the player pressed.
   *
   * The books' own latch ({@link Witnessing.booksAwaited}), one verb family
   * over and for its reason: a take and a drop are *commands*, so what they did
   * is only visible after the tick that applied them, and a client that re-read
   * in the same frame would read the world as it was.
   */
  private pilesAwaited = 0;

  /**
   * Steps left before the client decides a craft order never reached the board,
   * and the bench it was pressed at.
   *
   * **The one refusal in the client that is a *deferred* prediction** rather than
   * an immediate one: a workshop with nothing it can make today refuses the
   * command in silence (`sim_core::craft`'s `choose`), and knowing which rows a
   * forge can run would mean keeping a copy of the recipe table and the town's
   * stores. So the client presses, waits, and looks — and if the order is not on
   * the board it says the honest generic rather than guessing at a reason
   * ({@link REFUSED_NOTHING}).
   */
  private craftAwaited = 0;
  private craftSite: TilePos | null = null;

  /** Stepped seconds, for the one thing in a scene that moves on its own. */
  private scenePhase = 0;

  /**
   * Where the promoted town's bodies are *drawn*, between the tiles the
   * simulation walks them across (`witness/scenes.ts`).
   *
   * Presentation and nothing else: it is not hashed, not recorded, and a session
   * that ran with it disabled would be the same session — the same standing the
   * facing derived beside it has ({@link Witnessing.facing}). Without it a body
   * drawn from the tiles the simulation walks it across jumps a whole tile at a
   * time.
   */
  private readonly walk: WalkSmoothing;

  /**
   * Which way each of those bodies is turned (`witness/scenes.ts`).
   *
   * Presentation on {@link Witnessing.walk}'s terms and updated where it is —
   * the two are the same idea about the same two reads, one for the position
   * between them and one for the direction between them.
   */
  private readonly facing = new BodyFacing();

  /**
   * Whether a tick has happened since the local tier was last read.
   *
   * **The scene cadence: at most once a frame, and never without a tick behind
   * it.** The board and the bodies are read where they are *drawn*
   * ({@link Witnessing.render}) rather than where the world moves, and that is
   * a correctness decision rather than a saving.
   *
   * Reading them on the fixed step looked right and was not: a fast-forward
   * crosses tens of thousands of ticks between two frames — a night camped
   * through, a month the dev skip walks — and a scene re-read on every one of
   * them is a scene assembled tens of thousands of times for nobody. A headless
   * run of thirty days found it, which is exactly what that driver is for
   * (`scripts/live-smoke.mjs`).
   *
   * The flag rather than a plain per-frame read, because a frame drawn between
   * two ticks has nothing new to read: this makes the cadence *whichever is
   * slower*, the frame or the tick.
   */
  private localStale = true;

  /** Scratch, reused every frame — see the header's note on allocation. */
  private readonly sceneProps: SceneProp[] = [];
  private readonly interactables: Interactable[] = [];

  /**
   * Where this layer says what it just did, or `null` (the ordinary case).
   *
   * {@link WitnessWatch}. Every use of it is a `?.` and none of them reads
   * anything back, which is what makes an unwatched session identical to a
   * watched one.
   */
  private readonly watcher: WitnessWatch | null;

  /**
   * How many times {@link Witnessing.readPlace} has fired, by reason.
   *
   * The Info tab's tally beside the Log tab's lines: a session that has swapped
   * source forty times has a different problem from one that has swapped twice,
   * and the running count is what says which (wave 1.6, item 7).
   */
  private readonly placeReasons = new Map<PlaceReason, number>();

  /** How many bodies {@link WalkSmoothing} has snapped this session. Zero is healthy. */
  private snapCount = 0;

  /**
   * Every body drawn near the player last frame, by prop id, with where it was.
   *
   * The body-appeared/body-vanished diff the investigation asked for, and it
   * keeps the *position* because a body that has gone has nowhere left to be
   * asked about — the last place it stood is the only thing left to say about
   * it. Kept only while somebody is watching: an unwatched session never fills
   * this map.
   */
  private nearBodies = new Map<string, { x: number; y: number; z: number; label: string }>();

  /**
   * Whether {@link Witnessing.readPlace} has fired since the last body diff.
   *
   * What turns *"this body stopped existing"* into *"this body stopped existing
   * because the town swapped source"* — which is the membership rule the wave-1.6
   * note asks each body line to carry.
   */
  private placedSinceDiff: PlaceReason | null = null;

  constructor(options: WitnessOptions) {
    this.watcher = options.watcher ?? null;
    this.home = options.home ?? null;
    this.source = options.source;
    this.terrain = options.terrain;
    this.hud = options.hud;
    this.journal = options.journal;
    this.people = options.people;
    this.deeds = options.deeds ?? null;
    this.hands = options.hands ?? null;
    // `??` would be wrong here and it is the one place in this constructor where
    // it would: `null` is a *meaning* for this option — the session with no key
    // to name — so absent and null have to be told apart.
    this.promptKey = options.promptKey === undefined ? "F" : options.promptKey;
    this.props = new PropRegistry(options.scene);
    this.walk = new WalkSmoothing(
      options.walkTicksPerStep ?? null,
      options.stepSeconds ?? null,
      // The ground a drawn body stands on (chunk Sf). It is this session's own
      // terrain and not a copy of one, so a body's feet, the crowd's feet, the
      // player's feet and every prop in the street are four readings of one
      // sampler.
      options.terrain,
      // And the gait table (wave 2A): a child covers a tile in fifteen ticks
      // and a toddler in twenty-four, so a crowd of mixed ages smoothed at one
      // speed would draw the small ones rubber-banding. The resolver rather
      // than four numbers, because the table is the simulation's.
      (stage) => this.source.walkTicksForStage(stage),
    );
    this.crowd = new AmbientCrowd(options.terrain);
    this.ground = new WalkedGround(options.terrain);

    const knobs = options.director ?? DIRECTOR_DEFAULTS;
    this.director = new Director(knobs);
    this.watchesPerDay = knobs.watchesPerDay;

    this.today = this.source.date();
    // **Usually nowhere**, and that is not a failure: a town is promoted inside
    // the world's first tick and this runs in the boot sequence before it, so
    // the honest reading of a session that has not ticked is that it is standing
    // in no town yet. The first promoted read arrives on the first step as an
    // ordinary arrival ({@link Witnessing.crossBoundary}).
    this.settlement = this.source.here();

    // The same two reads the day boundary makes, made once so the first frame
    // has a town in it: what the log has left lying around, and then what stands
    // in it — in that order, because the wreckage is laid out clear of what is
    // already standing ({@link PROP_CLEARANCE_TILES}).
    this.readWorld();
    this.readPlace("boot");
  }

  /** How many draw calls the props cost before culling. The HUD reads it. */
  get drawObjects(): number {
    return this.props.drawObjects;
  }

  /** What the interact key would address, for the dev handle to read. */
  get aimedAt(): Interactable | null {
    return this.target;
  }

  /**
   * What the on-screen interact button says it would do, or `null` for nothing
   * in front of the player.
   *
   * The aiming prompt's own words without the key in them (`input/touch.ts`: a
   * line that names a key is a lie on a device with no keys), **and the crime
   * word with them** since chunk Fx-2 — a thumb pressing *pick up* is owed the
   * same warning a finger on `F` gets, and two surfaces composing one label
   * separately is how they come to disagree.
   *
   * The word is the one this frame already worked out
   * ({@link Witnessing.aimedCrime}), so the button costs nothing to label.
   */
  get aimedVerb(): string | null {
    const target = this.target;

    if (target === null) {
      return null;
    }

    const tag = crimeTag(this.aimedCrime);

    return tag === "" ? verbFor(target) : `${verbFor(target)} ${tag}`;
  }

  /**
   * Every beat the director has staged this session, with the records behind it.
   *
   * The phase's cross-cutting rule made readable: "every staged beat carries its
   * `EventId`s and the trace view shows them". Read by the trace panel and by
   * the debug handle, which is how a headless session checks that nothing
   * surfaced before its date (`scripts/live-smoke.mjs`).
   */
  get staged(): readonly StagedBeat[] {
    return this.director.log;
  }

  /** What the town is reacting to right now, or `null`. For the debug handle. */
  get reacting(): SpotlightBeat | null {
    return this.staging.beat;
  }

  /**
   * How many watches have been stood this session
   * ({@link Witnessing.standWatch}).
   *
   * For the self-checks, and the only honest way to assert a *cadence*: whether
   * the director is offered the feed once a day or six times is invisible in
   * what it stages, because most watches of most days have nothing to choose.
   */
  get watches(): number {
    return this.director.offers;
  }

  /**
   * Tells the client the session has actually begun.
   *
   * Called from `main.ts` off the same fact that takes the key card away — the
   * first input of the session. Until it is true the director holds whatever it
   * has chosen rather than spending it on a player who is still reading the
   * overlay (`witness/director.ts`, {@link Director.due}).
   */
  begin(): void {
    this.playing = true;
  }

  /** Whether a name has been claimed and the player's deeds have a figure to cite. */
  get claimed(): boolean {
    return this.player !== null;
  }

  /**
   * Puts the name this session was given into the record, once, without asking.
   *
   * **Identity settles fully before spawning** (Phase 5 decision 1, as the
   * owner's ruling of 2026-08-24 sharpened it). The name was typed in the start
   * menus before the world was drawn (`hud/origins.ts`), and a session that gave
   * one has already said everything it has to say about who it is: there is no
   * card out here, no timer in front of it, and no confirmation. `main.ts` calls
   * this once as the session goes live.
   *
   * **The town is the one the session opened in, and never `here()`**
   * ({@link WitnessOptions.home}, item 6). This runs before the world's first
   * tick and promotion happens inside one, so `here()` at this instant is
   * *nowhere* — a claim carrying it would be a claim the simulation silently
   * ignores (`world/types.ts`, {@link WorldSource.claim}: "ignored when
   * `settlement` is nowhere"), which is a player left unnamed for the whole
   * session with nothing said. The boot sequence already chose a town to stand
   * the player in and that is the answer; `here()` says the same thing one tick
   * later.
   *
   * **Nothing is drawn and nothing is said afterwards.** The claim lands as a
   * record like any other deed, `playerFigure()` answers whether it landed on the
   * next step like it does for a born session
   * ({@link Witnessing.watchForClaim}), and a client that congratulated somebody
   * for it would be the reward chrome the phase's brief rules out. A session that
   * gave no name is never touched at all — an unnamed player is simply never
   * cited, which is what makes question 8 of the playtest protocol a question
   * (`docs/playtests/prototype-2-addendum.md`).
   *
   * The name crosses **as typed**, all the way from the card that took it: this
   * hands the same string on. Whitespace, control characters, the ends and the
   * byte cap are the simulation's business at its own wall, because the replay
   * format has to agree with exactly one normalization (`world/types.ts`,
   * {@link WorldSource.claim}) — and `Command::Claim` is unchanged by any of
   * this, which is what makes the ruling a client-side change.
   */
  claimTheName(name: string): void {
    if (name === "") {
      // **An unnamed session claims nothing.** Skipping the start menus' naming
      // step is a real answer (Phase 5 decision 1), so there is no name here to
      // put anywhere and a command carrying an empty one would be the client
      // sending the wall a string nobody gave it. The rule lives here rather than
      // at the call site, spelled once: `main.ts` hands over whatever the menus
      // settled on and this is where an empty answer stops.
      return;
    }

    const seat = this.home ?? this.settlement;

    if (seat === null) {
      // Nowhere to be of. A world with no towns has nobody to be one of, which
      // is the `?bootstrap=0` session — and it has no witnessing layer to reach
      // this line with (`world/wasm.ts`). Spelled anyway, because a silent
      // command carrying a nowhere is exactly the failure the note above is
      // about.
      return;
    }

    // Sent, and never asked after: a tick has no error channel, so a claim the
    // simulation refuses is silent — and a client that watched for the refusal
    // would have to decide what to do about it, which is a beat past spawn and the
    // ruling says there are none. One command, once, and the world's answer
    // arrives through the same poll a birth's does.
    this.deeds?.claim(name, seat);
  }

  /**
   * Remembers the life this session was born into (Phase 6, wave 3C).
   *
   * Called once by `main.ts` when the birth screen settles, and it queues
   * nothing: the command was already sent by the screen that chose it
   * (`hud/origins.ts`), because the card and the command are one decision and a
   * caller holding both has every opportunity to send a life the player
   * declined.
   *
   * What this class does with it is show it in the trace view, and that is all —
   * **a born player is a claimed player** to everything else here. The figure
   * arrives through `playerFigure()` on the next step exactly as a claimed one
   * does ({@link Witnessing.watchForClaim}), the crowd stops drawing them, the
   * journal's header fills in, and the spoken-line lens is set sim-side. Nothing
   * downstream of this needed to learn a second word for the same fact, which is
   * the whole reason decision 4's two doors cost the client one screen.
   */
  bornAs(born: BornLife): void {
    this.born = born;

    if (this.hud.trace.open) {
      this.showTrace();
    }
  }

  /**
   * Advances the crowd by one fixed step, and re-reads the world if the day
   * turned or the player reached another settlement.
   *
   * The two questions asked here every step are the cheap ones on purpose: the
   * date is a field the tick summary already filled in and the visited
   * settlement is sixteen squared distances (`world/wasm.ts`). Everything
   * *behind* them — the aftermath, the roads, the roster — is pulled only when
   * one of them says something changed, which is the cadence plan decision 11
   * asks for.
   *
   * The one thing that now happens on a cadence of its own is the *watch*
   * ({@link Witnessing.standWatch}): the bulk reads stay on the day boundary,
   * and the director is offered what they found several times a day.
   */
  step(stepSeconds: number): void {
    this.sinceDay += stepSeconds;
    this.scenePhase += stepSeconds;
    // One step is one tick, which is what makes a step the right thing to count
    // the swing cadence in ({@link Witnessing.sinceSwing}).
    this.sinceSwing += 1;
    this.crowd.step(stepSeconds);
    // The promoted town's bodies catch up to where the simulation put them,
    // against the roster the last frame read: at most one frame behind, which is
    // a body drawn a fortieth of a tile short of where it is
    // (`witness/scenes.ts`).
    this.walk.step(this.bodies, stepSeconds);

    // Drained every step whether or not anybody is watching, so a count kept
    // while the window was shut is a count the window shows when it opens. The
    // ordinary answer is an empty array and it allocates nothing
    // (`witness/scenes.ts`'s `takeSnaps`).
    for (const snap of this.walk.takeSnaps()) {
      this.snapCount += 1;
      this.watcher?.note(
        "snap",
        `figure #${snap.figure} snapped ${snap.distance.toFixed(2)} tiles — ` +
          `further than a walk between two reads`,
      );
    }

    // The local tier is *marked* here and read when it is drawn
    // ({@link Witnessing.localStale}), which is the one cadence in this file
    // that is deliberately not the step's.
    this.localStale = true;
    this.stage(stepSeconds);
    // Advanced every step rather than only on the day boundary, because the
    // director may be holding a beat for a player who has not started yet, and
    // "the player started" happens on whatever step they press a key on. It is
    // a null check in the common case (`witness/director.ts`).
    this.director.step(stepSeconds, this.staging.active);

    // Who the player is, asked every step because a claim lands on a tick and
    // nothing else says when ({@link Witnessing.player}).
    this.watchForClaim();

    if (this.booksAwaited > 0) {
      this.booksAwaited -= 1;

      if (this.booksAwaited === 0) {
        this.readShelf();
      }
    }

    if (this.pilesAwaited > 0) {
      this.pilesAwaited -= 1;

      if (this.pilesAwaited === 0) {
        this.repileWatched();
      }
    }

    if (this.craftAwaited > 0) {
      this.craftAwaited -= 1;

      if (this.craftAwaited === 0) {
        this.checkCraftLanded();
      }
    }

    const today = this.source.date();
    const here = this.source.here();
    const dayTurned = today.year !== this.today.year || today.dayOfYear !== this.today.dayOfYear;
    const arrived = here !== this.settlement;

    if (dayTurned || arrived) {
      this.crossBoundary(today, here, dayTurned, arrived);
    }

    // Read, then offer, then release — in that order, and the order is the
    // point. A watch is a fraction of a day rather than an event, so it is
    // asked on every step and outside the branch above; on a day boundary it
    // has to come *after* the re-read, so the morning's first watch is offered
    // the morning's feed rather than yesterday's; and the release comes after
    // both, so a beat chosen this step can be staged this step rather than
    // waiting a tick for the next one to notice it.
    this.standWatch();
    this.beginStaging();
    this.answerForIt(stepSeconds);
    // Straight after the accusation, because it is the same conversation's other
    // end: what the watch wanted, and how it stopped wanting it (chunk Fx-7).
    // On every step rather than on the accusation's cadence — a reprimand is the
    // difference between two frames and a client that looked every few seconds
    // would miss the two the row closed between.
    this.watchTheRow();
    this.watchForDeath();

    // The map is the one open panel whose content moves faster than the
    // calendar: the walking that fills it happens every couple of seconds. It
    // is on the day-boundary list as well ({@link Witnessing.readWorld}) —
    // roads and settlements arrive there — and this is the other half.
    if (this.hud.map.open && this.mapDrawn !== this.ground.revision) {
      this.showMap();
    }
  }

  /**
   * Where the world stands in its day, in `[0, 1)` — dawn at zero — or `null`
   * when the client does not know yet.
   *
   * `render/daylight.ts`'s `TimeOfDaySource`, implemented over the measurement
   * this class already keeps for the walk down the roads. Plan decision 8 puts
   * the sky on it: sun, dome, fog and both lights are a function of this one
   * number, and nothing else in the client is allowed to have an opinion about
   * what time it is.
   *
   * # The seam, and it landed
   *
   * **The measurement is the fallback and not the answer.** `time_of_day()` is
   * on {@link WitnessSource} — the simulation's own tick-within-day, exact from
   * the first frame and needing no day to have gone by — and this asks for it
   * first. What is left measuring is a source with no clock, which is the
   * fixture, and that is the path the self-checks exercise.
   *
   * The answer is checked rather than trusted, and deliberately so: a seam that
   * silently accepted ticks where it asked for a fraction would light the world
   * at noon all night and nothing would say why.
   */
  phase(): number | null {
    const asked = this.simPhase();

    if (asked !== null) {
      return asked;
    }

    if (!this.dayMeasured) {
      return null;
    }

    return Math.min(this.sinceDay / this.daySeconds, DAY_FRACTION_CEILING);
  }

  /**
   * The **simulation's** own time of day, or `null` when it has none — the
   * measurement is deliberately not offered here.
   *
   * Split out of {@link Witnessing.phase} because one caller must be able to
   * tell the two apart, and it is the watch cadence
   * ({@link Witnessing.standWatch}). The measured clock saturates: it is capped
   * at {@link DAY_FRACTION_CEILING} so a day running longer than the last one
   * measured cannot wrap into the next dawn, which is right for a walk down a
   * road and wrong for a cadence — a slot count read off a saturated clock stops
   * advancing and the watches stop being stood. The simulation's clock has no
   * such problem, because it turns over exactly when the date does.
   *
   * The answer is checked rather than trusted: a seam that silently accepted
   * ticks where it asked for a fraction would light the world at noon all night
   * and nothing would say why.
   */
  private simPhase(): number | null {
    const asked = this.source.timeOfDay();

    return typeof asked === "number" && Number.isFinite(asked) && asked >= 0 && asked < 1
      ? asked
      : null;
  }

  /**
   * How much of today has gone by, as a fraction, with a floor under it.
   *
   * {@link Witnessing.phase} with the "I do not know" case answered instead of
   * reported: a walk down a road needs a number every frame and a column that
   * refused to move until the client had watched a day would be a column that
   * teleported when it had. So this falls back to the measurement, and the
   * measurement falls back to {@link DAY_SECONDS_GUESS} — being wrong about it
   * costs smoothness and nothing else, because everything sub-day re-anchors to
   * the date at every boundary.
   */
  private dayFraction(): number {
    return this.phase() ?? Math.min(this.sinceDay / this.daySeconds, DAY_FRACTION_CEILING);
  }

  /**
   * Rebuilds the scene's props and works out what the player is addressing.
   *
   * Called once per rendered frame, after the world's meshes have been applied,
   * so a body standing on a chunk that arrived this frame is placed on it rather
   * than under it.
   */
  render(from: { readonly x: number; readonly y: number; readonly z: number }, yaw: number): void {
    this.from = { x: from.x, y: from.y, z: from.z };
    // Where the player is, written down for the map (`map/ground.ts`). Fed here
    // rather than from the fixed step because this is the call that is *handed*
    // a position: the step has none, and reading last frame's would put a false
    // first cell at the origin before the first frame ever drew.
    this.ground.walk(from.x, from.y);
    this.readLocal();
    this.sceneProps.length = 0;
    this.interactables.length = 0;

    this.collectCrowd();

    // Straight after the crowd and before anything else is pushed, so the diff
    // reads a list that is nothing but bodies (wave 1.6, item 7).
    if (this.watcher !== null) {
      this.noteNearBodies();
    }

    // **The order of these two is a tie-break, and since chunk B it is
    // deliberate.** A workshop's subject stands on the lot's work tile and so
    // does everything lying on that tile — the same column, the same probed
    // ground — so the two are at exactly one distance from the player and
    // {@link selectInteractable} gives the tile to whichever went into the list
    // first. The rule the order states: **a thing standing on a tile is
    // addressed before the tile's own standing verb.** A heap is taken once and
    // a bench is offered for ever, so the bench is the one that can afford to
    // yield — and the two things that would break if it did not are what a craft
    // leaves on the bench that made it (`sim_core::craft` puts the product on
    // the work tile, and a bar nobody can reach is a dead end) and every
    // library's shelf, which stands on the library's own work tile and would be
    // shadowed by the library's work verb, taking decision 14's beat with it.
    //
    // **What makes that order safe is the collector below it.** Until chunk B a
    // pile offered `pick up` over whatever was on the tile, so a smithy's own
    // table won the bench with a press the simulation refused in silence; a pile
    // now offers nothing unless the press is one the world answers
    // ({@link Witnessing.pileSubject}), and on a furnished bench the workshop is
    // the only subject there.
    this.collectScenes();
    this.collectWorkshops();
    this.collectFeatures();
    this.collectChronicles();
    this.collectRuin();
    this.collectRefugees();
    this.collectSignposts();
    this.collectTownBoards();
    this.collectSmoke();

    this.props.sync(this.sceneProps);

    // **And a body standing on a heap's tile wins the subject** (chunk Fx-7,
    // closing Fx-2's filing): `take` lifts the body first, so a prompt that
    // named the heap carried the body's crime word — `pick up a boulder
    // (abduction)` (`witness/law.ts`'s {@link bodyBeforeHeap}).
    this.target = bodyBeforeHeap(
      selectInteractable(from, yaw, this.interactables),
      this.interactables,
    );
    // What the town would hold against this press, worked out once for the two
    // surfaces that say it: the prompt under the crosshair and the on-screen
    // button beside it ({@link Witnessing.aimedCrime}).
    this.aimedCrime = this.target === null ? "" : this.crimeOfPress(this.target);
    this.hud.prompt.set(
      this.target === null ? null : promptFor(this.target, this.promptKey),
      crimeTag(this.aimedCrime),
    );
    this.hud.word.set(this.wordPrompt());

    // **The body-aware pick: the same algorithm at a second call site** (wave
    // 2D-a, and `witness/interact.ts`'s note). A swing has a shorter cone and a
    // longer reach than a conversation, and range and cone have been call-site
    // arguments since the selector was written for exactly this.
    this.aimedFoe = selectInteractable(
      from,
      yaw,
      this.interactables.filter((entry) => entry.subject.kind === "npc"),
      STRIKE_RANGE_TILES,
      STRIKE_CONE_COS,
    );
    // And the bow's pick: the same algorithm at a **third** call site, over a
    // longer range and a narrower cone, because a shot is at what you are aiming
    // at where a swing is at what is in front of you (`witness/arms.ts`).
    this.aimedShot = selectInteractable(
      from,
      yaw,
      this.interactables.filter((entry) => entry.subject.kind === "npc"),
      LOOSE_RANGE_TILES,
      LOOSE_CONE_COS,
    );

    // **Nothing about a body is said while its own death screen is up.** The
    // origins panel is drawn over the frame either way, and a swing prompt over
    // a dead figure would be the one line in the HUD that is not true.
    this.hud.swing.set(
      this.died === null ? this.swingPrompt() : null,
      this.died === null ? crimeTag(this.strikeCrime()) : "",
    );
    this.hud.loose.set(
      this.died === null ? loosePrompt(this.looseState(), this.promptKey === null ? null : "R") : null,
    );
    this.hud.condition.set(this.died === null ? this.conditionLine() : null);

    // **And the two beats a companionship speaks** (wave 2F). Both lines are the
    // simulation's ({@link WitnessSource.followOffer},
    // {@link WitnessSource.partingLine}); what this client decides is *when* one
    // is said, which is the same division of labour every staged beat in this
    // file works under.
    this.stageCompany();
  }

  /**
   * Says the offer to the player's face, and the parting when a follower stops.
   *
   * **Client staging over a simulation projection**, which is the split the
   * whole speech layer is built on: the sim decides *whether* somebody would
   * come along and *what they say about it*, and a renderer decides when a
   * player is standing close enough to hear it.
   *
   * Two latches, and they are latched differently on purpose:
   *
   * - **an offer is said once per person, ever.** The rule holds for as long as
   *   the deeds and the dials do, so a line said every time the crosshair passed
   *   over somebody would be a person shouting the same sentence down a street.
   * - **a parting is said once per parting.** It is cleared when the follower
   *   can come again, because walking back into town and out of it again is two
   *   partings and a player who heard nothing the second time would think the
   *   companion had left.
   */
  private stageCompany(): void {
    const aimed = this.target;

    if (aimed !== null && aimed.subject.kind === "npc" && aimed.subject.follow === "beckon") {
      const who = aimed.subject.figure;

      if (!this.offersHeard.has(who)) {
        const said = this.source.followOffer(who);

        this.offersHeard.add(who);

        if (said !== "") {
          this.hud.dialogue.say(this.nameOf(who), said, null, null);
        }
      }
    }

    const player = this.source.playerFigure();

    if (player === null) {
      return;
    }

    for (const follower of this.source.companions(player)) {
      const said = this.source.partingLine(follower);

      if (said === "") {
        this.partingsHeard.delete(follower);

        continue;
      }

      if (this.partingsHeard.has(follower)) {
        continue;
      }

      this.partingsHeard.add(follower);
      this.hud.dialogue.say(this.nameOf(follower), said, null, null);
    }
  }

  /**
   * The line that says what the left mouse button will do right now, or `null`
   * when the answer is the one it has always been.
   *
   * # The disambiguation rule, in one sentence
   *
   * **Mouse-0 swings when the strike pick has a foe in reach, and digs
   * otherwise.** A *foe* is a drawn body inside the strike cone and range, whose
   * tile the simulation would let a blow cross
   * ({@link withinStrikeReach}), and which is one of two things:
   *
   * - somebody the player is **armed** for — there is a blade in their hands,
   *   and a hand with a blade in it pointed at a person is a hand that means it;
   * - somebody **already in the fight** — answering the player, running from
   *   the player, or a watchman taken up with them.
   *
   * Everything else is a dig, including an unarmed player pointing at a
   * neighbour who has done nothing: a pick that swung there would turn every
   * mistimed dig in a crowded street into an assault the town then answers.
   *
   * # And why the line is not on the screen at every moment
   *
   * The brief asks that the prompt always say what mouse-0 will do. What it says
   * is *the answer whenever there is a question*: the line is drawn whenever the
   * client is aiming at a **body** at all, which is every frame in which the
   * answer could be anything other than *dig*. A permanent `mouse — dig` under
   * the crosshair for the ninety-nine per cent of a session spent looking at
   * hillsides would be chrome, and chrome is what a player stops reading.
   *
   * **The one exception is a hand on the player's shoulder** (chunk Fx-2): an
   * arrest is a moment with three answers and they are true wherever the player
   * is looking, so `witness/law.ts`'s {@link arrestBeat} is drawn on this line
   * whether or not there is anybody in the crosshair. It used to be a clause
   * appended only while the *aimed* body was the arrester, and the live proof is
   * what found that wrong: a guard standing a hand's breadth behind a neighbour
   * is an arrest with nothing on the screen about it.
   *
   * Where the answer is *dig* **with a body in front of them**, the line says
   * so and says why — too far, or a child's hands — because those are ways a
   * player expects a swing and does not get one.
   *
   * # And the fourth answer, which is no answer (chunk Fx-2)
   *
   * **Nothing**, ruled by the owner on 2026-08-31 (*"option 2 it is, it's just
   * that the message was weird"*): a person filling the crosshair with no
   * quarrel used to fall through to a dig at the voxel behind them, which reads
   * as digging through them. The gate stands and the offer is empty
   * (`witness/law.ts`'s {@link mouseAnswer}), so the line says the button will do
   * nothing and says why — and the press agrees, because both read the one
   * derivation ({@link Witnessing.mousePress}).
   */
  private swingPrompt(): string | null {
    // **The arrest beat first, because it is true wherever the player is
    // looking** (chunk Fx-2, and the live proof is what found it: a guard with a
    // neighbour standing a little nearer used to take the beat off the screen).
    // It rides this slot rather than a sixth of its own — it is about the same
    // button and the same moment — and it is the only thing this line says when
    // there is nobody in the crosshair at all.
    const held = this.arrestingMe();
    const beat = held === null ? null : arrestBeat(this.nameOf(held));
    const aimed = this.aimedFoe;

    if (aimed === null || aimed.subject.kind !== "npc") {
      return beat;
    }

    const key = this.promptKey === null ? "" : "mouse — ";
    const under = (line: string): string => (beat === null ? line : `${line} · ${beat}`);
    const at = aimed.subject.at;
    const player = this.source.playerFigure();

    if (player !== null && this.source.lifeStage(player) >= 0 && !this.canFight(player)) {
      return under(`${key}dig · ${REFUSED_A_CHILD_CANNOT_FIGHT}`);
    }

    if (at === null || !withinStrikeReach(this.playerTile(), at)) {
      return under(`${key}dig · ${REFUSED_OUT_OF_SWING}`);
    }

    if (!this.isFoe(aimed.subject.figure)) {
      return under(`${key}nothing · ${REFUSED_NO_QUARREL}`);
    }

    // **Readiness rather than a silent refusal** (the graybox ruling): a swing
    // inside the cooldown is refused by the simulation without a word, so the
    // line says the arm is still coming back instead of the key doing nothing.
    // The absence of that clause is the promise that the next press lands.
    const ready = this.swingReady();
    const swing = ready
      ? `${key}strike ${aimed.label}`
      : `${key}strike ${aimed.label} (a moment)`;

    return under(swing);
  }

  /**
   * The line at the edge of the frame that says how the player's own body is.
   *
   * **The simulation's word, plus what the simulation says is wrong with it**
   * ({@link WitnessSource.condition} and {@link WitnessSource.afflictions}), and
   * nothing else: no number, no bar, no fraction. The phase's rule is that any
   * damage number computed in the client is a bug by definition, and the honest
   * reading of it here is that the client is not given one to compute with.
   *
   * `null` for a whole body, which is almost every session forever — and it is
   * the same *"an unmarked body says nothing at all"* rule the name plates
   * follow (`witness/scenes.ts`).
   *
   * The style of it is deliberately temporary: the UI-direction conversation
   * (filed at the wave-1.6 reconciliation) is where a condition readout gets
   * designed. What this is, is the one sentence a playtest needs to be able to
   * tell *hurt* from *about to die*.
   */
  private conditionLine(): string | null {
    const player = this.source.playerFigure();

    if (player === null) {
      return null;
    }

    const word = this.source.conditionWord(this.source.condition(player));
    const flags = this.source.afflictions(player);
    const clauses: string[] = [];

    if (word !== "") {
      clauses.push(word);
    }

    // The low-blood treatment the brief asks for, said the way somebody would
    // say it rather than as a fraction: the simulation decides *faint* and this
    // decides the words.
    if ((flags & AFFLICTED_FAINT) !== 0) {
      clauses.push("your sight is greying");
    }

    if ((flags & AFFLICTED_OFF_ITS_FEET) !== 0) {
      clauses.push("your legs will not hold you");
    }

    if ((flags & AFFLICTED_EMPTY_HANDED) !== 0) {
      clauses.push("your hands will not close");
    }

    return clauses.length === 0 ? null : clauses.join(" · ");
  }

  /**
   * The line that says the player is carrying something this town has not
   * heard, or `null`.
   *
   * **Two facts and no third one.** The journal is the player's own and
   * `rumors(here)` is the town's own, and what this says is that the first
   * holds something the second does not — which is exactly the comparison the
   * share-word flow is (`world/types.ts`, {@link WorldSource.tell}: "a
   * client-side comparison of two lists the simulation already answers with").
   * It names no record and describes none: that would be the client composing a
   * sentence about one, which is the thing above this seam that is never
   * allowed (devlog #2).
   *
   * **Gated on `here()`, which is the promotion slot** (item 6's rider 4). It
   * used to be gated on a client radius around the town's centre —
   * `IN_TOWN_TILES`, deleted — because `here()` was the nearest town at any
   * distance and a share-word offer on that authority would be shouting at a
   * hillside. The slot answers the same question better and answers it once: a
   * town the simulation is not holding up is a town the player is not in, and
   * `null` here is the honest "nowhere to hand it to".
   */
  private wordPrompt(): string | null {
    if (this.share.length === 0) {
      return null;
    }

    const centre = this.town;

    if (centre === null) {
      return null;
    }

    const count = this.share.length === 1 ? "one thing" : `${this.share.length} things`;

    return `J — ${centre.name} has not heard ${count} you know`;
  }

  /**
   * Fires the interact verb on whatever the last frame aimed at.
   *
   * Every branch does the same three things in the same order — ask the
   * simulation, show what it said, write down that it was said — because that
   * ordering *is* the phase's claim: nothing the player sees was composed here,
   * and what they remember is a reference to the record they were shown.
   *
   * Two of the branches write down a fourth thing since wave 3A, and it is the
   * player's rather than the world's: **they met somebody**
   * (`journal/people.ts`). A refugee column does not, and cannot — a group is a
   * population and not a person (history §8.4), so `speakGroup` names no figure
   * and there is nobody to remember.
   */
  interact(): void {
    const target = this.target;

    if (target === null) {
      // Said rather than swallowed (wave 1.5's ruling): `F` with nothing in
      // front of the player is the commonest press in the game that does
      // nothing, and silence makes it read as a key that is not bound.
      this.hud.feedback.say(REFUSED_OUT_OF_REACH);

      return;
    }

    switch (target.subject.kind) {
      case "npc":
        // Three verbs on one subject, and the simulation chose which
        // ({@link Witnessing.carryVerbFor}). The two carry arms go through the
        // *same* doors an item's pickup goes through, because a portable body
        // is one more thing lying on a tile as far as a pair of hands is
        // concerned (`sim_core::hands`).
        if (target.subject.carry === "take" && target.subject.at !== null) {
          this.pickUp(target.subject.at);

          return;
        }

        if (target.subject.carry === "drop" && target.subject.at !== null) {
          this.dropCarried(target.subject.at);

          return;
        }

        // And wave 2F's two, in the same shape and under the same rule: the
        // simulation chose which of them this subject carries
        // ({@link Witnessing.followVerbFor}), and the press is a confirmation
        // of an offer the world made rather than a request.
        if (target.subject.follow === "beckon") {
          this.takeAlong(target.subject.figure, target.subject.at);

          return;
        }

        if (target.subject.follow === "dismiss") {
          this.hands?.dismiss(target.subject.figure);

          return;
        }

        this.talkTo(target.subject.figure, this.settlement, "npc");

        return;

      case "refugees":
        this.deliver(this.source.speakGroup(target.subject.group), "refugee");

        return;

      case "grave":
        this.readGround(target, target.subject.feature);

        return;

      case "market":
        if (this.hud.market.open) {
          this.hud.market.hide();
        } else {
          this.showMarket();
        }

        return;

      case "chronicle":
        if (this.hud.book.open) {
          this.hud.book.hide();
        } else {
          this.readBook(target.subject.chronicle);
        }

        return;

      case "eras":
        this.readEras();

        return;

      case "ruin":
        this.readRuin(target.subject.settlement);

        return;

      case "item":
        this.pickUp(target.subject.at);

        return;

      case "workshop":
        this.workAt(target.subject.at, target.subject.building);

        return;

      case "shelf":
        this.readShelfAt(target.subject.at);

        return;
    }
  }

  /**
   * Takes the marketplace's work at whatever the crosshair is on.
   *
   * `G`, and it is the *aimed* verb of the four: what it claims is work done
   * **to** a tile, so it names one through the crosshair ray exactly as digging
   * does (`main.ts` traces it and hands the tile over).
   *
   * **Nothing is decided here, and something is asked.** Whether that tile is a
   * seam of rock, a trunk, a patch of growth or nothing at all is the
   * simulation's own predicate — the same one that emits a town's own work — and
   * this client keeps no copy of it. What it does is *ask*
   * ({@link WitnessSource.workAt}) so that a press the world would refuse in
   * silence can say why instead, which is the graybox ruling
   * ({@link REFUSED_NO_WORK}).
   *
   * The command still goes out whenever there is work there, and the world still
   * has the last word: a tree somebody felled between the ask and the tick is
   * refused exactly as it always was.
   */
  gather(at: TilePos): void {
    if (this.source.workAt(at) === null) {
      this.hud.feedback.say(REFUSED_NO_WORK);

      return;
    }

    // And the one refusal the *board* owns rather than the terrain: a site
    // somebody has already taken. Read off the board the client is already
    // holding, so it costs nothing — and only where there is a board at all,
    // because outside a promoted town the verb goes through the deferred door
    // and there is nobody to be ahead of you (`sim_core::jobs`' `Gathers`).
    if (this.board.some((post) => post.worker !== null && sameTile(post.site, at))) {
      this.hud.feedback.say(REFUSED_TAKEN);

      return;
    }

    this.hands?.gather(at);
    // The site is a tile something is about to be left on, so it joins the
    // watched set now rather than when the log lands on the floor.
    this.watch(at);
  }

  /**
   * Says why an aimed verb hit nothing at all.
   *
   * `main.ts` traces the crosshair and gets `null` when the ray leaves the world
   * without meeting anything solid, which is the one refusal this class cannot
   * be told about by being handed a tile — there is no tile.
   */
  missed(verb: "dig" | "gather"): void {
    this.hud.feedback.say(verb === "dig" ? REFUSED_NO_GROUND : REFUSED_OUT_OF_REACH);
  }

  /*
   * ---------------------------- Swinging at somebody ----------------------------
   *
   * Phase 7's wave 2D-a (plan, W8 and W11). Five members and one rule, and the
   * rule is worth stating before any of them: **nothing here decides what a blow
   * does.** The client picks who, says where the player is standing, and sends
   * the command; the accuracy roll, the part, the tissue contest, the wound, the
   * maiming and the death are the simulation's, through the same pipeline a
   * watchman and a struck neighbour swing through.
   *
   * What this side owns is aim, timing and *refusal* — every one of which is a
   * rule it can apply because the numbers behind them cross the wall
   * ({@link STRIKE_REACH_TILES}, {@link STRIKE_COOLDOWN_TICKS}, the life-stage
   * table). A press the world would refuse in silence says why instead, which is
   * the graybox ruling.
   */

  /**
   * What the left mouse button would do right now: swing, dig, or nothing.
   *
   * **The disambiguation rule, asked once** (`witness/law.ts`'s
   * {@link mouseAnswer} carries the argument, and {@link Witnessing.swingPrompt}
   * is the sentence a player reads it off). `main.ts` calls this on the press
   * and sends one verb, the other, or none, so the line under the crosshair and
   * the press cannot come to different conclusions.
   */
  get mousePress(): MouseAnswer {
    const aimed = this.aimedFoe;

    if (aimed === null || aimed.subject.kind !== "npc" || aimed.subject.at === null) {
      return "dig";
    }

    return mouseAnswer(
      true,
      withinStrikeReach(this.playerTile(), aimed.subject.at),
      this.isFoe(aimed.subject.figure),
    );
  }

  /**
   * Whether the left mouse button would swing rather than dig, right now.
   *
   * {@link Witnessing.mousePress}' first answer, kept as its own question
   * because that is what everything asking it wants to know — and derived rather
   * than re-decided, so the third answer could not be added behind its back.
   */
  get wouldSwing(): boolean {
    return this.mousePress === "strike";
  }

  /**
   * Says why the button did nothing, when the answer was *nothing*.
   *
   * **The graybox ruling applied to an empty offer** (chunk Fx-2): the prompt
   * already says the button will do nothing and why, but a player who presses it
   * anyway is owed the same sentence where every other refusal is written. What
   * it is *not* is a refusal by the simulation — nothing was sent, so there is
   * nothing to be refused; this is the client saying so out loud.
   */
  noQuarrel(): void {
    this.hud.feedback.say(REFUSED_NO_QUARREL);
  }

  /*
   * -------------------- What a press would be answered for --------------------
   *
   * Chunk Fx-2, the owner's ruling of 2026-08-31: **a prompt whose press would
   * be a crime says so, in red, with the crime word on it.** Three members and
   * one rule, and the rule is the same one every refusal in this file is written
   * under — *the judgement is the simulation's and the sentence is the
   * client's*.
   *
   * **This client keeps no crime table and no legal code.** It asks the wall
   * what a press would be ({@link WitnessSource.crimeOfTaking},
   * {@link WitnessSource.crimeOfStriking}), gets back an id off the blotter's
   * own five rows, and asks for the noun. The day a row is renamed or a sixth is
   * minted, every prompt here says the new word without being told.
   *
   * The reads run the verbs' own judgements **minus the witness clause**: the
   * act is a crime whether anybody saw, and witnesses only decide whether it
   * becomes known. A warning that only appeared where somebody was watching
   * would teach a player to look for witnesses rather than to know what they are
   * about to do.
   */

  /** The simulation's noun for a crime id, fetched once ({@link Witnessing.crimeWords}). */
  private crimeWord(crime: number): string {
    const known = this.crimeWords.get(crime);

    if (known !== undefined) {
      return known;
    }

    const word = this.source.crimeWord(crime);

    this.crimeWords.set(crime, word);

    return word;
  }

  /**
   * The crime word for pressing `F` on a subject, or `""`.
   *
   * The tile the press would cite is `witness/law.ts`'s
   * ({@link tileATakeWouldCite}) and the word is the simulation's; what is here
   * is the join between them and the memo that keeps a string off a per-frame
   * path.
   */
  private crimeOfPress(target: Interactable): string {
    const at = tileATakeWouldCite(target.subject);

    if (at === null) {
      return "";
    }

    const crime = this.source.crimeOfTaking(at);

    return crime === null ? "" : this.crimeWord(crime);
  }

  /**
   * The crime word for the swing the mouse is offering, or `""`.
   *
   * **The one the client decides *when* to say**, and the plan says so in as
   * many words: assault is the foe read this client already has. The word is
   * still the simulation's — what is decided here is that the warning belongs on
   * an **unprovoked** blow, which is a blow at somebody who is not already in
   * the fight ({@link Witnessing.inTheFightWith}).
   *
   * Somebody answering the player, running from them or holding them is
   * somebody the exchange is already under way with, and this client cannot say
   * who started it — so it says nothing, which is the same *"where the client
   * cannot know, it says the honest generic"* rule the refusals are held to. The
   * simulation blots what it blots either way.
   */
  private strikeCrime(): string {
    const aimed = this.aimedFoe;

    if (
      aimed === null ||
      aimed.subject.kind !== "npc" ||
      this.mousePress !== "strike" ||
      this.inTheFightWith(aimed.subject.figure)
    ) {
      return "";
    }

    const crime = this.source.crimeOfStriking(aimed.subject.figure);

    return crime === null ? "" : this.crimeWord(crime);
  }

  /**
   * Swings at whatever the strike pick is on.
   *
   * **The stand goes first, always** (Phase 7 plan, W11): a swing from a tile
   * the simulation never heard of is a defect, and submission order inside one
   * tick's batch is what the sim reads — so the pair is queued here rather than
   * left to two call sites that could drift apart. The dedupe is the world
   * source's ({@link WorldSource.stand}), so a player who has not moved sends
   * one command rather than two.
   *
   * Every refusal below is a rule the simulation holds and this client has the
   * number for, said before the command goes rather than after it comes to
   * nothing.
   */
  strike(): void {
    const player = this.source.playerFigure();

    if (player === null) {
      // No figure, no hand. The same silence every other verb gives a session
      // that never took a name: there is nobody to be refused.
      return;
    }

    if (this.source.lifeStage(player) >= 0 && !this.canFight(player)) {
      // **Rider 2's ruling 1, said out loud before the command goes.** The
      // simulation refuses the swing by the same stage row that refuses the
      // child next door, and a played heir under sixteen meets it exactly as an
      // NPC child does.
      this.hud.feedback.say(REFUSED_A_CHILD_CANNOT_FIGHT);

      return;
    }

    const aimed = this.aimedFoe;

    if (aimed === null || aimed.subject.kind !== "npc" || aimed.subject.at === null) {
      this.hud.feedback.say(REFUSED_NOBODY_TO_STRIKE);

      return;
    }

    const here = this.playerTile();

    if (!withinStrikeReach(here, aimed.subject.at)) {
      this.hud.feedback.say(REFUSED_OUT_OF_SWING);

      return;
    }

    if (!this.isFoe(aimed.subject.figure)) {
      this.hud.feedback.say(REFUSED_NO_QUARREL);

      return;
    }

    if (!this.swingReady()) {
      this.hud.feedback.say(REFUSED_STILL_SWINGING);

      return;
    }

    this.hands?.stand(here);
    this.hands?.strike(aimed.subject.figure);
    this.sinceSwing = 0;
  }

  /*
   * ------------------------------ The bow ------------------------------
   *
   * Phase 7's wave 2D-b (plan W4, W5 and decision 8). The rule the swing above
   * is written under holds here word for word — **nothing decides what an arrow
   * does** — with one thing added that the swing has no equivalent for: the
   * shaft is *drawn*, and the drawing is presentation of a fact the simulation
   * resolved before the first frame of it (`render/arrows.ts`).
   *
   * The refusals and the prompt live in `witness/arms.ts`, because they are the
   * verb's rules rather than the witnessing loop's, and because this file was
   * 2,565 code lines when the chunk opened.
   */

  /**
   * Everything the bow's prompt and the bow's press are decided from.
   *
   * Assembled here and read there: the state is this layer's — a pick, a tile,
   * a clock and two arena reads — and the *rules over it* are the arms module's,
   * which is what keeps the sentence a player reads and the press they make one
   * decision rather than two.
   */
  private looseState(): LooseState {
    const aimed = this.aimedShot;
    const player = this.source.playerFigure();
    const at = aimed !== null && aimed.subject.kind === "npc" ? aimed.subject.at : null;

    return {
      target:
        aimed === null || at === null
          ? null
          : { label: aimed.label, inRange: withinBowRange(this.playerTile(), at) },
      quiver: this.quiverState,
      // A world with no player figure has nobody to refuse, which is what the
      // strike verb answers the same question with: `lifeStage` is negative for
      // a handle the world has never had.
      mayFight: player === null || this.source.lifeStage(player) < 0 || this.canFight(player),
      ready: this.looseReady(),
    };
  }

  /** Whether the draw is finished since the last shot or swing. */
  private looseReady(): boolean {
    return this.sinceSwing >= LOOSE_COOLDOWN_TICKS;
  }

  /**
   * Looses an arrow at whatever the shot pick is on, and returns where it flies.
   *
   * **The stand goes first**, exactly as it does for the swing and for its
   * reason (W11): a shot from a tile the simulation never heard of is a defect,
   * and the pair is queued here rather than at two call sites that could drift.
   *
   * The returned point is the **drawn** position of the body the command names,
   * for the renderer to fly a shaft to (`render/arrows.ts`) — and it is returned
   * rather than drawn here because the scene is `main.ts`'s. `null` when nothing
   * was sent, which is every refusal below and is why a refused press draws no
   * arrow: a shaft in the air for a command that was never queued would be the
   * client claiming something happened.
   */
  loose(): { readonly x: number; readonly y: number; readonly z: number } | null {
    const state = this.looseState();
    const refusal = looseRefusal(state);

    if (refusal !== null) {
      this.hud.feedback.say(refusal);

      return null;
    }

    const aimed = this.aimedShot;

    if (aimed === null || aimed.subject.kind !== "npc") {
      // Unreachable: `looseRefusal` answers `nobody in front of you` for exactly
      // this state. Kept because the narrowing is what makes the handle below
      // legal, and a `never` here would be a cast.
      return null;
    }

    this.hands?.stand(this.playerTile());
    this.hands?.loose(aimed.subject.figure);
    this.sinceSwing = 0;

    return { x: aimed.x, y: aimed.y, z: aimed.z };
  }

  /**
   * Puts on the most protective thing the player is carrying.
   *
   * `P`, and it names **nothing**: which piece goes on is the simulation's pick
   * (`sim_core::hands`' `wear`), so a press is a press and never a selection.
   * The panel is where a player finds out they have something to put on, which
   * is the owner's UI rule — menus own choices, and the hands panel is the menu.
   *
   * The refusal is this client's own reading of two arena lists, and it is
   * deliberately coarse: *nothing to put on* covers an empty pair of hands, a
   * pocket full of boulders and a body already wearing everything it carries,
   * because all three are one sentence to a player.
   */
  wear(): void {
    if (this.wearable === null) {
      this.hud.feedback.say(REFUSED_NOTHING_TO_WEAR);

      return;
    }

    this.hands?.wear();
    this.pilesAwaited = HANDS_SETTLE_STEPS;
  }

  /** Takes off the first thing the player has on. {@link Witnessing.wear}'s mirror. */
  doff(): void {
    if (this.wornItems.length === 0) {
      this.hud.feedback.say(REFUSED_NOTHING_WORN);

      return;
    }

    this.hands?.doff();
    this.pilesAwaited = HANDS_SETTLE_STEPS;
  }


  /**
   * What the log says about the player's own death, or `null` while they live.
   *
   * **The one read permadeath is built on** (Phase 7 plan, decision 7), and it
   * is the log's rather than a flag this client keeps: the killer, the weapon
   * and the cause are on the record, and {@link Witnessing.epitaph} turns them
   * into the sentence a death screen shows.
   *
   * Latched on the step that first sees it, because the session that reads it
   * takes the frame away and a re-read every frame afterwards would be a query
   * about a fact that cannot change back.
   */
  get playerDeath(): EventId | null {
    return this.died;
  }

  /**
   * The world's own words about a record, on the board a town would post it on.
   *
   * The whole of what a death screen says, and **not one word of it is written
   * here**: the register is the simulation's written one
   * ({@link WitnessSource.notice}) and the town is wherever the player is
   * standing — or, where they are standing nowhere the world holds up, the town
   * they were of. `null` for a record this world does not have, which is a
   * screen that says nothing rather than a screen that invents something.
   */
  epitaph(record: EventId): string | null {
    const seat = this.settlement ?? this.home;

    return seat === null ? null : this.source.notice(seat, record);
  }

  /**
   * Whether the player's body has given in to pain.
   *
   * §8.2's own state, read as a bit rather than as a number
   * ({@link WitnessSource.afflictions}): the simulation decided it, and what
   * `main.ts` does about it is take the screen away and let the world run —
   * which is presentation of a sim fact rather than a consequence computed
   * here.
   */
  get blackedOut(): boolean {
    const player = this.source.playerFigure();

    return player !== null && (this.source.afflictions(player) & AFFLICTED_SENSELESS) !== 0;
  }

  /**
   * What a crossing in the dark asks about the player's own body.
   *
   * **Five of the simulation's verdicts, at one moment, in one shape**
   * (`world/types.ts`'s {@link RestReading}, chunk Fx-5). It is the read
   * `host/rest.ts` waits on between slices: whether the body is under, whether
   * the simulation would still call it unfit, whether anybody in the town has
   * reached it, what the town holds against it, and whether the watch has had
   * hold of it.
   *
   * **Nothing here is composed and nothing is decided.** *Under* is
   * {@link Witnessing.blackedOut}, spelled once and read by both; *unfit* is a
   * mask of three bits the simulation set ({@link AFFLICTED_UNFIT}); *reached* is
   * the same {@link Witnessing.underThreat} the stand stream is armed off; the
   * crime is the wall's own word and the taking is the log's own record.
   *
   * The reading for a session nobody has claimed a figure in is nothing wrong and
   * nobody coming, which is what makes the verb refuse itself there.
   */
  get bodyReading(): RestReading {
    const player = this.source.playerFigure();

    if (player === null) {
      return NOBODY_RESTING;
    }

    const crime = this.source.wantedFor(player);

    return {
      under: this.blackedOut,
      unfit: (this.source.afflictions(player) & AFFLICTED_UNFIT) !== 0,
      reached: this.underThreat(player),
      wanted: crime === null ? null : this.source.crimeWord(crime),
      taken: this.source.arrestOf(player),
    };
  }

  /**
   * Whether the player's legs will still hold them.
   *
   * §7.5's *"all `STANCE` lost → crawl instead of walk"*, and the client's half
   * of it is a **documented seam**: the player's gait is host physics — the
   * simulation has never moved their body (Phase 3 decision 10) — so the sim's
   * verdict arrives as a bit and the host applies it to its own speed constant
   * (`main.ts`). That is presentation of a sim fact and not a consequence
   * computed client-side, and the difference is that nothing here decides *how
   * hurt* anybody is.
   */
  get offItsFeet(): boolean {
    const player = this.source.playerFigure();

    return player !== null && (this.source.afflictions(player) & AFFLICTED_OFF_ITS_FEET) !== 0;
  }

  /**
   * Latches the player's own death the step the log first carries it.
   *
   * Cheap on every step a player is alive — one read of a biography index
   * sim-side — and it stops being asked at all once it has answered, which is
   * the honest shape of a fact that cannot become false.
   */
  private watchForDeath(): void {
    if (this.died !== null) {
      return;
    }

    const player = this.source.playerFigure();

    if (player === null) {
      return;
    }

    const record = this.source.deathOf(player);

    if (record === null || record === this.mourned) {
      return;
    }

    this.died = record;
  }

  /**
   * Forgets the life that ended, so the session can take another one.
   *
   * Called by `main.ts` after the origins screen has bound an heir (Phase 7
   * plan, decision 7). **Nothing about the late figure is undone** — their death
   * record, their line and their deeds are exactly where they were, which is the
   * whole of what permadeath is here — and what is cleared is this client's own
   * bookkeeping about *whose* session it is: the latched death, the swing clock,
   * and the figure the claim watch is holding.
   *
   * The new figure arrives through the same poll a claimed one does
   * ({@link Witnessing.watchForClaim}), because a birth is a command and a
   * command is answered by the world rather than by the caller.
   */
  liveAgain(): void {
    this.mourned = this.died;
    this.died = null;
    this.player = null;
    this.playerIntroduced = "";
    this.sinceSwing = Number.POSITIVE_INFINITY;
    // Including the row the last life was being held for: an heir walks into a
    // town that wants nothing from them, and a guard who said his piece about
    // somebody else's theft would be this client mixing up two lives
    // ({@link Witnessing.watchTheRow}).
    this.heldFor = null;
  }

  /**
   * Tells the world where the player is standing, if it is worth telling.
   *
   * **W11's whole rule, in one call** (Phase 7 plan): *sent on tile-crossing
   * ONLY while some body is engaged with or winding up at the player's figure*.
   * The two reads are the simulation's own derivations — a watchman who has hold
   * of them ({@link WitnessSource.engagedWith}) and anybody about to answer them
   * ({@link WitnessSource.answering}) — so the client is following the world's
   * decision rather than guessing at one.
   *
   * It is a rule and not an optimisation, and the reason is the replay: a host
   * that stated a tile on every tick of a two-hour walk would be a command log
   * nobody could read. What the log carries instead is every fight and nothing
   * else.
   *
   * The other half of that bargain is stated where it is honest to state it:
   * **while nothing is held, nobody in the world can reach the player at all.**
   * That is not a hole — engagement is what starts the stream, so there is
   * nothing before it to be reached by, and a watchman who cannot reach a player
   * who has walked off leaves the blotter row open (`sim_core::watch`).
   *
   * Called from the fixed step, like every other command.
   */
  standIfWatched(at: TilePos): void {
    const player = this.source.playerFigure();

    if (player === null || !this.needsMyTile(player)) {
      return;
    }

    this.hands?.stand(at);
  }

  /**
   * Whether anybody in the promoted town needs to know where the player is.
   *
   * **Three things, and chunk Fx-2 is the third** (the rule itself, with its
   * argument, is `witness/law.ts`'s {@link needsMyTile}). A wind-up needs it
   * because a blow is measured from it; a **follower** needs it because a
   * companion walks after the tile the host states; and a **wanted player with a
   * watchman beside them** needs it because that is the moment before an arrest,
   * and until the tile is stated there is nothing for the hand to reach.
   *
   * The order the four answers are worked out in is the cost: `wantedFor` is one
   * read and it gates the walk of bodies behind it, so the clause the arrest
   * needs costs an ordinary session nothing at all.
   */
  private needsMyTile(player: FigureId): boolean {
    const wanted = this.source.wantedFor(player) !== null;

    return needsMyTile(
      this.underThreat(player),
      this.source.companions(player).length,
      wanted,
      wanted && this.watchAtHand() !== null,
    );
  }

  /**
   * The nearest watchman standing within earshot of the player, or `null`.
   *
   * **One derivation for the two surfaces that read it** (chunk Fx-2): the guard
   * who says *"the watch wants a word with you"* and the guard whose presence
   * starts the stand stream are the same guard, and two rules here would be a
   * client that accuses somebody it will not then let reach the player.
   *
   * **A watchman is the simulation's own answer** ({@link WitnessSource.isWatchman}:
   * the captain, or somebody the board has on a patrol) rather than the board
   * read this used to do. The board filter missed the two watchmen most likely
   * to be the one coming — a town's captain holds no patrol order at all, and a
   * hand the watch has sent somewhere has left his post — which is the shape of
   * the defect the live probe found.
   *
   * The distance filter runs before the question, so what crosses the wall is
   * one call per body already standing next to the player rather than one per
   * body in the town.
   */
  private watchAtHand(): BodyNear | null {
    const here = this.playerTile();

    return nearestWithin(
      here,
      this.bodies.filter(
        (body) =>
          tilesApart(here, body.at) <= ACCUSATION_TILES && this.source.isWatchman(body.figure),
      ),
    );
  }

  /**
   * Whether anybody in the promoted town is about to swing at the player.
   *
   * The two ways a body ends up striking them, and there is no third: answering
   * a blow they landed, and a watchman's arrest. Both are reads of the
   * simulation's own state rather than of anything kept here.
   */
  private underThreat(player: FigureId): boolean {
    return this.bodies.some(
      (body) =>
        this.source.answering(body.figure) === player ||
        this.source.engagedWith(body.figure) === player,
    );
  }

  /**
   * Who is standing over the player with a hand on their shoulder, or `null`.
   *
   * The engagement, read from the *watchman's* side, which is the only side it
   * is written on: the player has no presence for the simulation to write theirs
   * into (`sim_core::watch`'s release note), and a player who is not held in
   * place by a hashed flag is a player who can walk away — which is one of the
   * three answers an arrest has.
   */
  private arrestingMe(): FigureId | null {
    const player = this.source.playerFigure();

    if (player === null) {
      return null;
    }

    return (
      this.bodies.find((body) => this.source.engagedWith(body.figure) === player)?.figure ?? null
    );
  }

  /**
   * Whether a body is somebody this client will offer a swing at.
   *
   * **This is the client's own aiming rule and not one the simulation holds**,
   * which is why it is stated here rather than predicted: the world would let a
   * player punch anybody at all. What it stops is the accident — an unarmed
   * player mistiming a dig in a crowded street and assaulting a neighbour the
   * town then answers for.
   *
   * Two ways to be a foe, and the second is the one that keeps a bare-handed
   * fight possible: **armed**, or **already in it** — answering the player,
   * running from them, or a watchman taken up with them.
   */
  private isFoe(figure: FigureId): boolean {
    if (this.source.playerFigure() === null) {
      return false;
    }

    return this.armed() || this.inTheFightWith(figure);
  }

  /**
   * Whether a body is already in it with the player: answering them, running
   * from them, or holding them.
   *
   * {@link Witnessing.isFoe}'s second clause, named since chunk Fx-2 because a
   * second reader wants exactly this half and not the armed one: a blow at
   * somebody already in the fight is not the unprovoked one the crime word warns
   * about ({@link Witnessing.strikeCrime}).
   */
  private inTheFightWith(figure: FigureId): boolean {
    const player = this.source.playerFigure();

    if (player === null) {
      return false;
    }

    return (
      this.source.answering(figure) === player ||
      this.source.fleeingFrom(figure) === player ||
      this.source.engagedWith(figure) === player
    );
  }

  /** Whether there is a blade in the player's hands. */
  private armed(): boolean {
    return this.held.some((item) => item.kind === ITEM_WEAPON);
  }

  /**
   * Whether a figure's life stage lets them fight.
   *
   * The stage table's own fight column, read rather than derived from the word
   * *child*: it is the same row the simulation refuses an NPC child by
   * (`sim_core::body`'s `LifeStage::fights`), which is rider 2's parity ruling
   * kept as one question asked in one place.
   */
  private canFight(figure: FigureId): boolean {
    return this.source.lifeStageFights(this.source.lifeStage(figure));
  }

  /** Whether the cooldown has passed since the last press. */
  private swingReady(): boolean {
    return this.sinceSwing >= STRIKE_COOLDOWN_TICKS;
  }

  /**
   * The tile the player is standing on, floored out of the position the render
   * step was handed.
   *
   * The same arithmetic `PlayerController.tile` does, over the same position,
   * one frame later at worst — and it is done here rather than passed in
   * because every caller of it is a *read* that happens between steps
   * ({@link Witnessing.swingPrompt} runs at frame rate). The one place the tile
   * has to be exact is the command, and that one takes it from
   * {@link Witnessing.strike}'s own call the same way.
   */
  private playerTile(): TilePos {
    return {
      x: Math.floor(this.from.x),
      y: Math.floor(this.from.y),
      z: Math.floor(this.from.z),
    };
  }

  /**
   * Puts down the first thing the player is carrying.
   *
   * `Q`, and it names the tile the player is *standing on* rather than the one
   * under the crosshair: a drop is a thing you do where you are, and aiming it
   * would make putting something down a shot. Which item goes is the
   * simulation's rule — the first in handle order, the one the inventory panel
   * marks (`hud/panels.ts`).
   */
  dropCarried(at: TilePos): void {
    if (this.inHands === 0) {
      // Said out loud rather than swallowed, because a verb that produces
      // nothing is a verb a player stops pressing (decision 10b's lesson, one
      // verb over). The simulation would refuse it in silence either way.
      //
      // On the refusal line since wave 1.5 rather than in the top-right notice:
      // the notice is where a *development* warning goes and it does not go
      // away by itself, and this is a keystroke that did nothing
      // (`hud/panels.ts`'s `FeedbackLine`).
      this.hud.feedback.say(REFUSED_HANDS_EMPTY);

      return;
    }

    this.hands?.drop(at);
    this.watch(at);
    this.pilesAwaited = HANDS_SETTLE_STEPS;
  }

  /** Opens or closes the inventory, and refreshes it while it is open. */
  toggleInventory(): void {
    if (this.hud.inventory.open) {
      this.hud.inventory.hide();

      return;
    }

    this.showInventory();
  }

  /**
   * Picks up whatever is on a tile.
   *
   * The `F` half of the pair ({@link Witnessing.dropCarried} is the other): a
   * heap is a thing in the world the player walked up to, so it is addressed the
   * way every other thing in the world is addressed. What it takes is the first
   * item in handle order and the prompt already said which that is, because the
   * label on the subject is that item's.
   */
  private pickUp(at: TilePos): void {
    // The one refusal a pickup has that the client can see coming: the cap is
    // the simulation's and it is enforced whatever the client believes, so a
    // ninth thing is refused in silence — and a verb that quietly stops working
    // is the thing wave 1.5's ruling is about ({@link MAX_CARRIED}).
    if (this.inHands >= MAX_CARRIED) {
      this.hud.feedback.say(REFUSED_HANDS_FULL);

      return;
    }

    this.hands?.take(at);
    this.pilesAwaited = HANDS_SETTLE_STEPS;
  }

  /**
   * Takes somebody along.
   *
   * **The stand goes first, always** (W11, as wave 2F widened it): the reach is
   * measured from the tile the simulation is holding, so a beckon from a tile it
   * never heard of is the same defect a swing from one would be — and the pair
   * is queued here rather than at two call sites that could drift apart. The
   * dedupe is the world source's ({@link WorldSource.stand}).
   *
   * The two refusals are the two the simulation makes that this client has the
   * numbers for (`witness/company.ts`); everything else it would refuse is a
   * reason the offer does not stand, and an offer that does not stand is a verb
   * that was never drawn.
   */
  private takeAlong(figure: FigureId, at: TilePos | null): void {
    const player = this.source.playerFigure();

    if (player !== null && this.source.companions(player).length >= MAX_COMPANIONS) {
      this.hud.feedback.say(REFUSED_COMPANY_FULL);

      return;
    }

    const here = this.playerTile();

    if (at === null || !withinBeckonReach(here, at)) {
      this.hud.feedback.say(REFUSED_TOO_FAR_TO_TAKE);

      return;
    }

    this.hands?.stand(here);
    this.hands?.beckon(figure);
  }

  /**
   * Takes a workshop's order at a bench.
   *
   * **The demand rule, and nothing else** — `null` for the recipe, which is the
   * simulation's own *"whatever this bench would have taken anyway, most refined
   * first"* (`sim_core::RecipeChoice`). The command *can* name a row and this
   * client deliberately does not: a menu of recipes would be the client showing
   * a table it would have to keep a copy of, and what wave 1 is for is the loop
   * — walk to a forge, press a key, come back to a blade with your name frozen
   * into it.
   *
   * The seam is one argument wide and it is named here: the day a client offers
   * a choice, it cycles through the rows the *simulation* says this bench can run
   * and passes the id straight through (plan O5, as wave 1E-b narrowed it).
   */
  private workAt(at: TilePos, building: string): void {
    this.hands?.craft(at, null);
    this.watch(at);
    // Said, because a craft is the one verb of the four whose result appears
    // somewhere the player is not looking — on the bench, a minute later — and
    // silence would read as a key that did nothing. It names the building out of
    // the simulation's own word and claims nothing about what will come off it.
    //
    // **This is the one verb whose refusal the client cannot predict**, and the
    // line is worded for that: a bench with nothing it can make today refuses
    // the command in silence (`sim_core::craft`'s `choose`), and knowing which
    // rows a forge can run would mean keeping a copy of the recipe table and the
    // town's stores with it. So it says what the *player* did — set to work —
    // rather than what will come of it, and if nothing does, the honest generic
    // is what a player is owed one press later ({@link REFUSED_NOTHING}).
    this.hud.feedback.say(`you set to work at the ${building}`);
    this.craftAwaited = CRAFT_SETTLE_STEPS;
    this.craftSite = at;
  }

  /**
   * Looks, a few steps after a craft, at whether the order reached the board.
   *
   * The deferred half of {@link Witnessing.workAt}'s refusal
   * ({@link CRAFT_SETTLE_STEPS}). An order the player took is a row on the board
   * with their figure against it; what the *absence* of that row means is two
   * different things, and chunk Fx-2 stopped answering both of them with the
   * generic.
   *
   * **Three answers, in the order the board can tell them apart** (the wave-2
   * playtest: *"work at smithy … it literally says nothing happened"*):
   *
   * - **no row at that bench at all** — the workshop can make nothing today, or
   *   something else the client cannot see. That is genuinely unknowable from
   *   here (knowing which rows a forge can run would mean keeping a copy of the
   *   recipe table and the town's stores with it), so it keeps
   *   {@link REFUSED_NOTHING};
   * - **a row somebody else has** — the board has carried open-or-taken-by since
   *   chunk D3, so this one *is* knowable, and it is named: chunk B's rule that a
   *   refusal says why when the client can know why, one verb further on;
   * - **a row nobody has, or the player's own** — silence. The player's own is
   *   the press having landed; a row nobody has taken is a row this press may
   *   still be about to take, because the board is re-read one frame behind the
   *   tick that decided it, and a *"nothing happened"* said over a claim that
   *   lands a moment later would be the client calling the world wrong.
   */
  private checkCraftLanded(): void {
    const at = this.craftSite;
    this.craftSite = null;

    if (at === null) {
      return;
    }

    const post = this.board.find((order) => sameTile(order.site, at));

    if (post === undefined) {
      this.hud.feedback.say(REFUSED_NOTHING);

      return;
    }

    if (post.worker === null || post.worker === this.source.playerFigure()) {
      return;
    }

    // **Named, or the generic — never a handle.** The debug window prints
    // `#172` for a hand this town's roster has no name for
    // ({@link Witnessing.workerName}); a line under the crosshair may not, so an
    // unnamed worker gets {@link REFUSED_TAKEN} and the sentence is the same
    // shape either way.
    const who = this.nameOf(post.worker);

    this.hud.feedback.say(who === "" ? REFUSED_TAKEN : `${who} is already working here`);
  }

  /**
   * Reads a library's shelf: the books on it, or the invitation.
   *
   * **Decision 14's beat.** A shelf with something on it opens the first book
   * the same way a book standing in the street opens — the pages are the
   * simulation's written register over the records it cites, and reading it
   * files every one of them in the journal. A shelf with nothing on it says so,
   * in the one client line the phase-6 ruling authorised
   * ({@link EMPTY_SHELF_LINE}).
   *
   * The two answers look alike from outside and are not: a town with no library
   * has no shelf to walk up to at all, so this is never the answer to *"is there
   * a library here"* — it is the answer to *"has anybody here written anything
   * down"*.
   */
  private readShelfAt(at: TilePos): void {
    if (this.hud.book.open) {
      this.hud.book.hide();

      return;
    }

    const books = this.source.shelved(at);
    const first = books[0];

    if (first === undefined) {
      const here = this.town;

      // Nobody's mouth and no record behind it, which is what the two `null`s
      // say: a bare shelf is a thing the player *read*, in the written register
      // a grave and a market board are read in, and there is nothing in the log
      // to file about a book nobody wrote.
      this.hud.dialogue.say(
        SHELF_LABEL,
        EMPTY_SHELF_LINE(here?.name ?? "this town"),
        null,
        null,
      );

      return;
    }

    // The shelf's own first volume. Its pages and its citations are the
    // simulation's, exactly as a chronicle standing in the street is — which is
    // the parity half of decision 14: a historian's book and a player's are read
    // through one code path (`sim_core::books`).
    this.hud.book.show(
      `${this.itemWords(first.item)} · ${first.citations} recorded`,
      this.shelfPages(at, first.item),
    );
  }

  /**
   * What one shelved book says, and what it teaches.
   *
   * The pages come out of the simulation and every record it cites is filed,
   * which is the same bargain reading a chronicle makes
   * ({@link Witnessing.readBook}) — a book is a channel, and a channel that
   * showed a player a record without writing down where they got it would break
   * the traceability the whole surface is held to.
   *
   * **Wave 1F reads the citation count and not the citations**, and that is an
   * honest gap rather than a design: the two book models are still two
   * (`sim_core::books`' own header names the reconciliation), so a shelved book's
   * *records* have no export of their own yet. What the panel shows is the
   * simulation's own label and how much is in it; the pages arrive with the
   * unification.
   */
  private shelfPages(at: TilePos, item: number): readonly string[] {
    void at;

    return [this.itemWords(item)];
  }

  /**
   * The names of everybody walking with the player, in the simulation's own
   * order.
   *
   * A read of two calls and no state: who follows the player is the world's
   * answer ({@link WitnessSource.companions}) and what each of them is called is
   * the world's too. Bounded by {@link MAX_COMPANIONS}, so it may be asked
   * whenever the panel is drawn.
   */
  private companyNames(): readonly string[] {
    const player = this.source.playerFigure();

    if (player === null) {
      return [];
    }

    return this.source.companions(player).map((figure) => this.workerName(figure));
  }

  /** Fills the inventory panel from what the simulation says is in the hands. */
  private showInventory(): void {
    // **The hands *and* the body** since wave 2D-b, composed in `witness/arms.ts`
    // because the panel is the menu the wear verbs live in and its layout is a
    // fact about those verbs. Every label in it is the simulation's own words;
    // this side hands over three lists and gets back the rows.
    // **The hands as a structure** since chunk U-5 (`hud/panels/inventory.ts`):
    // the page draws a register and a carry list with its doors, and the two
    // things it needs beyond the labels are the sim's own answers — which
    // handle is in hand (`wielded`) and which piece `P` would put on next
    // (`wearable`). Composed in `witness/arms.ts` as before; this side hands
    // over lists and gets back the page.
    this.hud.inventory.hold(
      handsHeld({
        carried: this.held.map((entry) => ({
          item: entry.item,
          label: this.itemWords(entry.item),
        })),
        worn: this.wornItems.map((entry) => ({
          item: entry.item,
          label: this.itemWords(entry.item),
        })),
        wielded: this.source.wielded(),
        wearable: this.wearable,
        quiver: this.quiverState,
        // **And who is walking with you** (wave 2F, `witness/company.ts`): the
        // panel is the menu, so it is where a player standing still checks
        // their company — and it is empty in every session nobody has ever
        // followed anybody in, which is nearly all of them.
        company: this.companyNames(),
        capacity: MAX_CARRIED,
      }),
    );
  }

  /** Opens or closes the journal, and refreshes it while it is open. */
  toggleJournal(): void {
    if (this.hud.journal.open) {
      this.hud.journal.hide();

      return;
    }

    this.showJournal();
  }

  /**
   * Opens or closes the map, and refreshes it while it is open.
   *
   * The journal's toggle exactly, over the journal's sibling store — and it is
   * bound in every session rather than in development only, because a map of
   * where you have been is a thing a player is meant to have (Phase 5 decision
   * 10, unlike the trace view's debug register).
   */
  toggleMap(): void {
    if (this.hud.map.open) {
      this.hud.map.hide();

      return;
    }

    this.showMap();
  }

  /**
   * The map as it stands, for the self-checks and the debug handle.
   *
   * The *same* projection the panel draws (`map/survey.ts`), deliberately: the
   * exit criterion is that nothing on the map is authored or revealed for free,
   * and a check that assembled its own version of the answer would be checking
   * its own arithmetic. What is asserted is what is drawn.
   */
  survey(): SurveyedMap {
    return surveyMap(this.ground, {
      settlements: [...this.settlements.values()],
      // The same routes the world's own roads are laid along, which is what
      // makes "the roads on the paper are the roads in the world, clipped to
      // what you have seen" a statement about one list rather than two.
      roads: this.roadLines,
      met: this.people.everyone,
    });
  }

  /** The walked-ground store itself, for the debug handle and the self-checks. */
  get walked(): WalkedGround {
    return this.ground;
  }

  /*
   * The Phase 5 reads, for the debug handle and the self-checks.
   *
   * Five getters rather than one bag, because they answer five questions and a
   * driver usually wants one of them: who the player is in the log, what the
   * simulation says about that figure, what they are carrying that this town
   * lacks, what is standing on its shelves, and whether it is in ruin. All five
   * are reads of state this class already keeps — nothing is computed for them,
   * which is what keeps a check asserting on what the client actually decided
   * rather than on a second opinion.
   */

  /** The figure the player's deeds cite, or `null` before a claim. */
  get figure(): FigureId | null {
    return this.player;
  }

  /** The simulation's own introduction of that figure — name, byname, town. Empty before a claim. */
  get introduced(): string {
    return this.playerIntroduced;
  }

  /** What the player holds that the visited settlement has not heard, oldest first. */
  get carrying(): readonly JournalEntry[] {
    return this.share;
  }

  /** The books standing in the visited settlement, as the simulation reports them. */
  get shelf(): readonly ChronicleView[] {
    return this.books;
  }

  /*
   * The Phase 7 reads, for the debug handle and the self-checks.
   *
   * Five getters over state this class already keeps, on the Phase 5 block's
   * terms: nothing is computed for them, so what a check asserts on is what the
   * client actually drew.
   */

  /** The promoted town's board, as of the last step. */
  get workBoard(): readonly JobPost[] {
    return this.board;
  }

  /** The bodies the local tier has standing here, as of the last step. */
  get present(): readonly PresentFigure[] {
    return this.bodies;
  }

  /** What the player is carrying, as of the last step. */
  get carriedItems(): readonly CarriedItem[] {
    return this.held;
  }

  /** The buildings this town has standing, as of the last place-or-day change. */
  get buildings(): readonly TownLot[] {
    return this.townLots.map((standing) => standing.lot);
  }

  /** How many tiles the client is watching for things lying about. */
  get watchedTiles(): number {
    return this.piles.size;
  }

  /**
   * Where the player stands with the watch, as a headless driver reads it back
   * (chunk Fx-2).
   *
   * **One getter over the whole arrest chain**, and it exists because the chain
   * is the one thing in this build a *scripted* proof could not check: the
   * wave-2 scripted watch tests passed while live play could not be arrested at
   * all, because the script sent the stands itself and nothing ever asked what
   * this client would have done (`docs/decisions/watch.md`, chunk Fx-1). So the
   * reading carries the whole chain in the order it happens — wanted, a guard
   * near, the tile going out, the tile held, a hand on the shoulder, the beat on
   * the screen — and a driver that finds it broken can say *where*.
   *
   * Everything in it is already decided elsewhere in this class; nothing is
   * computed for the reading, which is the same rule the Phase 5 and Phase 7
   * blocks above are written under. The two boundary calls it makes are the ones
   * the stand trigger makes every step anyway.
   */
  get watching(): WitnessWatching {
    const player = this.player;

    if (player === null) {
      return { wanted: "", guardAway: -1, standing: false, stood: null, arresting: -1, beat: "" };
    }

    const crime = this.source.wantedFor(player);
    const guard = this.watchAtHand();

    return {
      wanted: crime === null ? "" : this.crimeWord(crime),
      guardAway: guard?.away ?? -1,
      standing: this.needsMyTile(player),
      stood: this.source.stood(),
      arresting: this.arrestingMe() ?? -1,
      beat: this.swingPrompt() ?? "",
    };
  }

  /**
   * What the debug window reads off this layer (`dev/debuglog.ts`, wave 1.6
   * item 7).
   *
   * One getter rather than eight, on {@link DebugTown}'s terms: what the window
   * wants is a *reading*, taken at one instant, of things that live in six
   * different fields here — and eight members on this class would be eight more
   * things a reader of it has to place. Everything in it is already held or is a
   * count of something already held; the two boundary calls
   * ({@link WitnessSource.roster} and {@link WitnessSource.presentFigures}) are
   * cached reads and this is asked once a second.
   */
  get watched(): WitnessWatched {
    const centre = this.town;
    const taken = this.board.filter((post) => post.worker !== null).length;
    // Asked here rather than kept, because the useful reading is a *fresh* one:
    // `here` below is what this class latched on its last step, and the gap
    // between the two is the only thing left of the disagreement window
    // ({@link WitnessWatched.promoted}). One scalar across the boundary, once a
    // second.
    const promoted = this.source.promoted();
    // One boundary call per living resident, which is a dozen, at the cadence a
    // window refreshes at — once a second and only while one is open
    // (`dev/debugpanel.ts`). A flattened *"who here is what age"* read would be a
    // second layout to keep in step with the roster's for a question nothing on
    // a per-frame path asks ({@link WitnessSource.lifeStage}) — and a *drawn*
    // body's stage does not come through here at all: it rides its presence row
    // ({@link PresentFigure.stage}), which is the per-frame answer.
    const roster = this.source.roster();
    const counts = new Map<number, number>();

    for (const entry of roster) {
      const stage = this.source.lifeStage(entry.figure);

      counts.set(stage, (counts.get(stage) ?? 0) + 1);
    }

    // Oldest first, which is how the line reads: *seven adults, two children,
    // one baby*. The ids are the stage table's own order, so sorting on them is
    // sorting by age without this file knowing what any of them mean.
    const stages: WatchedStage[] = [...counts.entries()]
      .sort(([left], [right]) => right - left)
      .map(([stage, count]) => ({ stage, word: this.stageWord(stage), count }));

    return {
      here: this.settlement,
      hereName: centre?.name ?? "",
      hereDistance:
        centre === null ? null : Math.hypot(centre.x - this.from.x, centre.y - this.from.y),
      promoted,
      promotedName: promoted === null ? "" : (this.settlements.get(promoted)?.name ?? ""),
      roster: roster.length,
      stages,
      presence: this.bodies.length,
      props: this.sceneProps.length,
      bodies: this.sceneProps.filter((prop) => prop.kind === "npc").length,
      jobs: this.board.length,
      jobsTaken: taken,
      lots: this.townLots.length,
      piles: this.piles.size,
      aimed: this.target?.id ?? null,
      phase: this.phase(),
      snaps: this.snapCount,
      tierSwaps: [...this.placeReasons],
    };
  }

  /**
   * Every body standing here, joined with what the client already knows about
   * it (chunk D2's Bodies list, `dev/debuglog.ts`).
   *
   * **A getter rather than a field on {@link Witnessing.watched}, and that is
   * the cost rule.** The Info reading is assembled once a second whether or not
   * anybody is looking at it; this is a row per body with a board scan and a lot
   * scan behind each one, and it is only worth paying for while the tab that
   * draws it is on screen. So the panel holds a thunk and calls it when it
   * draws that tab and never otherwise (`dev/debugpanel.ts`) — which is once a
   * second at the very most, and zero in an ordinary session.
   *
   * In the sim's own order, unsorted: the useful order is by distance from the
   * player, and the sort belongs where it can be checked without a world.
   */
  get watchedBodies(): readonly WatchedBody[] {
    return this.bodies.map((body) => ({
      figure: body.figure,
      name: this.presentNames.get(body.figure) ?? "",
      stage: body.stage,
      stageWord: this.stageWord(body.stage),
      at: body.at,
      conditionWord: this.conditionWord(body.figure),
      doing: this.doingOf(body),
    }));
  }

  /**
   * Every place the client knows about in this town (chunk D2's Places list).
   *
   * {@link Witnessing.watchedBodies}' cost rule word for word, and the same
   * unsorted answer for the same reason. What goes in it and what does not is
   * {@link WatchedPlace}'s and {@link PLACE_SUBJECTS}'.
   */
  get watchedPlaces(): readonly WatchedPlace[] {
    // The plan first, so the buildings lead every tie the sort leaves — a town
    // is its buildings and the rest is what has happened to it.
    const places: WatchedPlace[] = this.townLots.map((standing) => ({
      what: standing.name,
      note: standing.lot.trade === null ? "" : workWord(standing.lot.trade),
      // The bench rather than the doorstep: it is where the work verb is
      // offered and where what a workshop made is left, so it is the tile
      // somebody hunting a workshop actually wants to walk to. It is also the
      // one of the two whose ground the client has already probed
      // ({@link Witnessing.readLots}), so the row costs no terrain query.
      at: standing.at,
    }));

    for (const thing of this.interactables) {
      if (!PLACE_SUBJECTS.has(thing.subject.kind)) {
        continue;
      }

      places.push({
        what: thing.label,
        note: "",
        // An interactable stands at a *column* — a prop is placed at the middle
        // of a tile — so the floor is what turns it back into the tile a player
        // reads off the Info tab and walks to.
        at: { x: Math.floor(thing.x), y: Math.floor(thing.y), z: Math.floor(thing.z) },
      });
    }

    return places;
  }

  /**
   * Every work order this town has on its board (chunk D3's third list).
   *
   * {@link Witnessing.watchedBodies}' cost rule word for word — a getter the
   * panel holds as a thunk and calls only on the tab that draws it — though this
   * one is the cheapest of the three: the board is already held and a row is a
   * name lookup. What is in it and why it is not a place is {@link WatchedOrder}.
   *
   * In the simulation's own order, unsorted, for the reason the other two are:
   * the useful order is by distance and the sort belongs where it can be checked
   * without a world.
   */
  get watchedOrders(): readonly WatchedOrder[] {
    return this.board.map((post) => ({
      kind: post.kind,
      work: workWord(post.kind),
      at: post.site,
      note: post.worker === null ? "open" : `taken by ${this.workerName(post.worker)}`,
    }));
  }

  /**
   * What to call the figure who has taken an order.
   *
   * The name the street knows them by, and the handle where there is none: an
   * order taken by somebody this town's roster has not named is still an order
   * that is taken, and `#187` is more use on the row than a blank
   * ({@link Witnessing.nameOf}).
   */
  private workerName(worker: FigureId): string {
    const name = this.nameOf(worker);

    return name === "" ? `#${worker}` : name;
  }

  /**
   * What one body is doing, out of what the client has already read.
   *
   * Three answers and no fourth: an order on the board with their name on it, a
   * bench they are standing at, or *standing*. **The simulation publishes no
   * "what is this figure up to" read and this is not a request for one** — the
   * recordless layer's whole bargain is that a town's daily work is seen rather
   * than logged (`witness/scenes.ts`), so a list that guessed at a mood or an
   * errand would be the client inventing what it cannot see.
   *
   * Two scans per body, of a board with a handful of rows and a plan with a
   * dozen, at the cadence a tab is drawn at. An index would be a second thing to
   * keep in step with two lists that are re-read on two different cadences, for
   * a saving nothing can measure.
   *
   * **A fourth clause, and it is not a fourth answer.** It rides on whichever of
   * the three is true: a body the simulation also names as the talker of a trade
   * pulse out of this town says so — `standing · named talker of the caravan to
   * Ashmarch`. The client used to answer that case by not drawing the body at all
   * ({@link Witnessing.collectCrowd}); it draws them and puts the contradiction on
   * the row instead, because a figure the log has standing here and away with a
   * pulse at once is the simulation's to fix and a reader who can see it is what
   * makes it get fixed. A third scan, of a list with a handful of rows on it.
   */
  private doingOf(body: PresentFigure): string {
    return (
      `${this.workOf(body)}${this.fleeNote(body.figure)}` +
      `${this.watchNote(body.figure)}${this.followNote(body.figure)}` +
      `${this.talkerNote(body.figure)}`
    );
  }

  /**
   * The clause that says this body is following somebody, or `""`.
   *
   * **The sixth answer, and it rides the other five** ({@link Witnessing.fleeNote}'s
   * shape, wave 2F): a companion reads `standing · following you`, and somebody
   * else's companion reads `following Bera Highfell` — one read
   * ({@link WitnessSource.leaderOf}) with the client comparing a handle against
   * its own figure, which is the whole of what it is allowed to decide here.
   */
  private followNote(figure: FigureId): string {
    const leader = this.source.leaderOf(figure);

    if (leader === null) {
      return "";
    }

    return leader === this.source.playerFigure()
      ? " · following you"
      : ` · following ${this.workerName(leader)}`;
  }

  /**
   * The clause that says this body is running from somebody, or `""`.
   *
   * **The fifth answer, and it rides the other four rather than replacing
   * them** ({@link Witnessing.talkerNote}'s shape): a figure the simulation says
   * is fleeing reads `standing · running from Bera Highfell`, because *what they
   * are doing about it* is a different question from *what work has their name
   * on it*.
   *
   * The predicate is the simulation's own ({@link WitnessSource.fleeingFrom}),
   * derived from the wound they are carrying and their own bravery — so the row
   * and the body walking away are one answer. A hand this town's roster has no
   * name for is printed as a handle, which is {@link Witnessing.workerName}'s
   * own fall-back.
   */
  private fleeNote(figure: FigureId): string {
    const from = this.source.fleeingFrom(figure);

    if (from !== null) {
      return ` · running from ${this.workerName(from)}`;
    }

    // **And the other half of the same derivation** (wave 2D-a): the simulation
    // splits *a fresh wound from somebody still standing here* by one dial, and
    // the two answers are *runs* and *stands their ground*
    // ({@link WitnessSource.answering}). A row that showed only the first would
    // print nothing at all for the body that is about to swing, which is the
    // half a developer watching a fight actually wants.
    //
    // It is also the read the client watches to know when to start telling the
    // simulation where the player is standing ({@link Witnessing.underThreat}),
    // so a row that says *squaring up to you* is a row saying the stand command
    // is going out.
    const at = this.source.answering(figure);

    return at === null ? "" : ` · squaring up to ${this.workerName(at)}`;
  }

  /**
   * The clause that says the watch wants this body, or has hold of it, or `""`.
   *
   * **The sixth answer, riding the other five** ({@link Witnessing.fleeNote}'s
   * shape, and for its reason): what a town holds against somebody and whether
   * anybody has caught up with them are two different questions from *what work
   * has their name on it*, and both of them are the simulation's — the blotter
   * and the engagement floor (`sim_core`'s `watch` and `local`).
   *
   * Two reads and one line: `standing · wanted for theft · held by Bera
   * Highfell`. A body nobody has anything against reads as it always did, which
   * is nearly every body in nearly every town.
   */
  private watchNote(figure: FigureId): string {
    const crime = this.source.wantedFor(figure);
    const held = this.source.engagedWith(figure);
    const wanted = crime === null ? "" : ` · wanted for ${this.source.crimeWord(crime)}`;
    const taken = held === null ? "" : ` · held by ${this.workerName(held)}`;

    return `${wanted}${taken}`;
  }

  /**
   * Puts the accusation in the guard's mouth when the watch has come for the
   * player.
   *
   * **The one thing the client owes the watch** (Phase 7 plan, W7): the player
   * has no body in the simulation, so a guard dispatched after them walks to the
   * *scene* of the crime and stands there — the simulation has done everything
   * it can, and without this the visible result of a witnessed theft is a man
   * standing in a street for no stated reason.
   *
   * Nothing here decides anything. **Whether the player is wanted and what for
   * are both read** ({@link WitnessSource.wantedFor}), and what the client adds
   * is a voice and a distance: the nearest watchman within earshot says it, once
   * per {@link ACCUSATION_SECONDS}, so a guard who has taken up a post beside a
   * thief does not repeat himself every frame.
   *
   * **A watchman is the simulation's own answer** ({@link Witnessing.watchAtHand}),
   * so a baker standing nearby never accuses anybody — and since chunk Fx-2 the
   * *same* read decides whether the player's tile starts crossing, because a
   * guard close enough to say the line is a guard the simulation is about to
   * need somewhere to send.
   *
   * The enforcement itself — the surrender, the resistance — is the player's own
   * combat chunk (2D), and it arms off the engagement this line's own condition
   * now makes reachable.
   */
  private answerForIt(stepSeconds: number): void {
    this.sinceAccusation += stepSeconds;

    if (this.player === null || this.sinceAccusation < ACCUSATION_SECONDS) {
      return;
    }

    const crime = this.source.wantedFor(this.player);

    if (crime === null) {
      return;
    }

    // Where the player is standing, as the render step last wrote it down: the
    // simulation has never known ({@link Witnessing.render}), which is the whole
    // reason a guard walks to the scene rather than to them.
    const guard = this.watchAtHand();

    if (guard === null) {
      return;
    }

    this.sinceAccusation = 0;
    this.hud.overheard.say(
      this.workerName(guard.figure),
      `The watch wants a word with you about that ${this.crimeWord(crime)}.`,
      null,
      null,
      null,
    );
  }

  /**
   * Puts the sentence in the guard's mouth when the row closes with a word.
   *
   * **The arrest beat's closure** (chunk Fx-7), and the owner's session is the
   * whole argument for it: *"a guard comes over and I overheard: the watch wants
   * a word with you about that violence … and then nothing happens? The guard
   * just left."* Nothing was broken. A landed blow is `Violence`, Violence
   * carries `Sentence::Reprimand`, and a reprimand is *a word, and let go* —
   * the watchman engages for one pulse, the row settles, he goes back to his
   * beat. What the owner heard was the **wanting** and what was never said was
   * the **word**.
   *
   * So this watches the two frames the sentence is the difference between: while
   * the town wants the player and somebody has hold of them, the row is
   * remembered ({@link RowInHand}); the step it is gone with nobody holding them
   * and nothing new in the log, the hand that held them says his piece
   * (`witness/law.ts`'s {@link reprimandClosed} and {@link reprimandLine}).
   *
   * **Nothing here decides anything**, which is the same division of labour the
   * accusation above it works under: the row, the hand and the record are three
   * reads, the sentence is the simulation's own escalation quoted, and what the
   * client adds is a voice and the noticing.
   *
   * It costs an ordinary session one read a step: `wantedFor`, a scalar, which
   * gates everything behind it. The accusation makes the same read on its own
   * cadence and this one cannot borrow it — a transition one frame wide is not
   * something to sample every twelve seconds.
   */
  private watchTheRow(): void {
    const player = this.source.playerFigure();

    if (player === null) {
      this.heldFor = null;

      return;
    }

    const wanted = this.source.wantedFor(player);

    if (wanted !== null) {
      // The row is open, and while a hand is on it there is something to
      // remember. The record is taken with it, because what tells a word from a
      // taking is one of those appearing afterwards.
      const hand = this.arrestingMe();

      if (hand !== null) {
        this.heldFor = {
          crime: this.crimeWord(wanted),
          by: hand,
          taken: this.source.arrestOf(player),
        };
      }

      return;
    }

    const held = this.heldFor;

    // Short-circuited on purpose: a session nobody is being arrested in never
    // asks the two reads behind the rule.
    if (held === null || !reprimandClosed(held, this.arrestingMe(), this.source.arrestOf(player))) {
      return;
    }

    this.heldFor = null;
    this.hud.overheard.say(this.workerName(held.by), reprimandLine(held.crime), null, null);
  }

  /**
   * How hurt one body is, in the simulation's own word.
   *
   * A read per body at the cadence a tab is drawn at, which is
   * {@link WitnessSource.condition}'s own stated cadence — a debug window, once
   * a second, over a roster of a dozen.
   */
  private conditionWord(figure: FigureId): string {
    return this.source.conditionWord(this.source.condition(figure));
  }

  /** The three answers: an order with their name on it, a bench, or standing. */
  private workOf(body: PresentFigure): string {
    const post = this.board.find((order) => order.worker === body.figure);

    if (post !== undefined) {
      return workWord(post.kind);
    }

    // The column rather than the tile: a bench's z is the ground the client
    // probed for it and a body's is the simulation's, and the two are the same
    // floor said twice ({@link Witnessing.readLots}).
    const bench = this.townLots.find(
      (standing) => standing.at.x === body.at.x && standing.at.y === body.at.y,
    );

    return bench === undefined ? "standing" : `at the ${bench.name}`;
  }

  /**
   * The clause that says the simulation has this body out on a road, or `""`.
   *
   * A pulse *out of* this settlement whose talker the simulation named, read for
   * a note instead of for a refusal to draw. **The one thing in the client that
   * still reads a pulse**: the trade is the abstract tier's and nothing is drawn
   * on the road for it, so a body in the street is where it surfaces.
   *
   * **Plural on purpose.** `economy::talker` picks per road with no exclusivity,
   * so one figure is the talker of two caravans in the curated home town this
   * season, and a row that named only the first would tidy away the very thing
   * this note exists to show. A town whose map has forgotten the destination is
   * skipped rather than named `undefined`.
   */
  private talkerNote(figure: FigureId): string {
    const towns = this.pulses
      .filter((pulse) => pulse.from === this.settlement && pulse.talker === figure)
      .map((pulse) => this.settlements.get(pulse.to)?.name ?? "")
      .filter((name) => name !== "");

    if (towns.length === 0) {
      return "";
    }

    const caravan = towns.length === 1 ? "caravan" : "caravans";

    return ` · named talker of the ${caravan} to ${listed(towns)}`;
  }

  /**
   * Where one present body is *drawn* right now, or `null` for somebody this
   * client is not holding up.
   *
   * The tracked marker's per-frame read (chunk D3, `dev/track.ts`), and it adds
   * no boundary crossing: it is the same {@link Witnessing.bodies} the crowd is
   * drawn from, put through the same {@link WalkSmoothing} — so the marker sits
   * over the body the player can see rather than over the tile the simulation
   * last reported, which is a whole walking step away for half of every step.
   *
   * **Height included, since chunk Sf.** `placeOf` stands a body on the ground
   * under its drawn point, so the ring over a figure climbing a flight of stairs
   * climbs with them instead of hanging at the bottom of the flight. This
   * function did not change: it reads the one call every drawn body is read
   * through, which is why it inherited the fix.
   *
   * `null` is the honest answer for a figure who has walked out of presence or
   * whose town was demoted: the client does not know where they are, and a
   * marker left pointing at the last place it saw them would be the client
   * inventing a position — the thing this layer never does.
   */
  drawnAt(figure: FigureId): { readonly x: number; readonly y: number; readonly z: number } | null {
    const body = this.bodies.find((present) => present.figure === figure);

    return body === undefined ? null : this.walk.placeOf(body);
  }

  /** What one item is called, asked of the simulation. For the handle's readout. */
  labelOf(item: number): string {
    return this.itemWords(item);
  }

  /**
   * Every named thing in the world, as the row a panel or a driver reads:
   * the handle, and what to call it.
   *
   * **The registry, in the simulation's own order** ({@link
   * WitnessSource.relics}) — which is the arena's, which is the order the world
   * hash folds in. Short by construction: a thing gets a name when something
   * record-worthy touches it, and this world's triggers are a killing and a
   * masterwork.
   *
   * The words go through {@link Witnessing}'s own composition like every other
   * item on a row, so a named blade reads the same here as it does in a prompt.
   */
  get namedThings(): readonly { readonly item: number; readonly words: string }[] {
    return this.source
      .relics()
      .map((entry) => ({ item: entry.item, words: this.itemWords(entry.item) }));
  }

  /**
   * What the world remembers about one named thing, one line each — empty for
   * anything that is not one.
   *
   * A pass-through, on {@link Witnessing.labelOf}'s terms: the projection is
   * the simulation's and so are its sentences ({@link WitnessSource.relicLines},
   * `sim_core::relics`). What the client owns is *when* to show them.
   */
  relicOf(item: number): readonly string[] {
    return this.source.relicLines(item);
  }

  /**
   * What to call one item on a row, a prompt or a panel line.
   *
   * **The one place the two item reads are put together** (chunk 2C): the
   * simulation says what a thing *is* ({@link WitnessSource.itemLabel} — the
   * rung, the material and the noun) and what it is *called*
   * ({@link WitnessSource.itemName} — the coined name a promotion gave it), and
   * this writes the comma: `Karfang, an iron blade`.
   *
   * It is one method rather than a comma at five call sites for the reason
   * every other composition here is one: a pile's prompt, an inventory row, a
   * shelf's first volume and the debug handle's readout are four surfaces
   * naming the same object, and four copies of the rule are four chances for
   * one of them to drop the name.
   *
   * **The article is the client's**, which is the one word of this sentence the
   * simulation does not own: `sim_core::Snapshot::item_label` is documented as
   * singular, lowercase and article-free precisely so that the sentence around
   * it can own both — and the sentence is here.
   */
  private itemWords(item: number): string {
    const label = this.source.itemLabel(item);
    const name = this.source.itemName(item);

    if (name === "" || label === "") {
      return name === "" ? label : name;
    }

    return `${name}, ${article(label)} ${label}`;
  }

  /**
   * The world's own history as the book keeps it, as of the last day boundary.
   *
   * The one door onto the saga for everything in this file and for the debug
   * handle: the shelf copy, the trace section and a headless driver all read
   * *this*, so what a check asserts on is what a page draws. Empty in a world
   * with no past, and in a build whose boundary has no saga export yet
   * (`witness/saga.ts` — the presence check is there and only there).
   */
  get eras(): SagaBook {
    this.book ??= readSaga(this.source);

    return this.book;
  }

  /** The ruin the visited settlement is in, or `null` if it is not in one. */
  get ruinHere(): RuinView | null {
    return this.settlement === null ? null : (this.ruins.get(this.settlement) ?? null);
  }

  /**
   * The visited settlement's own row, or `null` when the player is nowhere.
   *
   * The one place `here()`'s nullability is turned into something the rest of
   * this file can ask a question of. Nearly every consumer wants the *name* or
   * the *centre* rather than the handle, and every one of them already had an
   * "undefined" branch for a settlement the map does not know — so a nowhere
   * takes the branch that was already written for it.
   */
  private get town(): SettlementView | null {
    return this.settlement === null ? null : (this.settlements.get(this.settlement) ?? null);
  }

  /**
   * Opens or closes the trace view: every record the player has learned, with
   * the log's own sentence for it and every channel that carried it.
   *
   * The exit criterion's "traceable in debug view to the same event-log entries"
   * (roadmap Phase 3), and the whole of the client's part in it — the journal
   * already holds the references and their provenance, and
   * {@link WitnessSource.describe} is the log's own narration of a record. What
   * this adds is the grouping: three entries with one `event` is the claim, and
   * a flat list makes it something a reader has to notice.
   *
   * Bound in development only (`main.ts`), because the sentences it shows are
   * the chronicler's rather than anybody's.
   */
  toggleTrace(): void {
    if (this.hud.trace.open) {
      this.hud.trace.hide();

      return;
    }

    this.showTrace();
  }

  /**
   * Somewhere to stand to look at each thing in the world, for a driver taking
   * screenshots. Development only — see {@link Vantage}.
   *
   * Built from the props the last frame drew, because a vantage is a fact about
   * *geometry* — how big the thing is, which way round it is, where it is
   * standing right now — and the prop list is where the client keeps that. A
   * driver that asks before the first frame gets the list collected on the spot;
   * the next `render` clears and rebuilds both scratch arrays anyway, so there
   * is nothing to leave behind.
   */
  vantages(): readonly Vantage[] {
    if (this.sceneProps.length === 0) {
      this.sceneProps.length = 0;
      this.interactables.length = 0;
      // The local tier first, for the reason {@link Witnessing.render} reads it
      // first: since item 6 the bodies in a street are the simulation's list and
      // nothing else, and a driver that asked for vantages before the first
      // frame would otherwise be handed a town with nobody in it.
      this.readLocal();
      this.collectCrowd();
      this.collectFeatures();
      this.collectChronicles();
      this.collectRuin();
      this.collectRefugees();
      this.collectSignposts();
      this.collectTownBoards();
      this.collectSmoke();
    }

    const labels = new Map(this.interactables.map((entry) => [entry.id, entry.label]));

    // What the verb would call it, then what floats over it, then its id: a
    // signpost and a plume of smoke are drawn and never addressed, so the first
    // of those does not exist for them and the driver would otherwise be handed
    // a list of numbers to photograph.
    return this.sceneProps.map((prop) =>
      this.vantageFor(prop, labels.get(prop.id) ?? prop.label ?? prop.id),
    );
  }

  /** The vantage for one prop id, or `null` when nothing has that id. */
  vantage(id: string): Vantage | null {
    return this.vantages().find((entry) => entry.id === id) ?? null;
  }

  /**
   * The director's window, read back: every beat the projection currently
   * offers this settlement, each carrying the `earliest` morning it could land.
   *
   * For the dev skip (`main.ts`), which is why it interprets nothing: the skip
   * stops on a date the simulation named, and which mornings matter is the
   * simulation's list. Re-read every day boundary like everything else in
   * {@link Witnessing.readWorld}.
   */
  beatsAhead(): readonly SpotlightBeat[] {
    return this.beats;
  }

  /** Drops every prop and releases the geometry behind them. */
  dispose(): void {
    this.props.dispose();
    this.crowd.clear();
  }

  /**
   * Works out where to stand to look at one prop.
   *
   * The whole of the arithmetic is "step out of its front face, walk round it a
   * little, and turn back". A prop's geometry is built facing local +y and its
   * *front* is one side or the other of that depending on whether it walks or is
   * read ({@link propVantage}), so the front direction in world space is local
   * `(0, side)` turned by the prop's facing, then turned again by the kind's
   * bearing; the yaw that looks back down it is the one whose forward vector is
   * that direction reversed.
   *
   * **And then it is pulled in until a body fits.** The arithmetic above puts
   * the camera a fixed distance out of the prop's front face and asks nothing
   * about what is there, which a town made of *tiles* cannot afford: a shelf
   * against the back wall of a workshop would be looked at from two metres
   * behind that wall, `view` would teleport the player's collision box inside
   * the masonry, and the next physics step would eject them through the roof.
   *
   * See {@link vantageDistance} for the search. It is the only place in this
   * class that asks a physics question, and it asks it through the player's own
   * box and the player's own overlap test rather than a copy of either.
   */
  private vantageFor(prop: SceneProp, label: string): Vantage {
    // The body's own size, so a child is aimed at rather than over
    // ({@link propVantage}). The number is the simulation's since wave 2A.
    const { height, side, bearing } = propVantage(prop.kind, prop.scale);
    // The front of the prop, in world space: local (0, side) turned by the
    // prop's facing. Yaw 0 faces +y, so turning (x, y) by an angle is the
    // ordinary rotation and not the controller's `(-sin, cos)` basis.
    const frontX = -side * Math.sin(prop.facing);
    const frontY = side * Math.cos(prop.facing);
    const stepX = frontX * Math.cos(bearing) - frontY * Math.sin(bearing);
    const stepY = frontX * Math.sin(bearing) + frontY * Math.cos(bearing);
    const distance = this.vantageDistance(prop, stepX, stepY);
    const x = prop.x + stepX * distance;
    const y = prop.y + stepY * distance;
    // The ground under the *vantage*, not under the prop: a stone on a terrace
    // is looked at from wherever the hillside in front of it actually is.
    const z = surfaceZ(this.terrain, x, y);

    return {
      id: prop.id,
      label,
      x,
      y,
      z,
      // Looking back at the prop: the player's forward is `(-sin yaw, cos yaw)`
      // and it has to equal the step reversed (`player/controller.ts`).
      yaw: Math.atan2(stepX, -stepY),
      // Aimed at the middle of the thing rather than level, so a knee-high grave
      // is in the frame instead of under it.
      pitch: Math.atan2(prop.z + height - (z + EYE_HEIGHT), distance),
      distance,
    };
  }

  /**
   * How far out along a vantage's own step direction a player's body actually
   * fits: the kind's distance, or the first shorter one that is clear.
   *
   * **Pulled in and never pushed out, and never turned.** The bearing in
   * {@link propVantage} is a composition decision — which shoulder a body is
   * photographed over, how far off the axis a signpost's arm reads — and a
   * search that swung the camera round the prop looking for daylight would be
   * this file overruling that table. Distance is the one degree of freedom that
   * costs nothing but framing: closer is a tighter shot of the same subject from
   * the same side. So the ray is fixed and only its length moves.
   *
   * Three properties make this safe to run over every prop every time the
   * driver asks:
   *
   * - **It cannot make a vantage worse.** The full distance is tried first and
   *   returned unchanged when it is clear, which is nearly always — a town's
   *   props stand in streets and squares. Nothing moves for open ground.
   * - **It gives up rather than inventing.** A prop walled in on every side
   *   (a shelf in a one-tile alcove) falls back to the full distance, which is
   *   exactly today's behaviour and today's bad picture. This fixes the case it
   *   can fix and does not pretend about the rest.
   * - **It is bounded.** {@link VANTAGE_PULL_ATTEMPTS} steps of
   *   {@link VANTAGE_PULL_STEP}, so the cost is a fixed handful of terrain
   *   queries per prop even for the smoke's thirty-tile stand-off — and a wall
   *   is one tile thick, so a couple of body-widths of pull is the whole of what
   *   escaping one takes.
   *
   * The occupancy test is the player's, literally: the same box
   * ({@link BODY_HALF_WIDTH}, {@link BODY_HEIGHT}) fed to the same
   * {@link overlapsSolid} the walk uses, standing on the same {@link surfaceZ}
   * the vantage will report. A vantage that passes here is a vantage `view` can
   * teleport into without the next physics step having an opinion.
   */
  private vantageDistance(prop: SceneProp, stepX: number, stepY: number): number {
    const { distance } = propVantage(prop.kind);

    for (let attempt = 0; attempt <= VANTAGE_PULL_ATTEMPTS; attempt += 1) {
      const tried = distance - attempt * VANTAGE_PULL_STEP;

      // Inside this and the camera is in the prop's own geometry, which is a
      // different bad picture rather than a fixed one. Stop and let the caller
      // have the full distance back.
      if (tried < VANTAGE_MIN_DISTANCE) {
        break;
      }

      const x = prop.x + stepX * tried;
      const y = prop.y + stepY * tried;
      const z = surfaceZ(this.terrain, x, y);

      if (
        !overlapsSolid(
          {
            minX: x - BODY_HALF_WIDTH,
            maxX: x + BODY_HALF_WIDTH,
            minY: y - BODY_HALF_WIDTH,
            maxY: y + BODY_HALF_WIDTH,
            minZ: z,
            maxZ: z + BODY_HEIGHT,
          },
          this.terrain,
        )
      ) {
        return tried;
      }
    }

    return distance;
  }

  /**
   * Reads where the world is — the settlements — and puts the visited one's
   * named figures on the ground around its centre.
   *
   * Run when the player reaches a settlement and at no other time, which is what
   * keeps a body walking rather than snapping back to its anchor: see the
   * header's note on what moves and when.
   *
   * **The reason is taken rather than inferred** ({@link PlaceReason}, wave 1.6
   * item 7). This is the call that swaps which system draws a town's people, and
   * the wave-1.6 investigation named that swap as the cause of a figure
   * teleporting in front of a standing player — so a log that could not say
   * *which* of the six callers fired is a log that cannot close the question.
   * The tag costs a `Map` bump in a session with nobody watching.
   */
  private readPlace(reason: PlaceReason): void {
    this.placeReasons.set(reason, (this.placeReasons.get(reason) ?? 0) + 1);
    this.placedSinceDiff = reason;

    if (this.watcher === null) {
      this.anchorCrowd();

      return;
    }

    // What this client is drawing now, and what it will be drawing after. The
    // *before* is the last list that reached the screen and the *after* is a
    // fresh read, which is what makes the diff say something: the two straddle
    // the moment the promoted town changed, so the figures that appear and the
    // figures that cease to exist are the two towns' rosters.
    const before = new Set(this.bodies.map((body) => body.figure));
    this.anchorCrowd();

    const after = new Set(this.source.presentFigures().map((body) => body.figure));
    const appeared = [...after].filter((figure) => !before.has(figure));
    const vanished = [...before].filter((figure) => !after.has(figure));

    this.watcher.note(
      "tier",
      `readPlace(${reason}) in ${this.townTag(this.settlement)} — ` +
        `${before.size} → ${after.size} bodies` +
        `${appeared.length === 0 ? "" : `; appeared ${handleList(appeared)}`}` +
        `${vanished.length === 0 ? "" : `; ceased to exist ${handleList(vanished)}`}`,
    );
  }

  /**
   * Says so when a body close enough to talk to appears or ceases to exist.
   *
   * **The third of the investigation's instruments** (item 6: *"a
   * body-destroyed-near-player diff"*), and the one that answers the second
   * symptom directly — a figure the player was mid-conversation with vanishing.
   * Bounded to {@link NEAR_BODY_TILES} because a town holds two hundred bodies
   * and the ones worth a log line are the ones the player could reach.
   *
   * Every line carries the membership rule that explains it
   * ({@link Witnessing.membershipRule}), which is the whole point: *"it
   * disappeared"* is what a screenshot already said.
   *
   * # Earshot is not existence (chunk Fx-7)
   *
   * The diff is over the *near* set and has been since wave 1.6, so a body that
   * walked four tiles used to be reported as having `ceased to exist` — false,
   * and chunk I-6b filed it after narrowing the membership rule to two answers
   * and leaving it standing (`docs/decisions/witness.md`). Both questions are
   * answerable in the loop below and neither costs a read: this pass runs
   * straight after the crowd is collected, so the props it is walking are
   * **exactly the bodies the simulation is listing** — and a departed id that is
   * still among them left earshot, while one that is not was dropped by the
   * list.
   */
  private noteNearBodies(): void {
    const near = new Map<string, { x: number; y: number; z: number; label: string }>();
    // Every body the simulation has standing here, in the ids the diff is
    // keyed on — which is why it is collected here rather than composed from
    // {@link Witnessing.bodies}: one spelling of `npc:<figure>`, and it is
    // `witness/scenes.ts`'s.
    const listed = new Set<string>();

    for (const prop of this.sceneProps) {
      if (prop.kind !== "npc") {
        continue;
      }

      listed.add(prop.id);

      const away = Math.hypot(
        prop.x - this.from.x,
        prop.y - this.from.y,
        prop.z - this.from.z,
      );

      if (away <= NEAR_BODY_TILES) {
        near.set(prop.id, { x: prop.x, y: prop.y, z: prop.z, label: prop.label ?? "" });
      }
    }

    const rule = this.membershipRule();

    for (const [id, at] of near) {
      if (!this.nearBodies.has(id)) {
        this.watcher?.note(
          "body",
          `+ ${id} ${at.label} at ${place(at)} — ${rule}`,
        );
      }
    }

    for (const [id, at] of this.nearBodies) {
      if (near.has(id)) {
        continue;
      }

      // Two departures and two sentences. The membership rule explains only the
      // second: a body that walked away is not a body any rule stopped listing.
      this.watcher?.note(
        "body",
        listed.has(id)
          ? `− ${id} ${at.label} walked out of earshot, last drawn at ${place(at)}` +
              ` — still on the simulation's list`
          : `− ${id} ${at.label} ceased to exist, last drawn at ${place(at)} — ${rule}`,
      );
    }

    this.nearBodies = near;
    this.placedSinceDiff = null;
  }

  /**
   * Which rule explains the bodies that just came or went.
   *
   * **Two answers where the wave-1.6 investigation separated three.** The third
   * was the client's own ambient loop, which drew a town's named roster wherever
   * the simulation had not promoted it — item 6 deleted that owner, so what is
   * left is the promoted town changing (everybody here is somebody else now) and
   * the promoted roster being reconciled underneath a town that did not change
   * (somebody died, came of age, walked out of the presence list). Named rather
   * than guessed at: this class is the only thing that knows which of the two it
   * just did.
   */
  private membershipRule(): string {
    if (this.placedSinceDiff !== null) {
      return `the promoted town changed (${this.placedSinceDiff})`;
    }

    return "promoted roster reconcile — the simulation stopped listing them";
  }

  /** A settlement as a log line names one: `#9 Goldenhold`, or `nothing`. */
  private townTag(settlement: SettlementId | null): string {
    if (settlement === null || settlement === -1) {
      return "nothing";
    }

    const name = this.settlements.get(settlement)?.name ?? "?";

    return `#${settlement} ${name}`;
  }

  /**
   * Re-reads what the visited settlement has standing in it, and what its people
   * are called.
   *
   * **It puts nobody anywhere, and that is item 6** (ruled 2026-08-28 — *one
   * owner per body*). This method used to lay a town's named roster out on a
   * ring around its centre whenever the simulation had not promoted the town,
   * which meant two systems drew one roster and a promotion swapped them under a
   * standing player. A named figure is drawn from
   * {@link WitnessSource.presentFigures} or is not drawn at all, so what is left
   * here is the two reads that are genuinely *about the place*: what it has
   * built, and what to call the people the simulation has standing in it.
   *
   * Separate from {@link Witnessing.readPlace} so that the before-and-after of a
   * change of town can be taken around it — which is what the debug window's
   * body diff is (`dev/debuglog.ts`).
   */
  private anchorCrowd(): void {
    // What the town has built, and the benches worth watching in it.
    this.readLots();

    const named = this.source.roster();

    // Somebody else's roster — see {@link Witnessing.rosterStale}. A roster
    // entry carries the settlement it belongs to, so the client can tell, and
    // when it cannot find this town in the list the honest thing is to keep the
    // names it already has and ask again at the next day boundary.
    const mine = named.filter((entry) => entry.settlement === this.settlement);
    this.rosterStale = mine.length === 0 && named.length > 0;

    if (this.rosterStale) {
      return;
    }

    // The names, joined once per place-or-day change rather than once a frame:
    // the roster is a cached read on the far side and a *name* is a string,
    // which crosses per call (decision 11). `readLocal` refreshes them on the
    // scene cadence for the same reason a body's position is refreshed there —
    // somebody comes of age, somebody dies — and this is the first join, made
    // before the first frame of a new town is drawn.
    this.presentNames = new Map(mine.map((entry) => [entry.figure, entry.name]));
  }

  /**
   * Re-reads the local tier: the board, the bodies and the hands.
   *
   * **The third cadence** (the fields' own note): a body moves every tick and a
   * board turns over on a job pulse, so a read on the day boundary would hand a
   * scene yesterday's square — and a read on every tick would assemble a scene
   * for every tick of a night nobody watched.
   *
   * The three are read together because they are the same tier and they are all
   * empty in the same worlds: the local tier exists while a settlement is
   * promoted and nowhere else — which since item 6 is the same sentence as
   * *while the player is somewhere* ({@link WitnessSource.here}).
   *
   * Called from the frame rather than from the step, and only when a tick has
   * happened since the last one ({@link Witnessing.localStale}).
   *
   * It also drains the dug tiles, which is what turns *"the world changed
   * somewhere"* into a list of squares worth re-reading — the client's whole
   * answer to a simulation that deliberately publishes no pile iterator
   * ({@link PlayerHands.takeDugTiles}).
   */
  private readLocal(): void {
    if (!this.localStale) {
      return;
    }

    this.localStale = false;

    this.board = this.source.jobs();
    this.bodies = this.source.presentFigures();
    this.held = this.source.carried();
    this.wornItems = this.source.worn();
    this.quiverState = this.source.quiver();
    this.wearable = this.source.wearable();
    // Which way each of them is turned, from the displacement between this read
    // and the last one — presentation between two positions the simulation has
    // stated, and the only place either of them is compared (`witness/scenes.ts`
    // and the V-A rule it states). Here rather than where they are drawn,
    // because a frame drawn between two ticks would compare a read with itself.
    this.facing.update(this.bodies);
    // **The names are deliberately not re-joined here.** They are the roster's
    // and the roster is day-boundary bulk (decision 11) — a name does not change
    // — so joining them on this cadence would be a list pulled thirty times a
    // second for an answer that moves when a town does
    // ({@link Witnessing.anchorCrowd}). What it costs is that somebody the
    // simulation started embodying mid-day is drawn with a blank plate until the
    // next boundary, which is what `bodyPieces` already promises for a figure
    // the roster has no name for.

    const dug = this.hands?.takeDugTiles() ?? [];

    for (const at of dug) {
      this.watch(at);
    }

    // Every site somebody is working is a tile something is about to be left
    // on, so it joins the watched set the moment the order is taken rather than
    // when the log lands on the floor. New sites only: re-reading the whole set
    // on every step is exactly the scan this arrangement exists to avoid.
    for (const post of this.board) {
      if (post.worker !== null && !this.piles.has(levelKey(post.site))) {
        this.watch(post.site);
      }
    }

    if (dug.length > 0) {
      // A dig opens a tile *and* takes the floor above it (`sim_core::dig`), so
      // the tiles around the ones reported may have changed too — but the
      // report is the honest list of what did, and following it is what keeps
      // this a lookup rather than a sweep.
      this.repileWatched();
    }
  }

  /**
   * Starts watching a tile, and reads what is on it now.
   *
   * Bounded by {@link WATCHED_TILES} and pruned oldest-first, which is what
   * keeps a session that has dug a corridor from re-reading four hundred squares
   * a step. Oldest-first rather than furthest-first because a `Map` keeps
   * insertion order and distance would be a sort: the tile a player dug ten
   * minutes ago is the tile they have walked away from, in the ordinary case.
   */
  private watch(at: TilePos): void {
    this.repile(at);

    while (this.piles.size > WATCHED_TILES) {
      const oldest = this.piles.keys().next();

      if (oldest.done === true) {
        break;
      }

      this.piles.delete(oldest.value);
    }
  }

  /** Re-reads one watched tile, and stops watching it once it holds nothing. */
  private repile(at: TilePos): void {
    const key = levelKey(at);
    const items = this.source.tileItems(at);

    if (items.length === 0) {
      // A tile with nothing on it is not a tile worth watching. Dropped rather
      // than kept as an empty entry, so the set stays what its name says it is.
      this.piles.delete(key);

      return;
    }

    // The item a pickup would take — the first *portable* one in handle order,
    // which is the simulation's rule spelled once (`sim_core::hands`' `take`
    // walks past a door, a table and a shelf to find it). Asked kind by kind
    // rather than compared against a table kept here: the rows are the
    // simulation's and a copy of them is a second opinion
    // ({@link WitnessSource.itemPortable}).
    const taken = items.find((item) => this.source.itemPortable(item.kind));

    this.piles.set(key, {
      at,
      items,
      shelf: this.source.hasShelf(at),
      takes: taken === undefined ? null : this.itemWords(taken.item),
    });
  }

  /** Re-reads every watched tile. Called when something the player did could have moved one. */
  private repileWatched(): void {
    for (const at of [...this.piles.values()].map((pile) => pile.at)) {
      this.repile(at);
    }
  }

  /**
   * Re-reads the buildings this town has standing, and watches their benches.
   *
   * On the place-and-day cadence rather than the step's, because a plan is a
   * deterministic synthesis over (seed, settlement records) and the only thing
   * that moves it is a town growing — which happens on a day boundary at most
   * (`sim_core::townplan`). A read per step would be a plan derived thirty times
   * a second for an answer that changes once a season.
   *
   * The work tiles join the watched set because a workshop's bench is where a
   * craft leaves what it made, and a library's is where its shelf stands — both
   * of which are things a player walks up to.
   */
  private readLots(): void {
    if (this.settlement === null) {
      // Nowhere has nothing standing. The country is not a town with no
      // buildings — it is not a town — so the list is emptied rather than kept,
      // which is what stops the last town's workshops being offered on a
      // hillside.
      this.townLots = [];

      return;
    }

    // The name and the ground under it are resolved *here*, once, rather than
    // while a frame is assembling prompts: a building's word is a string and
    // strings cross per call (decision 11), and the footing probe is a handful
    // of terrain queries.
    this.townLots = this.source.lots(this.settlement).map((lot) => ({
      lot,
      name: this.source.lotName(lot.kind),
      at: this.groundTile(lot.work.x, lot.work.y),
    }));

    for (const standing of this.townLots) {
      if (!this.piles.has(levelKey(standing.at))) {
        this.watch(standing.at);
      }
    }
  }

  /** The tile a walker stands on at a column, which is where the client places things. */
  private groundTile(x: number, y: number): TilePos {
    return { x, y, z: Math.floor(surfaceZ(this.terrain, x + 0.5, y + 0.5)) };
  }

  /**
   * Lays out where a ruined town's wreckage stands, or empties the list.
   *
   * The positions are the client's ({@link RUIN_PROP_COUNT}) and the *fact* is
   * the simulation's: `ruins()` says a town is still in the wreckage of the
   * last army through it and says nothing about where anything is. Laid out on
   * the golden angle like the crowd, from the settlement's own id, so the same
   * town burns the same way on every reload — the screenshot promise every
   * cosmetic variant in this client makes.
   *
   * Only the visited settlement, because that is the only town whose ground the
   * client draws props on at all: the roads out of a sacked town already carry
   * the burned farmsteads the aftermath put there, which is what a ruin looks
   * like from a distance.
   */
  private layOutRuin(): void {
    const centre = this.town;
    const where = this.settlement;

    if (centre === null || where === null || !this.ruins.has(where)) {
      this.ruinProps = [];

      return;
    }

    const heaps: { x: number; y: number; z: number }[] = [];
    const spread = Math.max(1, RUIN_PROP_COUNT - 1);

    for (let index = 0; index < RUIN_PROP_COUNT; index += 1) {
      // Offset by the settlement's own id so two ruined towns are not the same
      // five heaps in the same five places.
      const angle = (index + where) * GOLDEN_ANGLE;
      const radius = this.clearRadius(
        centre,
        angle,
        RUIN_RING_MIN + (index / spread) * (RUIN_RING_MAX - RUIN_RING_MIN),
      );
      const x = centre.x + Math.cos(angle) * radius;
      const y = centre.y + Math.sin(angle) * radius;

      heaps.push({ x, y, z: surfaceZ(this.terrain, x, y) });
    }

    this.ruinProps = heaps;
  }

  /**
   * Collects everything a body must not be anchored on top of.
   *
   * The aftermath, the books and the wreckage: three lists that are read at the
   * same moment and are the same thing as far as the anchoring is concerned —
   * things the player walks up to and presses F on, where a body standing
   * nearer than the verb reaches takes the press
   * ({@link PROP_CLEARANCE_TILES}). A headless session found that failure with
   * the market board before a person did, and the fix generalises rather than
   * being repeated.
   *
   * The town's copy of the Book of Eras is a fourth, and only ever adds a
   * position in the one case that is not already covered: on a shelf it stands
   * on a book's own tile, and in a town with no books it stands somewhere the
   * list would otherwise not know about ({@link ERAS_SHELF_OFFSET}).
   */
  private readClearances(): void {
    const eras = this.erasStand();

    this.clearances = [
      ...this.features.map((feature) => ({ x: feature.position.x, y: feature.position.y })),
      ...this.books.map((book) => ({ x: book.position.x, y: book.position.y })),
      ...this.ruinProps.map((heap) => ({ x: heap.x, y: heap.y })),
      ...(eras === null ? [] : [{ x: eras.x, y: eras.y }]),
    ];
  }

  /**
   * Re-reads the shelf, and the clearances that depend on where a book stands.
   *
   * Its own method because it is called from two cadences: the day boundary
   * with everything else, and a couple of ticks after the player leaves a book
   * ({@link Witnessing.booksAwaited}).
   */
  private readShelf(): void {
    // A shelf belongs to a town, so nowhere has none — the same branch
    // {@link Witnessing.readWorld} takes for the day-boundary read.
    this.books = this.settlement === null ? [] : this.source.chronicles(this.settlement);
    this.readClearances();

    if (this.hud.journal.open) {
      this.showJournal();
    }
  }

  /**
   * Returns a radius along a bearing that keeps a body clear of everything the
   * player has to walk up to and read.
   *
   * Pushed outward rather than rotated, because the bearings are the golden
   * angle's and rotating one into another's is how a crowd of ten becomes a
   * crowd of nine standing in a line.
   *
   * The push is *solved* rather than stepped: a ray from the centre crosses a
   * feature's clearance circle over an interval of radii, and the far end of
   * that interval is the nearest radius on this bearing that is out of it —
   * which is the quadratic below, with a negative discriminant meaning the ray
   * misses the circle entirely. The obvious alternative, stepping out by
   * whatever the clearance is short by, *approaches* that far end from inside
   * and a bounded number of steps can stop short of it — which is how a body
   * ends up five point nine tiles from a board it has to be six from, which is
   * how this came to be written down.
   *
   * The loop is over re-entry: clearing one feature can push a body into
   * another's circle, and each pass clears at least one, so a settlement's worth
   * of props terminates long before the bound.
   */
  private clearRadius(centre: SettlementView, angle: number, wanted: number): number {
    let radius = wanted;

    for (let pass = 0; pass < 8; pass += 1) {
      let pushed = radius;

      for (const standing of this.clearances) {
        const toX = standing.x - centre.x;
        const toY = standing.y - centre.y;
        // How far along the bearing the feature lies, and how far the ray passes
        // from it at that point.
        const along = toX * Math.cos(angle) + toY * Math.sin(angle);
        const gap = PROP_CLEARANCE_TILES ** 2 - (toX ** 2 + toY ** 2) + along ** 2;

        if (gap <= 0) {
          continue;
        }

        const half = Math.sqrt(gap);

        if (radius > along - half && radius < along + half) {
          pushed = Math.max(pushed, along + half);
        }
      }

      if (pushed === radius) {
        return radius;
      }

      radius = pushed;
    }

    return radius;
  }

  /**
   * The day turned, or the player reached somewhere else. Re-reads accordingly.
   *
   * Separate from {@link Witnessing.step} for one reason: this returns early
   * when neither has happened, and a watch has to be asked on every step whether
   * or not anything did ({@link Witnessing.standWatch}).
   */
  private crossBoundary(
    today: WorldDateView,
    here: SettlementId | null,
    dayTurned: boolean,
    arrived: boolean,
  ): void {
    if (dayTurned) {
      // What that day cost, in stepped seconds, which is what everything
      // sub-day divides by. Two guards on believing it: it has to fall in the
      // band ({@link DAY_SECONDS_MIN}), and there has to have been a *previous*
      // boundary to have measured from — the span from a session's first frame
      // to its first boundary is a fragment of a day and believing it as a
      // whole one is how a long day gets measured as a short one
      // ({@link Witnessing.dayAnchored}).
      if (this.dayAnchored && this.sinceDay >= DAY_SECONDS_MIN && this.sinceDay <= DAY_SECONDS_MAX) {
        this.daySeconds = this.sinceDay;
        this.dayMeasured = true;
      }

      this.dayAnchored = true;
      this.sinceDay = 0;
      this.today = today;
      // First light is the first watch. Zero rather than the spacing, so the
      // morning's offer happens on the boundary itself, which is where Phase 4
      // made its one offer a day and is still the moment a beat can become
      // today's. On the simulation's clock the same thing is said by forgetting
      // which slice was last stood.
      this.nextWatch = 0;
      this.watchSlot = -1;
    }

    if (arrived) {
      // Before the assignment, so the line names both ends of the move. Since
      // item 6 this is the promotion slot changing hands and nothing else: there
      // is no second answer to compare it against, so the line that used to say
      // what the slot held while `here()` said something different is gone with
      // the window it was watching for (item 6's rider 4).
      this.watcher?.note(
        "town",
        `here() ${this.townTag(this.settlement)} → ${this.townTag(here)} — ` +
          `the promoted town changed hands`,
      );
      this.settlement = here;
      // A new town is a new watch. What the director may choose is asked of the
      // settlement the player is standing in, so walking into one is a change
      // in the client's own half of the question — the same kind of change a
      // watch exists to notice, arriving early.
      this.nextWatch = this.sinceDay;
      this.watchSlot = -1;
    }

    // The ground first, then what stands on it: the wreckage is laid out clear
    // of what is already there ({@link PROP_CLEARANCE_TILES}).
    this.readWorld();

    // The roster is re-read when the player reaches somewhere else, when the
    // last read was somewhere else's — a day boundary is exactly when the source
    // stops handing that one back ({@link Witnessing.rosterStale}) — and when
    // the town has just gone into or come out of ruin
    // ({@link Witnessing.ruinTurned}).
    if (arrived || this.ruinTurned || (dayTurned && this.rosterStale)) {
      // Three callers share one branch, so the tag names whichever of them fired
      // — an arrival and a ruin turning read the same from the outside and are
      // very different things to see in a log (wave 1.6, item 7).
      this.readPlace(arrived ? "arrived" : this.ruinTurned ? "ruinTurned" : "dayTurned");
    }
  }

  /**
   * Offers the feed to the director if a watch has come due.
   *
   * **The offer cadence** (Phase 5 decision 9). The day boundary is the only
   * moment a beat can *become* today's, and offering only there is one
   * scheduling decision per in-game day — which at a twenty-minute day is one
   * every twenty minutes of play. A watch is a fixed fraction of the day
   * instead, so the cadence follows whatever a day turns out to be without the
   * client ever being told what one is.
   *
   * **This is scheduling and therefore legitimately client-side** (the phase's
   * cross-cutting rule: salience sim-side, scheduling client-side). It changes
   * how often the director is asked and nothing about what it may answer: the
   * feed is the same list in the same order, and every rule in
   * `witness/director.ts` applies at the sixth watch of a day exactly as at the
   * first.
   *
   * The next watch is set from *now* rather than from the one that was due, so
   * a reset — a new day, a new town — spaces the following ones properly
   * instead of firing the rest of the day's at once.
   */
  private standWatch(): void {
    const phase = this.simPhase();

    // The simulation's own clock, where there is one: the watch is which slice
    // of the day the world is standing in, so the cadence is exact from the
    // first frame of a session rather than from its second day
    // ({@link Witnessing.watchSlot}). A *measured* clock is deliberately not
    // used for this — see {@link Witnessing.simPhase}.
    if (phase !== null) {
      const slot = Math.floor(phase * this.watchesPerDay);

      if (slot === this.watchSlot) {
        return;
      }

      this.watchSlot = slot;
      this.offerBeats();

      return;
    }

    if (this.sinceDay < this.nextWatch) {
      return;
    }

    this.offerBeats();
    this.nextWatch = this.sinceDay + this.daySeconds / this.watchesPerDay;
  }

  /**
   * Re-reads what the log has left standing and who is on the roads, and
   * refreshes whatever the player has open.
   *
   * The day-boundary pull. Both lists are cheap on the far side — the live
   * source caches them against a stamp that only moves when a record lands
   * (`world/wasm.ts`) — and both grow while a session runs, which is the whole
   * point of re-reading them: the grave of somebody who died this morning is the
   * ground carrying the news.
   */
  private readWorld(): void {
    // The map first, because everything under it is placed on it: a road needs
    // both ends, a column needs a line to walk, and the smoke over a sacked
    // town needs the town. Read here rather than with the roster because it is
    // *world* data on the day-boundary cadence, and the roster is read on a
    // different one (the header's note on what moves).
    this.settlements = new Map(
      this.source.settlements().map((settlement) => [settlement.settlement, settlement]),
    );
    this.features = this.source.features();
    this.groups = this.source.refugeeGroups();
    this.routes = this.source.routes();
    this.roadLines = this.readRoadLines();
    this.pulses = this.source.pulses();
    // The two reads that are *about a town* rather than about the world, and
    // therefore the two with a nowhere branch: a player standing between two
    // settlements is offered no beats and stands in front of no shelf. Both are
    // empty rather than stale, because holding the last town's is how the client
    // used to draw somebody else's street (Phase 4 decision 10a).
    this.beats = this.settlement === null ? [] : this.source.beats(this.settlement, BEAT_WINDOW_DAYS);
    // The books on this town's shelves, and every town the log says is still in
    // the wreckage of the last army through it. Both are day-boundary bulk like
    // everything above: a book is left by a command and a ruin is a projection
    // over records, so neither changes inside a day without something landing —
    // and the one case that does, the player leaving a book themselves, is
    // caught by {@link Witnessing.booksAwaited} rather than by re-reading.
    this.books = this.settlement === null ? [] : this.source.chronicles(this.settlement);
    this.ruins = new Map(this.source.ruins().map((ruin) => [ruin.settlement, ruin]));
    // And the world's own book, on the same cadence and for the same reason: an
    // age breaks when a record lands, so a day is the smallest span in which the
    // saga can have changed at all ({@link Witnessing.book}).
    this.book = readSaga(this.source);
    // And where the town will let them stand, asked once for all of them
    // ({@link Witnessing.propGround}).
    this.snapProps();

    const ruined = this.settlement !== null && this.ruins.has(this.settlement);

    this.ruinTurned = ruined !== this.ruinedHere;
    this.ruinedHere = ruined;
    // Twice, and the order is the point: the wreckage is laid out *clear of*
    // what is already standing — a heap of char across the market board would be
    // the client burying the one thing in a sacked town a player most wants to
    // read — and then it joins the list itself, because it is walked up to and
    // read like everything else on it.
    this.readClearances();
    this.layOutRuin();
    this.readClearances();

    for (const feature of this.features) {
      if (this.readable.has(feature.id)) {
        continue;
      }

      // The board is not aftermath and nothing carved it — it is the market's
      // physical read, placed by the client (`world/types.ts`).
      this.readable.set(
        feature.id,
        feature.kind !== FEATURE_MARKET_BOARD && this.source.inscription(feature.id) !== "",
      );
    }

    // What this town has not heard, re-derived: the journal has grown since the
    // last boundary and so has the town's own store (`rumors(here)` is the real
    // answer and the client's `told` set is only the tick of latency in front
    // of it).
    this.refreshShare();

    // A panel a player is reading is a panel that has to keep up. The market's
    // prices are derived on every call and move without a record being written,
    // so this is the one surface where a day boundary changes what is on screen
    // without anybody pressing anything.
    if (this.hud.market.open) {
      this.showMarket();
    }

    if (this.hud.trace.open) {
      this.showTrace();
    }

    // The people view draws on the columns and on what the director has staged,
    // and both were just re-read: a journal left open while the day turns keeps
    // up for the same reason the board does.
    if (this.hud.journal.open) {
      this.showJournal();
    }

    // And the map, which draws the settlements and the road lines this method
    // just replaced. A town founded during a session is a town whose mark
    // appears on the paper the next morning — if the player has been on the
    // ground it stands on, which is the map's own rule (`map/survey.ts`).
    if (this.hud.map.open) {
      this.showMap();
    }
  }

  /**
   * Every body: one prop, one label, one thing to talk to — drawn where the
   * simulation put them, and nowhere else.
   *
   * **One owner per body** (item 6, ruled 2026-08-28). There used to be a branch
   * here: a promoted town's people stood where the local tier said and every
   * other town's stood where the ambient loop had anchored them. Two owners for
   * one roster is what made a figure teleport in front of a standing player and
   * a conversation partner cease to exist mid-sentence — a promotion or a
   * demotion is a change of *owner*, and neither owner knew about the other. So
   * there is one: a named figure is drawn from
   * {@link WitnessSource.presentFigures} or is not drawn at all, which for an
   * unpromoted town means nobody, exactly as a town beyond the halo already
   * looked.
   *
   * The names come off the roster and the positions off the local tier — two
   * reads on two cadences, joined here, because a name is a string that crosses
   * per call and a position moves every tick (`witness/scenes.ts`'s
   * `bodyPieces`).
   *
   * **The player is not in the list**: the local tier gives their figure no
   * presence record at all (wave 1B's call), so nothing here has to filter them
   * out of their own street.
   *
   * **And nobody else is filtered out either.** There used to be one exception: a
   * trade pulse's named talker was skipped here, because the client also drew
   * that person as a cart on a road and drawing one figure twice in a frame is a
   * bug whichever list they came off. The owner's playtest of 2026-08-30 found
   * what it actually cost — standing 0.6 tiles from the tile the Bodies tab gave
   * for #250 Solald, with nobody drawn and no `[body]` line —
   * because `economy::talker` draws a talker from every living resident per road,
   * so three of the home town's ten were hidden and one of them was the ruler.
   * **Ruled under rider 1 (one owner per body): the client draws every body the
   * simulation has.** A body the simulation is holding up and the client refuses
   * to draw is the two-sources problem said the other way round — one list says
   * a figure is standing in this street and another says they are not, and the
   * player is the one who has to reconcile them. So the body is drawn, and the
   * *inconsistency is made visible instead*: the Bodies row says who the log has
   * away with a pulse ({@link Witnessing.doingOf}) while they stand in the street.
   * **Filed for the sim** (wave 2's first batch, a golden-mover): talkers drawn
   * from adults only, one figure per season, never the ruler — and the honest
   * end state, which is a figure who is away having a presence *on the road*
   * rather than at home.
   */
  private collectCrowd(): void {
    const names = this.presentNames;

    for (const prop of bodyPieces(
      this.bodies,
      (figure) =>
        // Somebody the player has spoken to carries where they are from wherever
        // they are standing, which is the persistent plate decision 6 asks for.
        this.people.labelFor(figure, names.get(figure) ?? ""),
      // What the plate calls each stage, and how tall it is drawn: both off the
      // simulation's own body plan since wave 2A, which is what deleted the two
      // authored child scales and the `(child)` tag this client used to compose
      // (`witness/scenes.ts`, `render/props.ts`).
      (stage) => this.stageWord(stage),
      (stage) => this.source.bodyScale(stage),
      this.walk,
      this.facing,
      // Where a carrier with no body of its own is standing — which is the
      // player, and only the player: the simulation moves a carried body with a
      // *sim* carrier itself and has never known where the host is
      // (`witness/scenes.ts`'s `carriedPlace`).
      (carrier) => (carrier === this.source.playerFigure() ? this.from : null),
    )) {
      const figure = Number(prop.id.slice("npc:".length));
      const body = this.bodies.find((other) => other.figure === figure);

      this.sceneProps.push(prop);
      this.interactables.push({
        id: prop.id,
        label: prop.label ?? "",
        x: prop.x,
        y: prop.y,
        // **The prop's height, and since chunk Sf that is a decision rather than
        // a coincidence.** A body on a flight of stairs is drawn on the treads
        // and the tile the simulation has it on is up to a whole course below
        // them (`witness/scenes.ts`), and `selectInteractable` measures range in
        // three dimensions — so a subject planted at the sim's own `z` would sit
        // under the body the player is aiming at, and the cone would meet one
        // while the eye met the other. The thing addressed is the thing seen.
        z: prop.z,
        subject: {
          kind: "npc",
          figure,
          // The *tile*, because a carry verb cites ground like every other
          // Phase 7 verb (`witness/interact.ts`), and `null` for a body this
          // pass could not find a row for — which cannot happen, since the row
          // is what the prop was built from.
          at: body?.at ?? null,
          carry: this.carryVerbFor(body),
          follow: this.followVerbFor(figure),
        },
        // An NPC does not know what they are about to say: which rumour they
        // reach for is the simulation's choice at the moment of asking.
        refs: [],
      });
    }
  }

  /**
   * What a plate calls one life stage, asked of the simulation once per stage.
   *
   * The read is {@link WitnessSource.lifeStageWord}, which is a table lookup
   * sim-side and a string across the boundary — so it is memoised, and the memo
   * is **four entries for the life of a session** rather than one per figure:
   * a stage's word is a fact about a row of a table and not about a person, so
   * nothing about a date turning can change it and there is nothing to clear.
   *
   * That is the whole of what replaced the per-figure *is this one grown* memo
   * wave 2A deleted. A body's own stage rides its presence row now
   * ({@link PresentFigure.stage}), which is the cheaper answer to the same
   * question: no call at all, at any cadence.
   *
   * `""` for a grown body, which is an answer rather than a gap — an adult is
   * unmarked — and for a stage id this build does not ship.
   */
  private stageWord(stage: number): string {
    const known = this.stageWords.get(stage);

    if (known !== undefined) {
      return known;
    }

    const word = this.source.lifeStageWord(stage);
    this.stageWords.set(stage, word);

    return word;
  }

  /**
   * How many things the player has in their hands, bodies included.
   *
   * **One pair of arms** (`sim_core::hands`' `carrying`): a body takes one of
   * [`MAX_CARRIED`]'s slots exactly as a boulder does, so the two refusals this
   * feeds — hands full and hands empty — have to count both or the client
   * predicts the wrong one. A player holding nothing but a baby used to be told
   * their hands were empty while the simulation put the baby down.
   *
   * The bodies are counted off the presence list rather than asked for, because
   * the answer is already on it ({@link PresentFigure.heldBy}) and the list is
   * pulled every step anyway.
   */
  private get inHands(): number {
    const player = this.source.playerFigure();

    if (player === null) {
      return this.held.length;
    }

    return this.held.length + this.bodies.filter((body) => body.heldBy === player).length;
  }

  /**
   * What the interact verb does to one body: talk to it, lift it, or put it
   * down.
   *
   * **Every branch is an answer the simulation gave** (wave 2A, and rider 2's
   * *"portability is a body property, not a baby rule"*): whether a stage is
   * liftable is the size curve's ({@link WitnessSource.lifeStagePortable}) and
   * who is holding somebody rides the presence row
   * ({@link PresentFigure.heldBy}). What is decided here is only the *order* of
   * three questions, which is a fact about a prompt.
   *
   * **Set down beats pick up**, because a body already in your arms cannot be
   * lifted again and the press a player has in mind while holding one is
   * putting it down. **Talk beats nothing**: a body too big to carry is still
   * somebody to talk to, which is every adult in the street.
   *
   * A body somebody *else* is carrying offers `talk to`: taking a child out of
   * a stranger's arms is a contest and not a pickup, and the simulation refuses
   * it at the door (`sim_core::hands`).
   */
  private carryVerbFor(body: PresentFigure | undefined): "none" | "take" | "drop" {
    if (body === undefined) {
      return "none";
    }

    if (body.heldBy !== null) {
      return body.heldBy === this.source.playerFigure() ? "drop" : "none";
    }

    return this.source.lifeStagePortable(body.stage) ? "take" : "none";
  }

  /**
   * What the company verb does to one body: take them along, send them on their
   * way, or nothing (wave 2F).
   *
   * {@link Witnessing.carryVerbFor}'s twin one relation over, and the same
   * discipline: **both answers are the simulation's**. A body offers to follow
   * when its own dials, its life stage, what it is taken up with and what its
   * town has heard of the player's deeds all say so
   * ({@link WitnessSource.offersToFollow}); a companion is somebody the world
   * says follows this player ({@link WitnessSource.leaderOf}). Nothing here
   * decides either.
   *
   * `none` in every world nobody has claimed a figure in, and for nearly
   * everybody in nearly every town.
   */
  private followVerbFor(figure: FigureId): "none" | "beckon" | "dismiss" {
    const player = this.source.playerFigure();

    if (player === null) {
      return "none";
    }

    if (this.source.leaderOf(figure) === player) {
      return "dismiss";
    }

    return this.source.offersToFollow(figure) ? "beckon" : "none";
  }

  /**
   * The work happening in this town, and the things lying about it.
   *
   * Two lists and one rule between them (`witness/scenes.ts`): a tool stands at
   * every order somebody has **taken**, and a heap stands on every watched tile
   * that has something on it. Neither of them decides anything — the board says
   * what work there is and whose, the arena says what is on the ground, and this
   * turns both into boxes.
   *
   * The heaps are what the pickup verb addresses, so each one becomes an
   * interactable carrying the label of the item a pickup would actually take —
   * the first *portable* one in handle order, which is the simulation's rule
   * spelled once (`sim_core::hands`). A shelf is the exception and takes its own
   * subject: it is read rather than lifted, and a town whose shelf is bare has
   * something to say about that (plan, decision 14).
   *
   * **A heap of nothing but fittings is drawn and not offered** — the same
   * answer a burned farmstead gets one collector down
   * ({@link Witnessing.collectFeatures}). See {@link Witnessing.pileSubject}.
   */
  private collectScenes(): void {
    for (const piece of toilPieces(
      this.board,
      (x, y) => surfaceZ(this.terrain, x, y),
      this.scenePhase,
    )) {
      this.sceneProps.push(piece.prop);
    }

    for (const pile of this.piles.values()) {
      const piece = goodsPiece(
        pile.at,
        pile.items,
        (x, y) => surfaceZ(this.terrain, x, y),
        pile.shelf,
      );

      if (piece === null) {
        continue;
      }

      this.sceneProps.push(piece.prop);

      const subject = this.pileSubject(pile, piece);

      if (subject !== null) {
        this.interactables.push(subject);
      }
    }
  }

  /**
   * What pressing `F` on one pile addresses: a shelf to read, a thing to pick
   * up, or — the answer chunk B added — **nothing**.
   *
   * `null` is for a heap holding nothing a pair of hands lifts
   * ({@link Pile.takes}), and it is the whole of the fix for the defect the
   * owner's playtest of 2026-08-30 found: every furnished workshop has a table
   * standing on its work tile, so walking up to a smithy read
   * `F — pick up brick table`, and the press sent a `take` the simulation
   * dropped without a word because a fitting is not portable
   * (`sim_core::items`' `is_portable`). A verb that says nothing and does
   * nothing is a verb a player stops pressing, which is the graybox ruling read
   * from the other end ({@link REFUSED_NO_WORK}).
   *
   * **Nothing rather than a subject with a milder verb**, and that is a decision
   * rather than the easy path. A prompt naming the table without a verb — *"a
   * brick table"* — would still be the line the `F` key is written on, and a
   * *look at* verb would be this client inventing a press the simulation has no
   * answer for: every other subject on this list resolves to a command or a
   * panel. The fitting keeps its prop and loses its promise, which is the same
   * bargain a burned farmstead, a signpost and a plume of smoke are already
   * drawn under. The day the simulation grows a verb for furniture — sit, open,
   * haul — this is the one branch that has to learn it.
   *
   * A shelf is decided before portability and stays exactly as it was: a shelf
   * is not portable either, and *read* is a press this world does answer (plan,
   * decision 14).
   */
  private pileSubject(pile: Pile, piece: ScenePiece): Interactable | null {
    // Where it is, which is the same answer for all three outcomes: the heap's
    // own prop, wherever {@link goodsPiece} put it.
    const standing = {
      id: piece.prop.id,
      x: piece.prop.x,
      y: piece.prop.y,
      z: piece.prop.z,
      refs: [],
    };

    if (pile.shelf) {
      return { ...standing, label: SHELF_LABEL, subject: { kind: "shelf", at: pile.at } };
    }

    if (pile.takes === null) {
      return null;
    }

    return {
      ...standing,
      label: pile.takes,
      subject: { kind: "item", at: pile.at, label: pile.takes },
    };
  }

  /**
   * The workshops standing in this town: somewhere to press the craft verb.
   *
   * **Only the buildings with a trade in them**, and that is the simulation's
   * column rather than a list kept here ({@link TownLot.trade}): a home and a
   * storehouse have a work tile like every other lot and no work at either, so
   * offering the verb at every doorstep would be offering a press the world
   * refuses in silence.
   *
   * No prop. A workshop is a *building* and buildings are terrain since wave 1E
   * — the walls, the roof and the doorway are real tiles the mesher draws
   * (`worldgen-direction.md` §4) — so what this adds is the thing to address and
   * nothing to look at.
   */
  private collectWorkshops(): void {
    for (const { lot, name, at } of this.townLots) {
      if (lot.trade === null) {
        continue;
      }

      this.interactables.push({
        id: `workshop:${lot.work.x}:${lot.work.y}`,
        // The simulation's own word for the building — *smithy*, *library* —
        // resolved when the town was read rather than now.
        label: name,
        x: at.x + 0.5,
        y: at.y + 0.5,
        z: at.z,
        subject: { kind: "workshop", at, building: name },
        refs: [],
      });
    }
  }

  /**
   * Every feature the log left standing: one prop each, and a thing to read for
   * the ones that carry writing.
   *
   * A burned farmstead and a battlefield marker are drawn and not offered — see
   * {@link Witnessing.readable}. They are still the log's, still placed from a
   * record, and still the reason the road out of a sacked town looks like one.
   */
  private collectFeatures(): void {
    for (const feature of this.features) {
      const id = `feature:${feature.id}`;
      const kind = propKindForFeature(feature);
      const board = kind === "market-board";
      // Beside the road rather than in it, where the town has built over the
      // column the record named ({@link Witnessing.snapProps}). The prop and the
      // thing to walk up to are one position, because they are one object — and
      // the height is the elevation field's, which is where a feature's own z
      // came from in the first place (`world/wasm.ts`'s `features`).
      const at = this.propAt(
        id,
        feature.position.x,
        feature.position.y,
        feature.position.z,
        (column, row) => this.terrain.groundLevel(column, row),
      );

      this.sceneProps.push({
        id,
        kind,
        ...at,
        facing: feature.facing,
        // The board says what it is; a grave says what is carved on it, and
        // that is read rather than floated.
        label: board ? "market board" : null,
        palette: feature.id,
      });

      if (!board && this.readable.get(feature.id) !== true) {
        continue;
      }

      this.interactables.push({
        id,
        label: labelForFeature(kind),
        ...at,
        subject: board
          ? { kind: "market", feature: feature.id }
          : { kind: "grave", feature: feature.id },
        refs: feature.refs,
      });
    }
  }

  /**
   * Every book standing in this town: one prop each, and something to read.
   *
   * **Sim truth, drawn without asking who wrote it** (Phase 5 decision 5). The
   * curated world has one on a shelf in Askmoor, one road out, before a player
   * has done anything, left by a ruler when a war it won ended, and a book the
   * player compiles goes into the same list through the same query and is drawn
   * by the same prop. That is the NPC parity test on the historian verb, answered
   * by there being nothing here that knows the difference — except one word in
   * the label, because "a book you left" is a true thing to say and a useful one.
   *
   * The position is the *book's own tile*, z included, which is the one thing on
   * the witnessing surface that carries one: a book left in a cellar is not a
   * book on the grass, so the prop stands exactly where the simulation says
   * rather than on the ground the client probes for.
   *
   * The town's copy of the Book of Eras goes up with them and at the end
   * ({@link Witnessing.collectEras}), because it stands on the same shelf and is
   * read with the same verb — it is the one volume here that belongs to nobody.
   */
  private collectChronicles(): void {
    // Which volumes share a tile, worked out before any of them is placed: a fan
    // has to know how many are in it ({@link SHELF_SPACING_TILES}). The town's
    // copy of the Book of Eras is one of them where it stands on a shelf, which
    // is what keeps two things on one tile both addressable
    // ({@link Witnessing.shelfFan}).
    const shelves = this.shelfFan();

    for (const book of this.books) {
      const id = `chronicle:${book.id}`;
      const label = this.bookLabel(book);
      // Out of the road first, then fanned: a volume somebody left standing on
      // ground the town has since built over is moved beside it
      // ({@link Witnessing.snapProps}), and a book on a *shelf* is never in the
      // map at all — it belongs to the library that put it there.
      const ground = this.propAt(
        id,
        book.position.x,
        book.position.y,
        book.position.z,
        (column, row) => this.terrain.groundLevel(column, row),
      );
      const { x, y, facing } = fanPlace(shelves, ground.x, ground.y, id);

      this.sceneProps.push({
        id,
        kind: "chronicle",
        x,
        y,
        z: ground.z,
        facing,
        // Nothing floats over it: what it says is read rather than announced,
        // exactly as a grave is, and the prompt names it when the player is
        // close enough to open it.
        label: null,
        palette: book.id,
      });
      this.interactables.push({
        id,
        label,
        x,
        y,
        z: ground.z,
        subject: { kind: "chronicle", chronicle: book.id },
        // A book knows what it is about before anybody opens it — unlike a
        // person, and like a grave. The citations are the sim's; asking for them
        // costs one flat array (`world/types.ts`).
        refs: this.source.chronicleEntries(book.id),
      });

      // And onto the paper, if the player has earned the ground under it. The
      // store refuses a mark it has not, so an unmarked book is retried every
      // frame it is drawn and lands the moment they walk close enough
      // (`map/ground.ts`'s `place`).
      //
      // The author's name alone rather than the prompt's whole phrase: the
      // glyph already says it is a book, and a page with two towns and three
      // books on it is a page where every extra word is somebody's name covered
      // up ({@link Witnessing.mapLabel}).
      this.mark(
        `chronicle:${book.id}`,
        "chronicle",
        book.position.x,
        book.position.y,
        this.mapLabel(book),
      );
    }

    this.collectEras(shelves);
  }

  /**
   * The town's copy of the Book of Eras, standing where its books stand.
   *
   * **Phase 6's O6 exit criterion, drawn**: the saga is readable in-world, on a
   * shelf, and it is *the same text* the session opened on — same projection,
   * same panel, one page per age (`hud/panels.ts`'s `ErasPanel`).
   *
   * Three things about it are decisions rather than details:
   *
   * - **It is presentation and nothing else.** No new sim state, no new record,
   *   no new prop kind: the volume is drawn by the chronicle's own prop and read
   *   through the chronicle's own verb, because it *is* a book on a shelf and
   *   the only thing that makes it a different one is where its lines come from
   *   (decision O6 — "derived, never stored").
   * - **It is offered only when there is something to read.** A world with no
   *   past has no ages and a build whose boundary has no saga export has no
   *   book, and in both cases nothing is drawn at all — a prompt that opened an
   *   empty page would be a prompt that does nothing, which is the rule every
   *   other readable thing here is under ({@link Witnessing.readable}).
   * - **Reading it files nothing in the journal**, and that is the one place
   *   this book differs from the chronicle beside it ({@link Witnessing.readEras}).
   */
  private collectEras(shelves: ReadonlyMap<string, readonly string[]>): void {
    const stand = this.erasStand();

    if (stand === null) {
      return;
    }

    const { x, y, facing } = fanPlace(shelves, stand.x, stand.y, ERAS_PROP_ID);

    this.sceneProps.push({
      id: ERAS_PROP_ID,
      kind: "chronicle",
      x,
      y,
      z: stand.z,
      facing,
      label: null,
      // The town's own number, so the volume looks the same every time a player
      // comes back to it and different from the one in the next town — the same
      // thing a book's id does for a chronicle (`render/props.ts` picks a
      // variant off this). Zero for nowhere, which never draws this at all: the
      // stand is an offset off a town's centre and there is no centre.
      palette: this.settlement ?? 0,
    });
    this.interactables.push({
      id: ERAS_PROP_ID,
      label: ERAS_LABEL,
      x,
      y,
      z: stand.z,
      subject: { kind: "eras" },
      // Nothing, and it is the only readable thing here with nothing: `refs` is
      // what a channel files when it is read ({@link Witnessing.readGround}),
      // and this book is not a channel — the world's own history is not
      // something the player learned from somebody.
      refs: [],
    });
  }

  /**
   * Where the town's copy of the Book of Eras stands, or `null` when there is
   * nothing to open.
   *
   * **On the shelf where the town's books stand**, which is the exit criterion
   * read literally and the reason the fan exists — the volume takes a place in
   * it like any other, so a player facing a shelf of two can address either.
   * The town's *first* book is what names the shelf: a town with books on two
   * tiles has a shelf and an afterthought, and the shelf is the one the oldest
   * book is on.
   *
   * Where nobody has ever written a book, it stands at the town's own spot
   * instead ({@link ERAS_SHELF_OFFSET}) — see that constant for why the world's
   * book cannot be allowed to exist only where somebody happened to write one.
   */
  private erasStand(): TilePos | null {
    if (this.eras.eras.length === 0) {
      return null;
    }

    const shelf = this.books[0];

    if (shelf !== undefined) {
      return { x: shelf.position.x, y: shelf.position.y, z: shelf.position.z };
    }

    const centre = this.town;

    if (centre === null) {
      return null;
    }

    const x = Math.floor(centre.x + ERAS_SHELF_OFFSET.x);
    const y = Math.floor(centre.y + ERAS_SHELF_OFFSET.y);

    // The town's own answer where it has one ({@link Witnessing.snapProps}) —
    // an offset off a centre is as likely to land in a main street as anything
    // in the plan — and the ground the client probes for otherwise: nobody
    // recorded this volume's tile, so it stands on the surface like a signpost.
    const at = this.propAt(
      ERAS_PROP_ID,
      x,
      y,
      surfaceZ(this.terrain, x + 0.5, y + 0.5),
      (column, row) => surfaceZ(this.terrain, column + 0.5, row + 0.5),
    );

    return { x: Math.floor(at.x), y: Math.floor(at.y), z: at.z };
  }

  /**
   * Which volumes share a tile, by the id each of them is drawn under.
   *
   * Ids rather than the books themselves, because the Book of Eras is in the fan
   * and is not one ({@link Witnessing.collectEras}): what the placement needs to
   * know is how many things are on the tile and which of them this is, and an id
   * answers both for either kind.
   */
  private shelfFan(): ReadonlyMap<string, readonly string[]> {
    const shelves = new Map<string, string[]>();
    const shelve = (x: number, y: number, id: string): void => {
      const tile = tileKey(x, y);
      const on = shelves.get(tile);

      if (on === undefined) {
        shelves.set(tile, [id]);
      } else {
        on.push(id);
      }
    };

    for (const book of this.books) {
      shelve(book.position.x, book.position.y, `chronicle:${book.id}`);
    }

    const stand = this.erasStand();

    if (stand !== null) {
      shelve(stand.x, stand.y, ERAS_PROP_ID);
    }

    return shelves;
  }

  /**
   * The wreckage of the last army through this town, and the record it cites.
   *
   * Phase 5 decision 11, drawn. Every heap is addressable and they all carry the
   * *same* record and the same label, which is deliberate: the ruin is the
   * town's and the heaps are how it is drawn, so reading any of them is reading
   * it and the journal files one entry however many the player walks up to
   * (identity is `(event, channel, who)` — `journal/journal.ts`).
   *
   * The read is offered only when the simulation has something written to say
   * about the sack in this town's own register, which is the same rule the
   * aftermath is under: a feature offers the verb exactly when the projection
   * comes back non-empty, so a prompt never does nothing
   * ({@link Witnessing.readable}).
   */
  private collectRuin(): void {
    const settlement = this.settlement;
    const ruin = settlement === null ? undefined : this.ruins.get(settlement);

    if (settlement === null || ruin === undefined) {
      return;
    }

    const where = this.town;
    const label = where === null ? "the wreckage" : `the wreckage of ${where.name}`;
    const readable = this.source.notice(settlement, ruin.sack) !== "";

    for (const [index, heap] of this.ruinProps.entries()) {
      const id = `ruin:${settlement}:${index}`;

      this.sceneProps.push({
        id,
        kind: "ruin",
        x: heap.x,
        y: heap.y,
        z: heap.z,
        facing: (index + settlement) * GOLDEN_ANGLE,
        label: null,
        palette: index + settlement,
      });

      if (!readable) {
        continue;
      }

      this.interactables.push({
        id,
        label,
        x: heap.x,
        y: heap.y,
        z: heap.z,
        subject: { kind: "ruin", settlement },
        refs: [ruin.sack],
      });
    }

    if (where !== null) {
      // A burned town is worth a cross on the paper, and `ruins()` is the whole
      // world's list precisely so a map can carry all of them — the fog is what
      // keeps that honest, since a town whose ground has never been crossed has
      // no position on this paper at all (`map/survey.ts`).
      //
      // **With no name of its own.** The cross lands on the town's own dot and
      // the town's own name is already written beside it; a second label saying
      // the same place twice is the smear the paper's label stacking exists to
      // prevent, spent on nothing (`map/panel.ts`).
      this.mark(`ruin:${settlement}`, "ruin", where.x, where.y, "");
    }
  }

  /**
   * Asks the town where each of its props may actually stand, once per boundary.
   *
   * **The wave-1.5 graybox ruling — *nothing stands in a street*** — and the
   * whole of the client's part in it is *asking*: which columns the plan speaks
   * for, what a walker could stand on and which candidate is nearest are all
   * facts the town plan holds ({@link WitnessSource.openGround}), and a client
   * that reconstructed them from {@link WitnessSource.lots} would be keeping a
   * copy of a layout it does not own.
   *
   * One call for every prop, because laying a town out is the expensive half of
   * the plan and a call per gravestone would derive it per gravestone.
   *
   * Three families are asked about and one is deliberately not:
   *
   * - **Aftermath features** — graves, the marks a sacked road carries, and
   *   **the market board**, which since wave 1.5 is anchored on the market
   *   lot's own doorstep rather than on a client guess (`world/wasm.ts`'s
   *   `MARKET_BOARD_LOT`). It is in this list all the same, because a
   *   plan-chosen street column still passes through the same read every other
   *   prop does — so a town whose market has not been built yet gets its board
   *   moved out of a wall.
   * - **The town board** — a fixed offset off the centre, which is as likely to
   *   be a main street as anything in the plan.
   * - **A chronicle nobody shelved.** A book *on a shelf* belongs to the
   *   building it is in — that is where its library put it — so a book with a
   *   shelf under it is left exactly where the simulation says
   *   ({@link WitnessSource.hasShelf}), and only a volume somebody left standing
   *   on the ground is moved out of the road.
   *
   * What is **not** asked about is the ruin's char heaps: they are laid out by
   * the client already, clear of everything else standing
   * ({@link Witnessing.layOutRuin}), and snapping a scatter would collapse it
   * onto one square.
   */
  private snapProps(): void {
    this.propGround = new Map();

    const settlement = this.settlement;

    if (settlement === null) {
      // Nowhere has no plan to ask. Every prop then stands where its record put
      // it, which is what the client did before this read existed and what a
      // town whose plan answers `null` for a column gets anyway
      // ({@link Witnessing.propAt}).
      return;
    }

    const asked: { readonly id: string; readonly at: readonly [number, number] }[] = [];

    for (const feature of this.features) {
      asked.push({
        id: `feature:${feature.id}`,
        at: [feature.position.x, feature.position.y],
      });
    }

    for (const book of this.books) {
      const at = { x: book.position.x, y: book.position.y, z: book.position.z };

      if (this.source.hasShelf(at)) {
        continue;
      }

      asked.push({ id: `chronicle:${book.id}`, at: [at.x, at.y] });
    }

    const here = this.town;

    if (here !== null) {
      asked.push({
        id: `town-board:${settlement}`,
        at: [here.x + TOWN_BOARD_OFFSET.x, here.y + TOWN_BOARD_OFFSET.y],
      });
      asked.push({
        id: ERAS_PROP_ID,
        at: [
          Math.floor(here.x + ERAS_SHELF_OFFSET.x),
          Math.floor(here.y + ERAS_SHELF_OFFSET.y),
        ],
      });
    }

    const found = this.source.openGround(
      settlement,
      asked.map((prop) => prop.at),
    );

    for (const [slot, prop] of asked.entries()) {
      const at = found[slot];

      if (at !== undefined && at !== null) {
        this.propGround.set(prop.id, at);
      }
    }

  }

  /**
   * Where a prop stands, given where its record put it.
   *
   * The town's answer where there is one and the record's otherwise
   * ({@link Witnessing.propGround}). The tile centre is added here rather than
   * by every caller, because a prop standing on a tile's corner is a prop
   * standing in the gutter — which is the same rule `witness/scenes.ts` states
   * for a body.
   *
   * **`groundAt` is the caller's own height rule, and passing it in is the
   * point**: the snap moves a prop *across the ground* and must not change how
   * high the ground is measured. A feature's z has always been
   * {@link TerrainSampler.groundLevel}'s and a board's has always been the
   * footing probe's, and mixing the two put a prop and the place a player stands
   * to read it on two different levels — which is a prompt that never appears.
   */
  private propAt(
    id: string,
    x: number,
    y: number,
    z: number,
    groundAt: (x: number, y: number) => number,
  ): { readonly x: number; readonly y: number; readonly z: number } {
    const snapped = this.propGround.get(id);

    if (snapped === undefined) {
      return { x, y, z };
    }

    const [column, row] = snapped;

    return { x: column + 0.5, y: row + 0.5, z: groundAt(column, row) };
  }

  /**
   * Writes one mark on the map, once, if the ground under it has been earned.
   *
   * The refusal is the guard and it is `map/ground.ts`'s rather than this
   * file's: a position off the surveyed ground is turned away and counted, so
   * the only thing this has to get right is not writing the same mark twice.
   * A mark that was refused is *not* remembered, which is what makes it appear
   * the day the player finally walks there.
   */
  private mark(key: string, kind: "chronicle" | "ruin", x: number, y: number, label: string): void {
    if (this.marked.has(key)) {
      return;
    }

    if (this.ground.place({ kind, x, y, label })) {
      this.marked.add(key);
    }
  }

  /**
   * What the player would call a book they are standing in front of.
   *
   * The author's name is the simulation's, pulled with the shelf like a
   * talker's, and the only word this file adds is which side of the wall wrote
   * it. **A word a player has to look up is a prompt that failed**
   * ({@link labelForFeature}), so it is "a book" and not "a chronicle": the
   * container has a name in `sim-core` and the player has never met it.
   */
  private bookLabel(book: ChronicleView): string {
    if (this.player !== null && book.author === this.player) {
      return "a book you left";
    }

    return book.authorName === "" ? "a book" : `a book by ${book.authorName}`;
  }

  /**
   * What a book is called on the *paper*, which is shorter than what it is
   * called in the world.
   *
   * The prompt names the verb and the thing — "read a book by …" — because it is
   * the one line between a player and something they have never seen. A map has
   * a glyph for "book" and no room to say it twice, so the mark carries the name
   * and nothing else: the simulation's word for who wrote it, or the client's
   * plainest word for a book nobody is named on.
   */
  private mapLabel(book: ChronicleView): string {
    if (this.player !== null && book.author === this.player) {
      return "your book";
    }

    return book.authorName === "" ? "a book" : book.authorName;
  }

  /**
   * Every refugee group, walked along its route as a straight line.
   *
   * Roads have no geometry in this world (decision 8), so the route *is* the
   * line between two settlement positions and there is nothing to follow. The
   * walk is client-side time: the simulation records that a group fled and to
   * where, and the client draws them getting there.
   */
  private collectRefugees(): void {
    for (const group of this.groups) {
      const from = this.settlements.get(group.from);
      const to = this.settlements.get(group.to);

      if (from === undefined || to === undefined) {
        continue;
      }

      const length = Math.hypot(to.x - from.x, to.y - from.y);

      if (length === 0) {
        continue;
      }

      const fraction = this.walkFraction(
        group.departed,
        length,
        ROAD_FRACTION_PER_DAY,
        refugeeCampTiles(group.id),
      );
      const x = from.x + (to.x - from.x) * fraction;
      const y = from.y + (to.y - from.y) * fraction;
      const z = surfaceZ(this.terrain, x, y);
      const id = `refugees:${group.id}`;
      const label = `refugees from ${from.name} (${group.size})`;

      this.sceneProps.push({
        id,
        kind: "refugees",
        x,
        y,
        z,
        // Facing the way they are walking: yaw 0 faces +y, so this is the same
        // `atan2(-dx, dy)` the crowd turns with.
        facing: Math.atan2(-(to.x - from.x), to.y - from.y),
        label,
        palette: group.id,
      });
      this.interactables.push({
        id,
        label,
        x,
        y,
        z,
        subject: { kind: "refugees", group: group.id },
        refs: group.refs,
      });
    }
  }

  /**
   * A signpost at each of this settlement's road mouths, naming where the road
   * goes.
   *
   * Phase 4 decision 4's other half. Every string on it is the simulation's —
   * the destination's own name — and every position is the simulation's too
   * since wave 1.5: the post stands at the column where the road actually leaves
   * the streets ({@link WorldSource.roadMouth}), so a signpost cannot point
   * anywhere a road does not go *and* cannot stand anywhere the road is not.
   *
   * Drawn and never offered to the verb, like the burned farmstead and the
   * cairn: there is nothing carved on it that the log wrote, and a prompt that
   * did nothing would be worse than no prompt (see
   * {@link Witnessing.readable}). What it has instead is the floating name plate
   * every labelled prop carries, which is the same affordance and not a new one.
   */
  private collectSignposts(): void {
    const centre = this.town;

    if (centre === null) {
      // A fingerpost stands at a town's road mouth, so nowhere has none — the
      // road is still terrain and still under the player's feet, and the sign is
      // what the *town* puts at the end of it.
      return;
    }

    for (const leg of this.routes) {
      const other =
        leg.from === this.settlement
          ? this.settlements.get(leg.to)
          : leg.to === this.settlement
            ? this.settlements.get(leg.from)
            : undefined;

      if (other === undefined) {
        continue;
      }

      const spanX = other.x - centre.x;
      const spanY = other.y - centre.y;
      const length = Math.hypot(spanX, spanY);

      if (length < SIGNPOST_ROAD_TILES) {
        continue;
      }

      const dirX = spanX / length;
      const dirY = spanY / length;
      // The mouth the simulation beds its road from, or the old bearing guess
      // for a source that has no plan to ask ({@link SIGNPOST_ROAD_TILES}).
      const mouth = this.source.roadMouth(centre.settlement, other.settlement) ?? {
        x: centre.x + dirX * SIGNPOST_ROAD_TILES,
        y: centre.y + dirY * SIGNPOST_ROAD_TILES,
      };
      // Off the road to the right of somebody walking out of town, which is an
      // arbitrary side consistently chosen — a post in the middle of the path is
      // a post every column walks through.
      const x = mouth.x + dirY * SIGNPOST_SIDE_TILES;
      const y = mouth.y - dirX * SIGNPOST_SIDE_TILES;

      this.sceneProps.push({
        id: `signpost:${centre.settlement}:${other.settlement}`,
        kind: "signpost",
        x,
        y,
        z: surfaceZ(this.terrain, x, y),
        // The arm points down the road, away from the town it stands outside.
        facing: Math.atan2(-spanX, spanY),
        label: other.name,
        palette: other.settlement,
      });
    }
  }

  /**
   * A board at each settlement's centre carrying the town's own name.
   *
   * Decision 4's missing half, found in the owner's dry-run: the signposts name
   * where every road goes, and nothing named where the player already was.
   * Every string on it is the simulation's — the settlement's own name — and it
   * is drawn for **every** settlement the map knows rather than only the one
   * the player stands in, so a town announces itself from the road on the way
   * in instead of after the client has decided the player arrived.
   *
   * Drawn and never offered to the verb, like the signpost: nothing is carved
   * on it that the log wrote, and the floating plate is the affordance.
   */
  private collectTownBoards(): void {
    for (const place of this.settlements.values()) {
      const x = place.x + TOWN_BOARD_OFFSET.x;
      const y = place.y + TOWN_BOARD_OFFSET.y;

      const id = `town-board:${place.settlement}`;
      // The town the player is standing in has an answer about where its own
      // board may stand ({@link Witnessing.snapProps}); every other town on the
      // map is drawn from the offset, because a plan is only derived for the one
      // that was asked about.
      const at = this.propAt(id, x, y, surfaceZ(this.terrain, x, y), (column, row) =>
        surfaceZ(this.terrain, column + 0.5, row + 0.5),
      );

      this.sceneProps.push({
        id,
        kind: "town-board",
        ...at,
        // Face on to the centre it names: the board's front is its local -y
        // (`render/props.ts`), so local +y points *out* along the offset — the
        // same mapping the signpost uses for its arm.
        facing: Math.atan2(-TOWN_BOARD_OFFSET.x, TOWN_BOARD_OFFSET.y),
        label: place.name,
        palette: place.settlement,
      });
    }
  }

  /**
   * The smoke over a settlement a battle record says was stormed this morning.
   *
   * Only while a reaction is running, only on the bearing to the settlement the
   * beat is about, and never for a beat that has not landed —
   * `witness/staging.ts` owns all three of those and this is the drawing of what
   * it decided.
   */
  private collectSmoke(): void {
    const plume = this.staging.plume;
    const beat = this.staging.beat;

    if (plume === null || beat === null) {
      return;
    }

    this.sceneProps.push({
      id: `smoke:${beat.id}`,
      kind: "smoke",
      x: plume.x,
      y: plume.y,
      z: surfaceZ(this.terrain, plume.x, plume.y),
      facing: plume.facing,
      label: null,
      palette: beat.id,
    });
  }

  /** Resolves the legs to pairs of positions, for the paper map to draw. */
  private readRoadLines(): readonly MapRoad[] {
    const lines: MapRoad[] = [];

    for (const leg of this.routes) {
      const from = this.settlements.get(leg.from);
      const to = this.settlements.get(leg.to);

      if (from === undefined || to === undefined) {
        continue;
      }

      lines.push({
        fromX: from.x,
        fromY: from.y,
        toX: to.x,
        toY: to.y,
      });
    }

    return lines;
  }

  /**
   * Hands the day's feed to the director.
   *
   * Called from a watch ({@link Witnessing.standWatch}) — several times a day
   * since Phase 5 decision 9, and on the day boundary as the first of them.
   * What is passed in is the whole of what the client knows and the simulation
   * cannot: the journal, and the columns actually on the roads. Both of those
   * *move within a day*, which is the reason a second look at the same list is
   * a different question and not a retry.
   *
   * The choice itself is `witness/director.ts`'s and it is deliberately not
   * here — what the client is allowed to decide about a beat is *when the player
   * meets it*, and that decision is made in one place (Phase 4's cross-cutting
   * rule).
   */
  private offerBeats(): void {
    const here = this.settlement;

    if (here === null) {
      // Nowhere has no feed — `beats` is a read about a settlement and is empty
      // out here ({@link Witnessing.readWorld}) — and the director's own
      // question is "what is worth meeting *in this town*". Returning rather
      // than offering an empty list keeps the offer count honest: a watch stood
      // in the country is a watch nobody could have been offered anything at.
      return;
    }

    this.director.offer({
      beats: this.beats,
      here,
      today: this.today,
      known: (event: EventId) => this.journal.channelsFor(event).length > 0,
      carried: (beat: SpotlightBeat) => this.columnCarrying(beat) !== null,
    });
  }

  /**
   * Starts a staged reaction when the director says one is due.
   *
   * Called every step rather than on the day boundary: the beat is *chosen* on
   * the boundary and *released* when the player is there to see it, and those
   * are two different clocks (`witness/director.ts`).
   */
  private beginStaging(): void {
    const beat = this.director.due(this.today, this.playing, this.staging.active);

    if (beat === null) {
      return;
    }

    // What the smoke may stand over, which is stricter than what the beat is
    // about: only a town a refugee column is walking out of, fleeing the same
    // record — see `witness/staging.ts`'s `begin`.
    const column = this.columnCarrying(beat);
    const struck = column === null ? null : (this.settlements.get(column.from) ?? null);
    const here = this.town;

    this.staging.begin(beat, struck, here);
    this.director.begun(beat, this.today, here?.name ?? "the road");

    // The crowd turns out. The board is what it turns out *at* — it is where a
    // price that moved this morning is legible, so a crowd round it is a crowd
    // round the thing the news is about (decision 5) — and the settlement's
    // centre is the fallback for a town whose board the client has not placed.
    //
    // **It moves nobody named, and it moves nobody at all today.** The crowd
    // holds only the unnamed population since item 6 and that is empty at this
    // scale (`witness/crowd.ts`), so this is the empty layer's lifecycle kept
    // whole rather than a call that does something. What it must never become is
    // the client asking the simulation to walk its people somewhere: *making* a
    // town turn out is sim behaviour and is filed with the conversation area's
    // engagement work (item 6, rider 3). Who *speaks* is unaffected — a figure
    // near the focus says what landed, out of the presence list
    // ({@link Witnessing.stage}).
    const board = this.features.find((feature) => feature.kind === FEATURE_MARKET_BOARD);
    // Where the board *stands*, not where its anchor was: the town's own answer
    // may have moved it off a wall ({@link Witnessing.snapProps}), and a ring
    // gathering round the anchor would be a ring gathering round nothing.
    const focus =
      board === undefined
        ? here
        : this.propAt(
            `feature:${board.id}`,
            board.position.x,
            board.position.y,
            board.position.z,
            (column, row) => this.terrain.groundLevel(column, row),
          );

    if (focus !== undefined && focus !== null) {
      this.crowd.gatherAt(focus.x, focus.y, GATHER_RADIUS_TILES);
    }
  }

  /**
   * Returns a refugee column on the roads carrying this beat's records, or
   * `null`.
   *
   * **The join that makes the smoke honest.** A beat on the road surface is
   * either a caravan or a column and the surface cannot tell them apart
   * (`world/types.ts`: the client switches on the *place*), but the columns are
   * read from the simulation every day with the battle they fled attached
   * (`WitnessSource.refugeeGroups`) — so a record they share is the simulation
   * saying, twice and independently, that this beat is about a town somebody ran
   * out of. `group.from` is then a settlement that was actually struck, which is
   * the only thing this client will draw smoke over.
   *
   * A handful of groups against one or two records: a scan is the whole cost.
   */
  private columnCarrying(beat: SpotlightBeat): RefugeeGroup | null {
    for (const group of this.groups) {
      if (group.refs.some((event) => beat.refs.includes(event))) {
        return group;
      }
    }

    return null;
  }

  /**
   * Advances a running reaction, and lets whoever is nearest say what happened.
   *
   * The three things that make this a *reaction* rather than a cutscene are all
   * visible in ten lines: the line is asked of the simulation and shown
   * unchanged, it is filed in the journal like anything else the player was told
   * — same channel, same dedupe, same `EventId` — and when the time is up the
   * crowd goes back to what it was doing.
   *
   * **Whoever speaks is one of the simulation's own people, where it is drawing
   * them** (item 6). The voices used to be picked out of the client's ambient
   * crowd, which draws no named figures now — so the list is the presence list
   * at its *drawn* position, which is where the player sees somebody standing
   * and therefore the right thing to measure earshot against
   * ({@link WalkSmoothing}). Nobody is asked to come nearer.
   */
  private stage(stepSeconds: number): void {
    if (!this.staging.active) {
      return;
    }

    // A handful of small objects per step, and only while a reaction is running:
    // the two lists this joins are a position that moves every tick and a handle
    // that does not, which is `bodyPieces`' own join one consumer over.
    const near = this.bodies.map((body) => {
      const at = this.walk.placeOf(body);

      return { figure: body.figure, x: at.x, y: at.y };
    });
    const request = this.staging.step(stepSeconds, near, this.from);

    if (request !== null) {
      // Unprompted, but not unattributed: `speakOf` is the same projection the
      // F verb gets, with the record named instead of chosen, and it answers
      // `null` for a figure whose settlement has never heard of it. Unprompted
      // is also why it lands on the overheard line rather than the answer line
      // — a voice the player never addressed must not replace one they did.
      this.deliver(
        this.source.speakOf(request.figure, request.event),
        "npc",
        this.memoryOf(this.staging.beat),
        "overheard",
      );
    }

    if (!this.staging.active) {
      this.crowd.release();
    }
  }

  /**
   * Shows a spoken line and writes down that it was heard.
   *
   * `memory` is the anchor's emphasis and the only thing on this path that did
   * not come out of the simulation this instant — it is what the *player* has
   * already done, spelled out beside the line rather than instead of it
   * ({@link Witnessing.memoryOf}).
   *
   * `heard` is which element shows it: an answer the player asked for, or a
   * voice reaching them unasked (`hud/hud.ts`). The journal does not care — a
   * line is filed the same however it arrived — but the screen must, because
   * the two arrive on different clocks and the unasked one must never overwrite
   * the asked one mid-read.
   */
  private deliver(
    line: SpokenLine | null,
    channel: JournalChannel,
    memory: string | null = null,
    heard: "answer" | "overheard" = "answer",
  ): void {
    if (line === null) {
      return;
    }

    const voice = heard === "answer" ? this.hud.dialogue : this.hud.overheard;

    voice.say(line.speaker, line.text, line.about, line.learned, memory);

    if (line.about !== null) {
      this.record(line.about, channel, line.speaker, line.text);
    }
  }

  /**
   * Talks to somebody standing in the street: their news, or who they are.
   *
   * **News first, and an introduction only when there is none.** Which one to
   * show is presentation and this is the call (Phase 4 decision 10b leaves it
   * open): the reason the verb exists is that people in this world know things,
   * and an introduction shown *before* the news would make the player press F
   * twice to reach the thing the phase is about. What decision 10b actually
   * fixes is the failure at the other end — a figure with nothing to say used to
   * answer with silence, and a verb that produces nothing is a verb a player
   * stops pressing. So most of the roster, most of the time, says who they are.
   *
   * The meeting is written down either way, because the player met them either
   * way. An introduction files no journal entry: there is no record in it to
   * cite (`world/types.ts`, {@link WitnessSource.introduce}), and a journal is
   * references or it is nothing.
   */
  private talkTo(figure: FigureId, home: SettlementId | null, channel: JournalChannel): void {
    const line = this.source.speak(figure);

    this.met(figure, line?.speaker ?? this.nameOf(figure), home, channel);

    if (line !== null) {
      this.deliver(line, channel);

      return;
    }

    this.sayWhoTheyAre(figure);
  }

  /**
   * Asks a figure who they are and shows the answer.
   *
   * `speech::introduce`, whole and unedited — a projection over the figure's own
   * record, which is the same discipline every other sentence in this client is
   * held to (decision 9). No `EventId`, so nothing is filed: this is the world
   * telling the player about a *person* rather than about something that
   * happened.
   */
  private sayWhoTheyAre(figure: FigureId): void {
    const said = this.source.introduce(figure);

    if (said === "") {
      return;
    }

    this.hud.dialogue.say(this.nameOf(figure), said, null, null);
  }

  /**
   * Writes down that the player met somebody.
   *
   * Everything stored is either the simulation's (the name, the home) or the
   * client's own account of what the player did (where they were standing, what
   * day it was, which verb it was). Nothing is inferred — see
   * `journal/people.ts` for why that line is where it is.
   */
  private met(
    figure: FigureId,
    name: string,
    home: SettlementId | null,
    how: JournalChannel,
  ): void {
    const today = this.source.date();

    this.people.meet({
      figure,
      name,
      home,
      homeName: home === null ? "" : (this.settlements.get(home)?.name ?? ""),
      // Where the player was standing when they met them, and "the road" is the
      // honest answer for a meeting out of every town's reach.
      where: this.town?.name ?? "the road",
      when: today,
      lastSeen: today,
      how,
      seen: 1,
    });

    if (this.hud.journal.open) {
      this.showJournal();
    }
  }

  /**
   * What the player remembers that bears on a staged beat, or `null`.
   *
   * **The anchor, made visible without a word of invention.** A beat's
   * `subject` is the settlement at the other end of it — who told this town, or
   * whose people are on the road — and the people store knows which settlements
   * the player has actually met somebody from. When those are the same place,
   * the line the crier is speaking is news out of a town the player has stood in
   * front of somebody from, and saying so is the difference between a place name
   * and a person.
   *
   * Both halves are facts: the beat's subject is the simulation's, and the
   * meeting happened. Nothing here claims anything about the person's fate.
   */
  private memoryOf(beat: SpotlightBeat | null): string | null {
    if (beat === null || beat.subject === null) {
      return null;
    }

    const known = this.people.everyone.filter((met) => met.home === beat.subject);
    const first = known[0];

    if (first === undefined) {
      return null;
    }

    const others = known.length > 1 ? ` and ${known.length - 1} more` : "";

    return `${first.name}${others} — met, ${formatWorldDate(first.when)}`;
  }

  /** The name the simulation gave a figure standing in this street. */
  private nameOf(figure: FigureId): string {
    return this.presentNames.get(figure) ?? this.people.get(figure)?.name ?? "";
  }

  /**
   * Notices a claim landing, and re-reads what depends on who the player is.
   *
   * A tick has no error channel, so the honest test that a queued claim was
   * accepted is to look at the world afterwards (`world/types.ts`,
   * {@link WitnessSource.playerFigure}). Three things turn on the answer and all
   * three are re-read here rather than polled: the introduction the journal and
   * the trace view show, the town's names (the player's own figure joins the
   * roster of the town they claimed in, and a book they left is labelled
   * differently from one they did not), and any panel that is open.
   *
   * **Their own body was never this file's to refuse.** Before item 6 the crowd
   * had to be re-anchored here so the ambient loop would stop drawing somebody
   * with the player's name ambling about in front of them; the simulation gives
   * the player's figure no presence record at all, so the re-read is now about
   * the roster rather than about the street.
   */
  private watchForClaim(): void {
    const figure = this.source.playerFigure();

    if (figure === this.player) {
      return;
    }

    this.player = figure;
    // One string per change, which for a session is once: strings cross per
    // call, and this one is the only way the client can read the byname the
    // player's own deeds have earned them ({@link Witnessing.player}).
    this.playerIntroduced = figure === null ? "" : this.source.introduce(figure);
    // The names were joined before there was a player on the roster.
    this.readPlace("claim");

    if (this.hud.journal.open) {
      this.showJournal();
    }

    if (this.hud.trace.open) {
      this.showTrace();
    }
  }

  /**
   * Re-derives what the player holds that the visited settlement does not.
   *
   * **A comparison of two lists the simulation already answers with** — the
   * journal, and `rumors(here)` — and nothing else: the client is not deciding
   * what is worth telling, it is noticing what is missing. What happens when one
   * of them is handed over is entirely the simulation's, which is why the offer
   * makes no promise about it (Phase 5 decision 4: the ordinary outcome is
   * nothing at all, because most of what a player knows the town next door
   * already heard).
   *
   * By record and not by entry: hearing one war from three mouths is one thing
   * to carry, so the first entry filed about a record stands for it. The
   * client's own {@link Witnessing.told} set is the one tick of latency in
   * front of the town's store, no more.
   */
  private refreshShare(): void {
    const here = this.settlement;

    if (here === null) {
      // Nowhere to hand it to. The offer is a comparison against *a town's*
      // store, so out past every halo there is no second list — and an offer
      // that survived out here is the "shouting at a hillside" the old
      // nearest-town `here()` produced (item 6, rider 4).
      this.share = [];

      return;
    }

    const held = new Set(this.source.rumors(here).map((rumor) => rumor.event));
    const offered: JournalEntry[] = [];
    const seen = new Set<EventId>();

    for (const entry of this.journal.entries) {
      if (held.has(entry.event) || seen.has(entry.event)) {
        continue;
      }

      if (this.told.has(`${here}:${entry.event}`)) {
        continue;
      }

      seen.add(entry.event);
      offered.push(entry);
    }

    this.share = offered;
  }

  /**
   * Hands one thing the player knows to the town they are standing in.
   *
   * `Command::Tell`, queued and applied inside a tick like a dig. The simulation
   * runs the same first-telling-wins learn a caravan runs and, **if and only
   * if** the town genuinely learned something, a record citing the player's
   * figure lands in the log and starts travelling the roads. There is no reward
   * and nothing is said: the deed is the record, and whether it was one is a
   * question about the world rather than about this press
   * (`world/types.ts`, {@link WorldSource.tell}).
   *
   * What the client does afterwards is stop offering it — see
   * {@link Witnessing.told} — and re-draw the page.
   */
  private tell(entry: JournalEntry): void {
    const here = this.settlement;

    if (here === null) {
      // Unreachable by construction — the offer is only ever drawn for a town
      // ({@link Witnessing.refreshShare}) — and spelled rather than asserted,
      // because a `tell` carrying a nowhere is a command the simulation would
      // take and drop in silence.
      return;
    }

    this.told.add(`${here}:${entry.event}`);
    this.deeds?.tell(here, entry.event);
    this.refreshShare();

    if (this.hud.journal.open) {
      this.showJournal();
    }
  }

  /**
   * Adds a record to the book being compiled, or takes it out again.
   *
   * Click order is citation order, which is the only ordering a player can
   * express by pressing lines in a list and the only one this client is entitled
   * to invent ({@link MAX_CHRONICLE_ENTRIES} bounds it, read off the boundary).
   * Taking one out renumbers the rest, which is what "an ordered list" means and
   * is why the picked list is an array rather than a set.
   */
  private pickPage(event: EventId): void {
    const at = this.picked.indexOf(event);

    if (at >= 0) {
      this.picked.splice(at, 1);
    } else if (this.picked.length < MAX_CHRONICLE_ENTRIES) {
      this.picked.push(event);
    }

    if (this.hud.journal.open) {
      this.showJournal();
    }
  }

  /**
   * Leaves the compiled book on the ground where the player is standing.
   *
   * `Command::PlaceChronicle`, and **not one word of prose crosses**: what the
   * player has chosen is which records the book should read out, and what it
   * says when somebody opens it is the simulation's own written register
   * (Phase 5 decision 5, the phase's "no player prose enters the world" rule).
   *
   * The tile is the player's own, floored — the client's position, which the
   * simulation still does not learn: what it learns is where a *book* is, and
   * which town that belongs to is its answer rather than this one's.
   */
  private placeBook(): void {
    if (this.picked.length === 0) {
      return;
    }

    this.deeds?.placeChronicle(this.picked, {
      x: Math.floor(this.from.x),
      y: Math.floor(this.from.y),
      z: Math.floor(this.from.z),
    });
    this.picked = [];
    this.compiling = false;
    // The shelf is a day-boundary read and a book left now exists on the next
    // tick, so the re-read is scheduled rather than waited for
    // ({@link Witnessing.booksAwaited}).
    this.booksAwaited = 2;

    if (this.hud.journal.open) {
      this.showJournal();
    }
  }

  /**
   * Opens a book and files what it taught.
   *
   * Both halves of a chronicle cross here and they are two calls on purpose
   * (`world/types.ts`): the **lines** are what the player reads — the written
   * register, composed by `sim-core` over the records the book cites — and the
   * **entries** are what they now hold. A journal entry is a reference and its
   * provenance, never a copy of what was said, so filing a book is filing its
   * citations under a channel of their own with the author's name on them.
   *
   * `lines[i]` voices `entries[i]`: same order, same length. The note kept for
   * each entry is therefore the book's own sentence about that record, which is
   * the same bargain a grave's inscription makes and never a sentence this file
   * composed.
   *
   * A settlement's own shelves teach it these same ids, one a month. **A client
   * filing them is doing what the town does**, which is what makes a book a
   * witness channel rather than decoration.
   */
  private readBook(chronicle: number): void {
    const book = this.books.find((entry) => entry.id === chronicle);

    if (book === undefined) {
      return;
    }

    const lines = this.source.chronicleLines(chronicle);
    const entries = this.source.chronicleEntries(chronicle);
    const where = this.settlements.get(book.settlement)?.name ?? "";
    const by = book.authorName === "" ? "" : ` by ${book.authorName}`;
    const ground = where === "" ? "" : ` in ${where}`;

    // The title is chrome about the object — who wrote it, where it stands, the
    // day it was left — and every part of it is the simulation's own string or
    // the client's own date formatting. What the book *says* is below it and
    // untouched.
    this.hud.book.show(`a book${by}${ground} — left ${formatWorldDate(book.left)}`, lines);

    const who = this.bookLabel(book);

    for (const [index, event] of entries.entries()) {
      this.record(event, "book", who, lines[index] ?? "");
    }

    // Reading one is the strongest reason to have it on the paper, and the mark
    // is written here as well as where the prop is drawn: a player who read a
    // book is standing on the ground it stands on, so this one is never refused.
    this.mark(
      `chronicle:${book.id}`,
      "chronicle",
      book.position.x,
      book.position.y,
      this.mapLabel(book),
    );
  }

  /**
   * Opens the Book of Eras, turns its page, and closes it at the last one.
   *
   * **The same book the session opened on, read off a shelf** (decision O6):
   * the projection is this class's own cached read ({@link Witnessing.eras}) and
   * the pages are the same assembly the start flow uses (`witness/saga.ts`'s
   * `erasPages`), so "the same text is readable in-world" is a fact about one
   * code path rather than a claim about two.
   *
   * **One key does all three things**, and that is the shelf reading's whole
   * interface: the pointer is locked, so there is no second key to bind and no
   * cursor to press a button with (`input/controls.ts` — the client's key card
   * is the verb list, and this phase does not add to it). `F` opens it, `F`
   * turns the page, and `F` on the last page closes it, which is what a person
   * does to a book they are standing in front of. The foot of the page says so.
   *
   * **Nothing is filed.** Every other readable thing in this file writes a
   * journal entry, because a journal entry is a record the player learned from
   * somebody and the entry is how the trace view proves it. This one is not
   * learned from anybody: it is the world's own account of itself, derived from
   * records rather than carried by a figure, and filing it would put a few dozen
   * ids in the player's journal that no witness ever handed them. What a shelf
   * teaches a *settlement* is the simulation's business and unchanged
   * (`sim_core::history`'s shelves teach a town their citations, one a month).
   */
  private readEras(): void {
    const book = this.eras;

    if (book.eras.length === 0) {
      return;
    }

    if (!this.hud.eras.open) {
      this.erasPage = 0;
      this.showEras();

      return;
    }

    const next = this.erasPage + 1;

    if (next >= book.eras.length) {
      this.erasPage = 0;
      this.hud.eras.hide();

      return;
    }

    this.erasPage = next;
    this.showEras();
  }

  /**
   * Draws the page the shelf copy is open at.
   *
   * The title is chrome about the object and every part of it is either this
   * file's own word or the simulation's own name for the town it stands in —
   * the same bargain a chronicle's title line makes ({@link Witnessing.readBook}).
   */
  private showEras(): void {
    const where = this.town;
    const kept = where === null ? "" : ` — kept in ${where.name}`;
    const pages = erasPages(this.eras);
    const last = this.erasPage >= pages.length - 1;

    this.hud.eras.show({
      where: "shelf",
      title: `${ERAS_LABEL}${kept}`,
      pages,
      page: this.erasPage,
      hint: last ? "F closes the book" : "F turns the page",
      // No buttons: the pointer is locked and there is nothing to click with.
      onTurn: null,
      onBegin: null,
    } satisfies ErasView);
  }

  /**
   * Reads what happened to a town that is still in ruin.
   *
   * The ruin cites the battle that caused it (`world/types.ts`,
   * {@link RuinView.sack}) and the sentence is `speech::notice` — the world's
   * own **written** register, spoken with the ground under it, which is what
   * turns "this place is burned" into "this town was stormed in year 169". The
   * same projection a market board posts a cause in, and the same rule: it
   * crosses whole and this file composes nothing.
   *
   * Shown through the dialogue surface rather than a panel of its own, exactly
   * as an inscription is: it is one short thing the world said, and a second
   * panel would be a second thing to close.
   */
  private readRuin(settlement: SettlementId): void {
    const ruin = this.ruins.get(settlement);

    if (ruin === undefined) {
      return;
    }

    const posted = this.source.notice(settlement, ruin.sack);

    if (posted === "") {
      return;
    }

    const where = this.settlements.get(settlement);
    const who = where === undefined ? "the wreckage" : `the wreckage of ${where.name}`;

    this.hud.dialogue.say(who, posted, ruin.sack, ruin.since);
    // Filed under the ground's own channel: a burned town is aftermath read
    // where it stands, which is what that channel has always meant
    // (`journal/journal.ts`) — and the entry cites the battle, so the trace view
    // resolves it in the log like everything else.
    this.record(ruin.sack, "grave", who, posted);
  }

  /**
   * Fills the journal panel: who the player is, what this town has not heard,
   * the people, and then what was learned.
   *
   * **The journal is where the player's own two verbs live**, and that is a
   * decision rather than a layout. What they are carrying and what they are
   * writing down is exactly what this page already holds, so the share-word
   * offer and the compile-a-book mode are sections of it rather than panels of
   * their own: no new key, no new surface, and the mouse is already free here
   * because a reading panel hands the pointer lock back (`main.ts`).
   *
   * Where the *offer* is discovered is elsewhere — the word prompt at the top of
   * the screen, set from the same list ({@link Witnessing.wordPrompt}) — which
   * is the half of "inside the talk interaction" that survives contact with a
   * pointer lock: talking to somebody in a town you have news for is exactly
   * when that line is on screen, and it names the key.
   */
  private showJournal(): void {
    const where = this.town;

    this.hud.journal.show({
      entries: this.journal.entries,
      people: this.remembered(),
      figure: this.player === null ? null : this.playerIntroduced,
      // The byname the player's own records earned them, read at each showing
      // rather than once at the claim because deeds accumulate — `""` for a
      // figure whose records earned none, which is most figures forever
      // (chunk U-3's filing, the read added beside wave U2).
      byname: this.player === null ? "" : this.source.byname(this.player),
      share:
        where === null || this.share.length === 0
          ? null
          : ({
              where: where.name,
              entries: this.share,
              onTell: (entry: JournalEntry) => {
                this.tell(entry);
              },
            } satisfies JournalShare),
      book: {
        compiling: this.compiling,
        picked: this.picked,
        onPick: (event: EventId) => {
          this.pickPage(event);
        },
        onToggle: () => {
          this.compiling = !this.compiling;
          this.picked = [];
          this.showJournal();
        },
        onPlace: () => {
          this.placeBook();
        },
      },
    });
  }

  /**
   * Fills the map panel from the ground the player has covered.
   *
   * Dated in the title, because a hand-drawn chart is a thing somebody drew on
   * a day — and because it is the honest answer to "how old is this": the map
   * is what the player knew the last time they sat down with it, and the date
   * says when that was.
   */
  private showMap(): void {
    this.mapDrawn = this.ground.revision;
    this.hud.map.show(this.survey(), `map — ${formatWorldDate(this.source.date())}`);
  }

  /**
   * Everybody met, with the connections the client can honestly draw.
   *
   * Assembled here because this is where the three sources meet — the journal,
   * the director's log of what it has staged, and the columns currently on the
   * roads — and computed on demand, which is what the panel that draws it does
   * too (`journal/people.ts`).
   */
  private remembered(): readonly RememberedPerson[] {
    return remember(this.people, {
      entries: this.journal.entries,
      news: this.director.log.map((staged) => ({
        from: staged.beat.subject,
        refs: staged.beat.refs,
        where: staged.where,
        when: staged.when,
      })),
      columns: this.groups.map((group) => ({
        from: group.from,
        refs: group.refs,
        size: group.size,
      })),
    });
  }

  /**
   * Reads what is carved on a feature, and remembers the records it cites.
   *
   * The inscription arrives with its own line breaks and keeps them on the way
   * to the dialogue surface — a stone reads down, and a name over two dates
   * flattened into one line reads as a sentence about somebody rather than as a
   * grave. The journal takes the flattened version instead, because an entry
   * there is one line in a list of them.
   */
  private readGround(target: Interactable, feature: number): void {
    const inscription = this.source.inscription(feature);

    if (inscription === "") {
      return;
    }

    // Shown through the dialogue surface rather than a panel of its own: it is
    // one short thing the world said, which is exactly what that surface is
    // for, and a second panel would be a second thing to close.
    const first = target.refs[0] ?? null;
    this.hud.dialogue.say(target.label, inscription, first, null);

    for (const event of target.refs) {
      this.record(event, "grave", target.label, inscription.replaceAll("\n", " · "));
    }
  }

  /**
   * Fills the market board and writes down what moved each price.
   *
   * **The shock is the sign and the cause is the news.** A `PriceShock` carries
   * an `Option<EventId>` for the record that emptied or filled the store
   * (decision 7 — "the `cause` link is the channel's traceability"), and that is
   * what the player has actually learned by reading the board: not that food is
   * dear, but that food is dear *because Stonehearth was stormed*. So the entry
   * is filed against the cause where there is one.
   *
   * And since wave 4 of the voice overhaul the board *says* so in its own
   * register ({@link WitnessSource.notice}): what the player reads is a notice a
   * town would post, not the chronicle a developer reads. The board also speaks
   * with the ground under it, so the notice is asked for on behalf of *this*
   * settlement — the same handle the market rows came off. The journal note below
   * is still the client's own one-line label for the entry, which is chrome about
   * how the record was learned rather than a claim about the world.
   *
   * Where there is none the shock itself is filed instead, because the swing is
   * still a record in the log and reading the board is still how the player came
   * to know about it — it is simply news about the weather rather than about the
   * war, and the difference is visible in the panel and checkable in the log.
   *
   * Called when the player reads the board and again on every day boundary while
   * it is open ({@link Witnessing.readWorld}), which is the one surface in the
   * client that changes while nobody presses anything: a price is derived on
   * every call and moves without a record being written (plan decision 7), so a
   * board left open is a board that has to keep up. The journal takes the repeat
   * for free — an entry's identity is `(event, channel, who)` and the board is
   * one "who" (`journal/journal.ts`).
   */
  private showMarket(): void {
    const here = this.settlement;

    if (here === null) {
      // No town, no board. Unreachable through the verb — a market board is a
      // *feature* of the visited town and there are none out here — and reached
      // on a day boundary with the panel already open, which is exactly the case
      // this guard is for: the player walked out of the halo while reading it.
      this.hud.market.hide();

      return;
    }

    const settlement = this.town;
    const rows = this.source.market(here);
    const where = settlement?.name ?? "the market";

    // The board's own projection is handed over rather than the panel being
    // given the source: a panel renders strings, and turning an `EventId` into
    // what the board posts about it is a query (`hud/panels.ts`).
    //
    // **`notice`, not `describe`** (voice overhaul, wave 4). `describe` is the
    // log's dev narration — handle numbers and a casualty subtraction — and it is
    // what the trace view below asks for. That is the difference: one surface is
    // the world talking and the other is us.
    //
    // **And the board knows which town it is standing in**, which is why `here`
    // is closed over rather than passed down: a market board is a *town's*
    // feature, so a record about this town posts "this town was stormed in year
    // 169" where the next town's board would name the place. The panel still does
    // not need the settlement — it renders whatever this closure answers — so the
    // ground stays on the side of the client that already knows where the player
    // is standing.
    this.hud.market.show(
      `${where} — market board`,
      rows,
      (event) => this.source.notice(here, event),
      // The town's work, on the board where a townsperson would read it
      // (chunk U-2, `hud/panels/market.ts`): the rows are the sim's own nouns
      // over the same posts the debug window reads, and the names are the
      // roster's — the one expression the reading window could not write
      // from inside its own fence.
      boardWork(this.board, this.source, here, (worker) => this.workerName(worker)),
    );

    for (const row of rows) {
      if (row.shock === null) {
        continue;
      }

      this.record(
        row.shock.cause ?? row.shock.event,
        "market",
        "the market board",
        `${row.commodityName} ${row.shock.moveName} in ${where}`,
      );
    }
  }

  /** Appends a journal entry and refreshes the overlay if it is open. */
  private record(event: EventId, channel: JournalChannel, who: string, note: string): void {
    const added = this.journal.append({
      event,
      via: { channel, who },
      whenLearned: this.source.date(),
      note,
    });

    if (added && this.hud.journal.open) {
      this.showJournal();
    }

    if (added && this.hud.trace.open) {
      this.showTrace();
    }

    if (added) {
      // A thing just learned may be a thing this town has not heard, and the
      // word prompt is read every frame — so the offer is re-derived where the
      // journal grows rather than on the day boundary alone. It is one set of
      // the town's rumours against a few dozen entries, on the rare frames when
      // something was actually filed.
      this.refreshShare();
    }
  }

  /**
   * Fills the trace view: the records learned, what the director staged, and
   * who every line is being composed *to*.
   *
   * The listener is Phase 5 decision 6a's debug half. The lens is set once,
   * sim-side, and the client neither passes it nor can turn it off — so what a
   * debug surface can honestly show is who the figure is, and a tester reading
   * a second-person line has the answer to "to whom" on the same panel.
   */
  private showTrace(): void {
    this.hud.trace.show(
      this.traced(),
      this.stagings(),
      this.player === null ? null : { figure: this.player, introduced: this.playerIntroduced },
      this.sagaTraced(),
      this.bornTraced(),
    );
  }

  /**
   * The life this session was born into, with the log's own sentence under every
   * record it stands on.
   *
   * {@link Witnessing.sagaTraced}'s sibling and the origins screen's half of the
   * same rule: the birth screen is a diegetic surface and carries no record tags
   * (`hud/panels.ts`'s `OriginsPanel`), so the citations behind a life — the
   * sack, or the birth, and the record that seated each parent a claim named —
   * are one keypress away instead.
   *
   * `null` in every session nobody was born in, which is the Phase 5 session
   * unchanged.
   */
  private bornTraced(): TracedOrigin | null {
    const born = this.born;

    if (born === null) {
      return null;
    }

    const describe = (event: EventId): { event: EventId; narration: string } => ({
      event,
      narration: this.source.describe(event),
    });

    return {
      kind: born.chosen.kind,
      line: born.chosen.line,
      name: born.name,
      queued: born.queued,
      cites: born.chosen.cites.map(describe),
      claims: born.chosen.claims.map((claim) => ({
        line: claim.line,
        cites: claim.cites.map(describe),
      })),
    };
  }

  /**
   * The Book of Eras with the log's own sentence under every line it cites.
   *
   * **Phase 6's exit criterion, drawn**: *"every line traceable in the debug
   * view to log records; nothing invented"*. The saga hands over each line
   * paired with the records it narrates (decision O6's trace contract) and this
   * resolves them through {@link WitnessSource.describe}, exactly the way
   * {@link Witnessing.stagings} resolves a beat's — so the owner can read a
   * sentence of the world's history and the record behind it on the same screen.
   *
   * Both naming surfaces are carried whatever the page shows, because this is
   * the debug view and which of them the *book* leads with is an open taste call
   * (`witness/saga.ts`'s `ERA_HEADING`): a tester judging the two wants both in
   * front of them.
   */
  private sagaTraced(): readonly TracedEra[] {
    return this.eras.eras.map((era) => ({
      ordinal: era.ordinal,
      title: era.title,
      name: era.name,
      began: era.began,
      ended: era.ended,
      lines: era.lines.map((line) => ({
        text: line.text,
        cites: line.cites.map((event) => ({ event, narration: this.source.describe(event) })),
      })),
    }));
  }

  /**
   * The journal grouped by record, with the log's own sentence for each.
   *
   * The only place the client asks {@link WitnessSource.describe} outside a
   * debug surface's own rendering, and it is one call per *record* rather than
   * per entry: three channels carrying one war ask the log about that war once.
   */
  private traced(): readonly TracedRecord[] {
    return this.journal
      .byRecord()
      .map((record) => ({ ...record, narration: this.source.describe(record.event) }));
  }

  /**
   * What the director has staged, with the log's own sentence for every record
   * it carried.
   *
   * **The cross-cutting rule, drawn**: "every staged beat carries its `EventId`s
   * and the trace view shows them". It is separate from {@link
   * Witnessing.traced} because it answers a different question — that one is
   * what the *player* was told, this one is what the *client* chose to put in
   * front of them, and a staged beat whose criers all answered `null` appears
   * here and nowhere else.
   */
  private stagings(): readonly StagedDelivery[] {
    return this.director.log.map((staged) => ({
      id: staged.beat.id,
      channel: staged.beat.channel,
      where: staged.where,
      when: staged.when,
      earliest: staged.beat.earliest,
      records: staged.beat.refs.map((event) => ({
        event,
        narration: this.source.describe(event),
      })),
    }));
  }

  /**
   * How far along its road something that set out on a given day is, as a
   * fraction of it.
   *
   * Days since it left, plus however much of today the client has watched go by,
   * times whatever a day is worth to that kind of traveller — and then stopped a
   * fixed distance short of the destination. Every term is in-game time except
   * the sub-day one, which is the client's measurement of a day
   * ({@link DAY_SECONDS_GUESS}); the whole thing re-anchors to the date at every
   * boundary, so a wrong measurement is a slightly uneven walk and never a
   * column in the wrong place.
   *
   * Something that left in an earlier year reads as infinitely far along and
   * therefore arrived, which is `daysSince`'s honest answer to a question the
   * client cannot ask — and the useful failure of the two: a column that would
   * otherwise be stranded six hundred tiles away in the fog is standing at the
   * gate where it can be asked what it is running from.
   *
   * The refugee columns are the only walkers on it today, and the *rates* stay
   * arguments rather than constants here because how fast a body walks and how
   * far short it stops are facts about that body, not about the arithmetic.
   */
  private walkFraction(
    departed: WorldDateView,
    length: number,
    fractionPerDay: number,
    stopTiles: number,
  ): number {
    // How much of today has gone by: the simulation's own answer where there is
    // one, and the measurement where there is not
    // ({@link Witnessing.dayFraction}). Before a day has been measured the
    // divisor is a placeholder, and a placeholder that is too small would
    // otherwise walk a column several days down its road and snap it back when
    // the boundary arrived — which is what the ceiling is for.
    const days = daysSince(this.today, departed) + this.dayFraction();

    // The two ends of the road, in fractions of it. Never past the stop — a road
    // shorter than the stopping distance is a road whose whole length is
    // "outside the gate" — and never nearer than the mouth it set out from
    // ({@link SETS_OUT_TILES}), which on a road too short to hold both is the
    // stop itself.
    const furthest = Math.max(0, 1 - stopTiles / length);
    const mouth = Math.min(SETS_OUT_TILES / length, MOUTH_FRACTION_MAX, furthest);

    return Math.min(Math.max(days * fractionPerDay, mouth), furthest);
  }
}

/**
 * How many days ago something happened, as far as the client is allowed to
 * count.
 *
 * **The client does not know how long a year is** and is not going to learn:
 * comparing two dates is comparing the year and then the day, and anything
 * harder than that is a question for the simulation that owns the calendar
 * (`world/types.ts`, {@link WorldDateView}). So a date inside this year is a
 * subtraction and a date in any earlier one is simply "longer ago than anything
 * this is used to scale" — which is the honest answer for a refugee column that
 * left before the turn of the year, and which the one caller clamps anyway.
 */
function daysSince(now: WorldDateView, then: WorldDateView): number {
  if (then.year < now.year) {
    return Number.POSITIVE_INFINITY;
  }

  if (then.year > now.year) {
    return 0;
  }

  return Math.max(0, now.dayOfYear - then.dayOfYear);
}

/**
 * The indefinite article an item label wants in front of it.
 *
 * **The one word of the item vocabulary that is the client's**, and it is the
 * simulation's own instruction: `sim_core::Snapshot::item_label` is documented
 * as *"singular, lowercase and with no article — the sentence or the panel row
 * around it owns both"*, and this is that sentence
 * ({@link Witnessing}'s `itemWords`).
 *
 * The vowel test, and nothing cleverer. English has honest exceptions in both
 * directions — *an hour*, *a one-off* — and the item vocabulary has none of
 * them: it is eleven nouns and a handful of material and quality words, every
 * one of which the rule gets right. A table of exceptions here would be a
 * grammar nobody can check against a vocabulary that is not this file's.
 */
function article(label: string): string {
  return "aeiou".includes(label[0] ?? "") ? "an" : "a";
}

/** A tile as a map key. Never parsed back; it only has to be unique and readable. */
function tileKey(x: number, y: number): string {
  return `${x},${y}`;
}

/**
 * Whether two tiles are the same one, all three coordinates.
 *
 * A job's identity is `(site, kind)` and the site is a *tile*
 * (`sim_core::jobs`), so a comparison that dropped the level would call a
 * boulder in a cellar the same work as the floor above it.
 */
function sameTile(left: TilePos, right: TilePos): boolean {
  return left.x === right.x && left.y === right.y && left.z === right.z;
}

/**
 * The same, for a key that has to tell two levels of one column apart.
 *
 * A shelf on a library's floor and a boulder in the cellar under it are two
 * piles, so the watched set is keyed by the whole tile
 * ({@link Witnessing.piles}) where the shelf fan above is keyed by the column —
 * a book's own z is already in its handle.
 */
function levelKey(at: TilePos): string {
  return `${at.x},${at.y},${at.z}`;
}

/** A drawn position as the debug log writes one. Two decimals: sub-tile matters. */
function place(at: { readonly x: number; readonly y: number; readonly z: number }): string {
  return `${at.x.toFixed(2)}, ${at.y.toFixed(2)}, ${at.z.toFixed(2)}`;
}

/**
 * A list of figure handles as the debug log writes one.
 *
 * Capped, because a tier swap in a town of two hundred is a list of two hundred
 * and the useful half of the line is the *count* with a handful of examples
 * under it — a log line nobody can read is a log line nobody reads.
 */
function handleList(figures: readonly FigureId[]): string {
  const shown = figures.slice(0, HANDLES_SHOWN).map((figure) => `#${figure}`);
  const rest = figures.length - shown.length;

  return `${figures.length} (${shown.join(", ")}${rest > 0 ? `, +${rest} more` : ""})`;
}

/** How many handles a swap line names before it starts counting instead. */
const HANDLES_SHOWN = 8;

/**
 * A few names read out the way a sentence reads them: `Ashmarch and Brambleworth`.
 *
 * Uncapped, unlike {@link handleList}, and that is the difference between the two:
 * this joins a list that is two long in the one case there is
 * ({@link Witnessing.talkerNote}, a figure named as the talker of more than one
 * caravan), and a list long enough to need cutting would be a fact about the
 * simulation worth reading in full.
 */
function listed(names: readonly string[]): string {
  const last = names.at(-1) ?? "";

  return names.length < 2 ? last : `${names.slice(0, -1).join(", ")} and ${last}`;
}

/**
 * Where one volume of a shelf stands, and which way round it is.
 *
 * Evenly around the tile rather than on the golden angle, which is the one place
 * in this file where "spread n points so they never line up" is the wrong rule:
 * what a stack of books needs is for each of them to be the *nearest* thing to
 * its own vantage, and that is a question about opposite sides rather than about
 * looking scattered.
 *
 * The facing turns each one's face *out* of the fan. A monument's front is its
 * local -y (`render/props.ts`), so the front direction in world space is
 * `(sin f, -cos f)` and pointing that along the outward radial is `f = angle +
 * π/2`. The effect is what makes two books on one tile both readable: each
 * vantage stands outside the fan looking in, so the other book is *behind* the
 * one being addressed and the nearest-in-the-cone rule picks the right one
 * (`witness/interact.ts`).
 *
 * A volume the fan has never heard of stands alone in the middle of its tile,
 * which is the right answer for the one caller that can ask before the fan is
 * built and never a wrong one for the others.
 */
function fanPlace(
  shelves: ReadonlyMap<string, readonly string[]>,
  tileX: number,
  tileY: number,
  id: string,
): { readonly x: number; readonly y: number; readonly facing: number } {
  const on = shelves.get(tileKey(tileX, tileY)) ?? [id];
  const place = Math.max(on.indexOf(id), 0);
  const angle = on.length === 1 ? 0 : (place / on.length) * Math.PI * 2;
  const spread = on.length === 1 ? 0 : SHELF_SPACING_TILES;

  return {
    x: tileX + 0.5 + Math.cos(angle) * spread,
    y: tileY + 0.5 + Math.sin(angle) * spread,
    facing: angle + Math.PI / 2,
  };
}

/**
 * What the player would call a feature they are standing in front of.
 *
 * **A word a player has to look up is a prompt that failed.** This is the one
 * line of text between somebody and a thing they have never seen, it gets no
 * gloss and no second chance, and the owner's dry run found the failure case
 * exactly: they walked up to "a cairn" and had to ask what one was. So every
 * label here is words a reader already owns — and the mystery is kept by *not
 * explaining what it is doing there*, which is the interesting half and the half
 * the inscription answers when they read it.
 *
 * Client chrome, not simulation prose: none of this crosses the boundary and
 * none of it is drawn from a record. The stone's own text is
 * `speech::inscription`'s, and it is read rather than floated.
 *
 * Only the *aftermath* kinds are here. Phase 5's two addressable props are named
 * from the simulation's own strings instead — a book by whoever wrote it
 * ({@link Witnessing.bookLabel}) and the wreckage of whichever town it is
 * ({@link Witnessing.collectRuin}) — so there is nothing for this switch to
 * decide about them.
 */
function labelForFeature(kind: PropKind): string {
  switch (kind) {
    case "grave":
      return "a grave";
    // "Farmstead" is a word people meet in print and not in speech, and the
    // burning is the part that makes somebody walk over.
    case "burned-farmstead":
      return "a burned-out farm";
    // Was "a cairn". A pile of stones somebody stacked on purpose is what the
    // player is looking at, and saying so gives away nothing about why.
    case "battlefield":
      return "a pile of stacked stones";
    case "market-board":
      return "the market board";
    // Unreachable while `propKindForFeature` maps every id it does not know
    // onto a battlefield, and kept as the honest thing to say about a mark this
    // build has no name for.
    default:
      return "a stone marker";
  }
}
